import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, AlertTriangle, List, Table, Table2, Download, Edit2, X, RefreshCw, Sparkles, Scissors, Copy, Clipboard, Plus, Trash2, ArrowUp, ArrowLeft, Link, Undo2, ArrowUpDown, Calculator, Eraser, CaseSensitive, Send, Check, Settings } from 'lucide-react';
import { renderHighlightedText } from '../utils/highlightDiff';
import { getSourceNickname } from '../utils/modelNickname';
import VehicleMasterManagement from './VehicleMasterManagement';
import AIChatPanel from './AIChatPanel';
import * as XLSX from 'xlsx';

// ============================================================
// 📐 계층적 컬럼 너비 설정 시스템 (BatchAUIGrid에서 가져옴)
// ============================================================

/**
 * 1단계: 기본 데이터타입 너비 (모든 문서 공통)
 */
const BASE_WIDTH = {
  // 숫자 타입
  numeric: 80,      // 일반 숫자
  currency: 95,     // 금액
  quantity: 70,     // 수량
  percentage: 60,   // 퍼센트
  index: 45,        // 순번/인덱스
  // 텍스트 타입
  code: 90,         // 코드 (Material, HS Code 등)
  shortText: 100,   // 짧은 텍스트
  mediumText: 150,  // 중간 텍스트
  longText: 200,    // 긴 텍스트 (Description 등)
  date: 90,         // 날짜
  unit: 55,         // 단위 (EA, KG 등)
};

/**
 * 2단계: 문서 유형별 특화 필드 너비
 */
const DOCUMENT_TYPE_WIDTHS = {
  // 🚢 수출입 선적서류 (Invoice, Packing List, B/L, L/C 등) - 95% 사용
  shipping: {
    // 인보이스 필드
    item: 45, no: 45, seq: 45,
    invoiceno: 100, 'invoice no': 100, 'invoice number': 100, 'invoicenumber': 100,
    blno: 100, 'bl no': 100, 'b/l no': 100,
    pono: 100, 'po no': 100, 'po number': 100, 'purchase order': 100,
    // 품목 필드 - ⭐ description은 flex로 처리
    description: 0, desc: 0, 품명: 0, 'goods description': 0, 'description of goods': 0, 'descriptionofgoods': 0,
    material: 80, 'material no': 80, materialcode: 80,
    productcode: 80, 'product code': 80,
    hscode: 75, 'hs code': 75, hsn: 75, hts: 75, tariff: 75,
    origin: 60, 'country of origin': 80, coo: 50,
    composition: 60,
    // 수량/금액 필드
    quantity: 60, qty: 60, '수량': 60, 'unitquantity': 60, 'unit quantity': 60,
    unit: 50, uom: 50, 'unit of measure': 60, unittype: 50, 'unit type': 50,
    unitprice: 70, 'unit price': 70, 단가: 70, price: 70,
    amount: 80, total: 80, 금액: 80, 'total amount': 90,
    currency: 55, curr: 50,
    // 중량/용적 필드
    grossweight: 75, 'gross weight': 75, netweight: 75, 'net weight': 75, weight: 70,
    cbm: 60, m3: 60, volume: 70,
    packages: 60, cartons: 60, ctns: 55, pcs: 50,
    incoterms: 70,
  },

  // 🏢 사업자등록증
  businessRegistration: {
    사업자등록번호: 130, 사업자번호: 130,
    상호: 150, 법인명: 150, 회사명: 150,
    대표자: 100, 대표자명: 100, 성명: 80,
    업태: 120, 종목: 120, 업종: 100,
    사업장주소: 250, 주소: 200, 본점소재지: 250,
    법인등록번호: 130, 개업일: 90, 개업연월일: 100,
  },
};

/**
 * 3단계: 필드 키워드 → 데이터타입 매핑 (기본 너비 fallback)
 */
const FIELD_TYPE_PATTERNS = [
  // 인덱스/순번
  { pattern: /^(item|no|seq|순번|번호)$/i, type: 'index' },
  // 금액
  { pattern: /(amount|total|price|금액|가액|합계)/i, type: 'currency' },
  // 수량
  { pattern: /(quantity|qty|수량|개수)/i, type: 'quantity' },
  // 단위
  { pattern: /^(unit|uom|단위)$/i, type: 'unit' },
  // 퍼센트
  { pattern: /(rate|percent|%|비율)/i, type: 'percentage' },
  // 날짜
  { pattern: /(date|일|일자|작성일|발행일)/i, type: 'date' },
  // 코드
  { pattern: /(code|번호|no$|number)/i, type: 'code' },
  // 긴 텍스트 (Description 등)
  { pattern: /(description|desc|품명|상호|명칭|주소|remarks|비고)/i, type: 'longText' },
  // 중간 텍스트
  { pattern: /(name|업태|종목)/i, type: 'mediumText' },
];

/**
 * 컬럼 너비 결정 함수 (계층적 lookup)
 * @param {string} fieldKey - 필드 키
 * @param {string} documentType - 문서 유형 ('shipping' | 'businessRegistration')
 * @returns {number} 컬럼 너비 (px), 0이면 flex 컬럼
 */
const getOptimalColumnWidth = (fieldKey, documentType = 'shipping') => {
  const keyLower = fieldKey.toLowerCase().replace(/[\s_-]/g, '');

  // 1단계: 문서 유형별 특화 너비 확인
  const docWidths = DOCUMENT_TYPE_WIDTHS[documentType];
  if (docWidths) {
    // 정확한 키 매치
    if (docWidths[keyLower] !== undefined) {
      return docWidths[keyLower];
    }

    // 부분 매칭 (키워드 포함 검사)
    for (const [configKey, width] of Object.entries(docWidths)) {
      if (keyLower.includes(configKey) || configKey.includes(keyLower)) {
        return width;
      }
    }
  }

  // 2단계: 필드 타입 패턴 매칭 → 기본 너비
  for (const { pattern, type } of FIELD_TYPE_PATTERNS) {
    if (pattern.test(fieldKey)) {
      return BASE_WIDTH[type];
    }
  }

  // 3단계: 기본값 (텍스트 길이 기반)
  return fieldKey.length > 10 ? BASE_WIDTH.mediumText : BASE_WIDTH.shortText;
};

// ============================================================


export default function PageBasedGridPanel({
  documentType,
  flashData,
  proData,
  extractedData, // ⭐ 검증 후 수정된 데이터 (단일 이미지용)
  pageResults,
  totalPages,
  selectedPageNumber,
  checkedPages = [],
  fileId,
  validatedPages = {},
  onValidatePage,
  onValidateMultiplePages,
  onValidateAllPages,
  isPageValidated,
  onProceed,
  isRightPanelCollapsed,
  onSaveVehicle,

  onFieldHover, // ⭐ 새로운 prop: 필드 호버 이벤트
  onFieldClick, // ⭐ 새로운 prop: 필드 클릭 이벤트
  onPageChange, // 📄 페이지 변경 콜백
  onPageToggle, // 📄 페이지 체크 토글 콜백
  templates = {},
  selectedTemplateId = '', // ⭐ 왼쪽 패널에서 선택한 템플릿 ID
  groundedFields = {}, // ⭐ [Grounding] LangExtract grounded 필드 데이터 { fieldName: { value, bbox, confidence } }
  onOpenAIPanel, // 🤖 AI 패널 열기 콜백 (App.js 레벨) - deprecated, 인라인 사이드바로 대체
  externalAIToggle, // 🤖 App.js 툴바 AI 버튼으로부터 토글 트리거
  // 탭 통합 props (RightPanelTabs에서 전달)
  activeTab = 'results',
  onTabChange,
  hasTabChanges = false
}) {
  // 🔍 [DEBUG] Grounding 데이터 확인
  React.useEffect(() => {
    const groundedCount = Object.keys(groundedFields).length;
    const withBbox = Object.entries(groundedFields).filter(([k, v]) => v?.bbox?.coordinates).length;

    // 🆕 테이블 행 grounding 키 확인
    const tableRowKeys = Object.keys(groundedFields).filter(k => k.includes('['));

    console.log('🎯 [Grounding Debug] groundedFields:', {
      totalFields: groundedCount,
      fieldsWithBbox: withBbox,
      // 🆕 테이블 행 키 목록 (items[0], items[1] 등)
      tableRowKeys: tableRowKeys,
      hasItems0: !!groundedFields['items[0]'],
      items0Data: groundedFields['items[0]'],
      sampleFields: Object.entries(groundedFields).slice(0, 5).map(([k, v]) => ({
        key: k,
        hasBbox: !!v?.bbox?.coordinates,
        page: v?.bbox?.page,
        coords: v?.bbox?.coordinates
      }))
    });
  }, [groundedFields]);


  // ⭐ 문서 타입 변경 시 자동으로 뷰 모드 전환 (Invoice 등은 Grid 기본)


  const [viewMode, setViewMode] = useState('form'); // 기본값은 'form', useEffect에서 덮어씌움
  const [editedData, setEditedData] = useState({});
  const [hoveredField, setHoveredField] = useState(null); // ⭐ 호버된 필드

  // 📄 [Page Navigation] onPageChange 최신 참조 유지 (closure 문제 방지)
  const onPageChangeRef = useRef(onPageChange);
  useEffect(() => { onPageChangeRef.current = onPageChange; }, [onPageChange]);

  // 🎨 [Page Highlight] 현재 활성 페이지 번호 (그리드 행 하이라이트용)
  const activePageRef = useRef(null);
  const selectedRowRef = useRef(null);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [newVehicleData, setNewVehicleData] = useState({
    car_name_kr: '',
    car_name_en: '',
    brand: '',
    model_year: '',
    trade_item_name: '',
    item_name: '',
    displacement: '',
    weight: '',
    cbm: ''
  });
  const [editingMasterId, setEditingMasterId] = useState(null); // 기존 데이터 업데이트용
  const [showCarNameMappingModal, setShowCarNameMappingModal] = useState(false); // 차량명 매핑 팝업
  const [carNameMappingData, setCarNameMappingData] = useState(null); // 차량명 매핑 데이터
  const [similarMatches, setSimilarMatches] = useState([]); // 유사 차량 목록
  const [searchingSimilarity, setSearchingSimilarity] = useState(false); // 유사도 검색 중
  const [showMasterSettingsModal, setShowMasterSettingsModal] = useState(false); // 기초설정 모달
  const [columnFilter, setColumnFilter] = useState('all'); // 🔧 컬럼 필터: 'all' | 'single' | 'table'
  const [showAIPanel, setShowAIPanel] = useState(false); // 🤖 AI 인라인 사이드바
  const [aiPrompt, setAiPrompt] = useState(''); // AI 프롬프트 입력
  const [aiLoading, setAiLoading] = useState(false); // AI 변환 로딩
  const [aiResult, setAiResult] = useState(null); // { transformedData, explanation }
  const [aiError, setAiError] = useState(null); // AI 에러 메시지
  const [aiTargetColumns, setAiTargetColumns] = useState([]); // [{field, headerText}]
  const [aiMessages, setAiMessages] = useState([]); // 대화 이력 [{role, content, transformedData, timestamp}]
  const aiAbortRef = useRef(null); // AbortController for cancel
  const aiChatEndRef = useRef(null); // 채팅 자동 스크롤
  const gridRef = useRef(null);
  const [undoStack, setUndoStack] = useState([]); // 🔄 Undo 스택 (병합/셀편집 되돌리기)
  const [undoToastVisible, setUndoToastVisible] = useState(false); // 되돌리기 토스트 표시
  const undoToastTimerRef = useRef(null);
  const selectedColumnsRef = useRef([]); // 🆕 다중 열 선택 추적
  const lastClickedColumnRef = useRef(-1); // 🆕 마지막 클릭된 열 인덱스

  // 🔄 Undo 토스트 표시 함수 (3초 후 자동 숨김)
  const showUndoToast = React.useCallback(() => {
    setUndoToastVisible(true);
    if (undoToastTimerRef.current) clearTimeout(undoToastTimerRef.current);
    undoToastTimerRef.current = setTimeout(() => setUndoToastVisible(false), 5000);
  }, []);

  // 🤖 App.js 툴바 AI 버튼 토글 연동 (커스텀 이벤트)
  useEffect(() => {
    const handleToggle = () => {
      setShowAIPanel(prev => {
        if (!prev) {
          // 열 때: selectedColumnsRef에서 선택된 열 가져오기
          const selCols = selectedColumnsRef.current || [];
          const grid = gridRef.current || '#horizontal_grid';
          if (selCols.length > 0 && window.AUIGrid?.isCreated(grid)) {
            const layout = window.AUIGrid.getColumnLayout(grid);
            const targetCols = selCols.map(idx => ({
              field: layout[idx]?.dataField,
              headerText: layout[idx]?.headerText || layout[idx]?.dataField
            })).filter(c => c.field);
            setAiTargetColumns(targetCols);
          } else {
            setAiTargetColumns([]);
          }
          setAiResult(null);
          setAiError(null);
          setAiPrompt('');
          setAiMessages([]);
        }
        return !prev;
      });
    };
    window.addEventListener('toggleAIPanel', handleToggle);
    return () => window.removeEventListener('toggleAIPanel', handleToggle);
  }, []);

  // 🔄 열 선택 변경 → AI 사이드바 동기화 (CustomEvent 리스너)
  useEffect(() => {
    const handleColumnSelectionChanged = (e) => {
      if (!showAIPanel) return; // 패널 닫혀있으면 무시
      const { selectedIndices } = e.detail || {};
      const grid = gridRef.current || '#horizontal_grid';
      if (!window.AUIGrid?.isCreated(grid)) return;
      if (!selectedIndices || selectedIndices.length === 0) {
        setAiTargetColumns([]);
        return;
      }
      const layout = window.AUIGrid.getColumnLayout(grid);
      const targetCols = selectedIndices.map(idx => ({
        field: layout[idx]?.dataField,
        headerText: layout[idx]?.headerText || layout[idx]?.dataField
      })).filter(c => c.field);
      setAiTargetColumns(targetCols);
    };
    window.addEventListener('columnSelectionChanged', handleColumnSelectionChanged);
    return () => window.removeEventListener('columnSelectionChanged', handleColumnSelectionChanged);
  }, [showAIPanel]);

  const performUndo = React.useCallback(() => {
    const grid = gridRef.current;
    if (!grid || !window.AUIGrid?.isCreated(grid)) return;

    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const lastAction = prev[prev.length - 1];
      const newStack = prev.slice(0, -1);

      console.log('↩️ [Undo] 실행:', lastAction.type);

      try {
        if (lastAction.type === 'merge_column_left') {
          lastAction.beforeValues.forEach(({ rowIndex, targetValue, sourceValue }) => {
            window.AUIGrid.updateRow(grid, {
              [lastAction.targetField]: targetValue,
              [lastAction.sourceField]: sourceValue
            }, rowIndex);
          });
        } else if (lastAction.type === 'merge_up' || lastAction.type === 'merge_left') {
          lastAction.beforeValues.forEach(({ rowIndex, field, value }) => {
            window.AUIGrid.updateRow(grid, { [field]: value }, rowIndex);
          });
        } else if (lastAction.type === 'merge_columns') {
          const currentLayout = window.AUIGrid.getColumnLayout(grid);
          const currentFields = new Set(currentLayout.map(c => c.dataField));
          const sortedRemovedCols = [...lastAction.removedColumns].sort((a, b) => a.columnIndex - b.columnIndex);
          sortedRemovedCols.forEach(col => {
            if (!currentFields.has(col.dataField)) {
              window.AUIGrid.addColumn(grid, {
                dataField: col.dataField,
                headerText: col.headerText,
                width: col.width,
                editable: true
              }, col.columnIndex);
            }
          });
          lastAction.beforeValues.forEach(({ rowIndex, values }) => {
            window.AUIGrid.updateRow(grid, values, rowIndex);
          });
          console.log('↩️ [Undo] 열 병합 되돌리기:', sortedRemovedCols.length, '개 열');
        } else if (lastAction.type === 'split_column') {
          [...lastAction.createdColumns].reverse().forEach(col => {
            const freshLayout = window.AUIGrid.getColumnLayout(grid);
            const colIdx = freshLayout.findIndex(c => c.dataField === col.dataField);
            if (colIdx >= 0) {
              window.AUIGrid.removeColumn(grid, colIdx);
            }
          });
          lastAction.beforeValues.forEach(({ rowIndex, originalValue }) => {
            window.AUIGrid.updateRow(grid, { [lastAction.sourceField]: originalValue }, rowIndex);
          });
          console.log('↩️ [Undo] 열 분리 되돌리기:', lastAction.createdColumns.length, '개 열 삭제');
        } else if (lastAction.type === 'sort_column' || lastAction.type === 'delete_empty_rows') {
          // 정렬/빈행삭제 되돌리기: 전체 데이터 복원
          window.AUIGrid.setGridData(grid, lastAction.beforeData);
          console.log('↩️ [Undo]', lastAction.type, '되돌리기 완료');
        } else if (lastAction.type === 'full_snapshot') {
          // 전체 스냅샷 되돌리기: 데이터 + 열 레이아웃 복원
          // 1) 열 레이아웃 복원 (열 추가/삭제 작업용)
          if (lastAction.beforeLayout) {
            try {
              const currentLayout = window.AUIGrid.getColumnLayout(grid);
              // 현재 열 전부 제거 (역순으로)
              for (let i = currentLayout.length - 1; i >= 0; i--) {
                window.AUIGrid.removeColumn(grid, i);
              }
              // 스냅샷 열 순서대로 복원
              lastAction.beforeLayout.forEach((col, idx) => {
                window.AUIGrid.addColumn(grid, {
                  dataField: col.dataField,
                  headerText: col.headerText,
                  width: col.width || 100,
                  editable: col.editable !== false
                }, idx);
              });
            } catch (layoutErr) {
              console.warn('↩️ [Undo] 열 레이아웃 복원 실패 (데이터는 복원 진행):', layoutErr);
            }
          }
          // 2) 데이터 복원 (항상 실행)
          window.AUIGrid.setGridData(grid, lastAction.beforeData);
          console.log('↩️ [Undo] full_snapshot 되돌리기:', lastAction.label || '');
        } else if (lastAction.type === 'cell_update') {
          lastAction.beforeValues.forEach(({ rowIndex, field, value }) => {
            window.AUIGrid.updateRow(grid, { [field]: value }, rowIndex);
          });
        }
      } catch (err) {
        console.error('↩️ [Undo] 오류:', err);
      }

      // 토스트 숨김
      setUndoToastVisible(false);
      return newStack;
    });
  }, []);

  // 🆕 Ctrl+Z 전용 useEffect (capture phase로 AUIGrid보다 먼저 잡음)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        e.stopPropagation(); // AUIGrid가 이벤트 가로채지 못하도록
        performUndo();
      }
    };
    // capture: true → 이벤트 캡처 단계에서 처리 (AUIGrid 핸들러보다 먼저 실행)
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [performUndo]);

  // 🆕 헤더 컨텍스트 메뉴 오버레이 상태
  const [headerMenuVisible, setHeaderMenuVisible] = useState(false);
  const [headerMenuPosition, setHeaderMenuPosition] = useState({ x: 0, y: 0 });
  const [headerMenuTarget, setHeaderMenuTarget] = useState({ columnIndex: -1, dataField: '', headerText: '' });

  // 🆕 그리드 메뉴 액션 처리 함수 (Benchmarking 대응) - 컴포넌트 스코프로 이동
  const handleGridMenuAction = React.useCallback((actionId, targetData = {}) => {
    const grid = gridRef.current;
    if (!grid) return;

    const { rowIndex, columnIndex, dataField, selectedIndices } = targetData;
    console.log(`🚀 [GridMenu] Action: ${actionId}`, targetData);

    // 🆕 스냅샷 헬퍼: 데이터 + 열 레이아웃 저장
    const takeSnapshot = (label) => ({
      type: 'full_snapshot',
      label,
      beforeData: window.AUIGrid.getGridData(grid).map(r => ({ ...r })),
      beforeLayout: window.AUIGrid.getColumnLayout(grid).map(c => ({ ...c }))
    });

    // 📐 자동 정렬: 실제 데이터 값을 검사하여 숫자→우측, 문자→좌측 정렬
    const autoAlignColumns = () => {
      try {
        const layout = window.AUIGrid.getColumnLayout(grid);
        const data = window.AUIGrid.getGridData(grid);
        if (!layout || !data || data.length === 0) return;

        // 숫자 판별 정규식: 쉼표 포함 숫자, 소수점, 음수
        const numRegex = /^-?[\d,]+\.?\d*$/;
        const skipFields = new Set(['No', 'P', 'rowNum', 'pageNumber']);

        let cssRules = '';
        layout.forEach((col, colIdx) => {
          if (!col.dataField || skipFields.has(col.dataField)) return;

          // 비어있지 않은 셀의 값 샘플링 (최대 20개)
          let numCount = 0, totalCount = 0;
          for (let i = 0; i < Math.min(data.length, 20); i++) {
            const val = String(data[i]?.[col.dataField] || '').trim();
            if (val === '') continue;
            totalCount++;
            if (numRegex.test(val.replace(/\s/g, ''))) numCount++;
          }

          // 50% 이상이 숫자면 우측 정렬
          const isNumeric = totalCount > 0 && (numCount / totalCount) >= 0.5;
          const align = isNumeric ? 'right' : 'left';

          // AUIGrid 셀 CSS: 열 인덱스 기반 (nth-child)
          cssRules += `
            .aui-grid .aui-grid-default-column[data-column-index="${colIdx}"] {
              text-align: ${align} !important;
            }
          `;
        });

        // 기존 스타일 제거 후 새로 주입
        let styleEl = document.getElementById('auto-align-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'auto-align-style';
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = cssRules;
        console.log('📐 [AutoAlign] 열 정렬 갱신 완료');
      } catch (err) {
        console.warn('📐 [AutoAlign] 실패:', err);
      }
    };

    try {
      switch (actionId) {
        // ============ 열 관련 메뉴 ============
        case 'mergeColumns': {
          // 🔗 TEXTJOIN 스타일 열 병합 (구분자 입력, 기본값: 공백)
          const selCols = selectedColumnsRef.current;
          if (!selCols || selCols.length < 2) {
            alert('병합할 열을 2개 이상 선택하세요.\n(헤더를 클릭하여 열을 선택합니다)');
            return;
          }

          const columnLayout = window.AUIGrid.getColumnLayout(grid);
          // 선택된 열을 왼쪽→오른쪽 순서로 정렬
          const sortedCols = [...selCols].sort((a, b) => a - b);
          const colInfos = sortedCols.map(idx => ({
            index: idx,
            dataField: columnLayout[idx]?.dataField,
            headerText: columnLayout[idx]?.headerText || columnLayout[idx]?.dataField,
            width: columnLayout[idx]?.width || 100
          })).filter(c => c.dataField);

          if (colInfos.length < 2) return;

          // 구분자  입력 (TEXTJOIN 스타일)
          const colNames = colInfos.map(c => c.headerText).join(' + ');
          const separator = prompt(
            `[${colNames}] 병합\n\n` +
            `구분자를 입력하세요:\n` +
            `• 공백 1개 (기본값) → "ABC DEF"\n` +
            `• 비워두기 → "ABCDEF"\n` +
            `• 쉼표 등 자유 입력 → "ABC, DEF"`,
            ' '
          );
          if (separator === null) return; // 취소

          const targetField = colInfos[0].dataField;
          const sourceInfos = colInfos.slice(1); // 삭제될 열들
          const gridData = window.AUIGrid.getGridData(grid);

          // ⚡ Undo 스냅샷: 병합 전 각 행의 원래 값 저장 (병합 전에 반드시!)
          const beforeValues = gridData.map((row, idx) => {
            const values = {};
            colInfos.forEach(c => { values[c.dataField] = row[c.dataField] || ''; });
            return { rowIndex: idx, values };
          });

          // 병합 실행: 각 행에서 선택된 열 값을 구분자로 결합 (빈 값 스킵)
          gridData.forEach((row, idx) => {
            const parts = colInfos
              .map(c => String(row[c.dataField] || '').trim())
              .filter(v => v !== '');
            const mergedValue = parts.join(separator);
            const updateObj = { [targetField]: mergedValue };
            // 삭제할 열들은 비우기 (removeColumn 전에)
            sourceInfos.forEach(c => { updateObj[c.dataField] = ''; });
            window.AUIGrid.updateRow(grid, updateObj, idx);
          });

          // 삭제될 열 레이아웃 정보 보존 (Undo 시 복원용)
          const removedColumns = sourceInfos.map(c => ({
            dataField: c.dataField,
            headerText: c.headerText,
            width: c.width,
            columnIndex: c.index
          }));

          // 열 삭제 (뒤에서부터 삭제해야 인덱스 안 밀림)
          [...sourceInfos].reverse().forEach(c => {
            window.AUIGrid.removeColumn(grid, c.index);
          });

          // Undo 스택 저장 + 토스트 표시
          setUndoStack(prev => [...prev, {
            id: Date.now(),
            type: 'merge_columns',
            targetField,
            removedColumns,
            separator,
            beforeValues
          }]);
          showUndoToast();

          // 열 선택 상태 초기화
          selectedColumnsRef.current = [];
          lastClickedColumnRef.current = -1;
          // style 태그 제거
          const existingStyle = document.getElementById('column-selection-style');
          if (existingStyle) existingStyle.remove();
          document.querySelectorAll('.selected-column-header').forEach(el => el.classList.remove('selected-column-header'));

          console.log(`✅ [GridMenu] 열 병합 완료: ${colNames} → ${targetField} (구분자: "${separator}", ${gridData.length}행)`);
          break;
        }

        case 'mergeColumnsPrompt': {
          // 🤖 AI 프롬프트로 열 변환 — 인라인 사이드바 열기
          const selCols = selectedColumnsRef.current;
          if (!selCols || selCols.length < 1) {
            alert('변환할 열을 1개 이상 선택하세요.');
            return;
          }
          const layout = window.AUIGrid.getColumnLayout(grid);
          const targetCols = selCols.map(idx => ({
            field: layout[idx]?.dataField,
            headerText: layout[idx]?.headerText || layout[idx]?.dataField
          })).filter(c => c.field);
          setAiTargetColumns(targetCols);
          setAiResult(null);
          setAiError(null);
          setAiPrompt('');
          setShowAIPanel(true);
          // 툴바 버튼 상태 동기화
          window.dispatchEvent(new CustomEvent('aiPanelStateChange', { detail: { open: true } }));
          break;
        }

        case 'splitColumn': {
          // ✂️ 열 분리 (Text to Columns) — 병합의 반대
          const splitColIdx = columnIndex >= 0 ? columnIndex : (selectedColumnsRef.current?.[0] ?? -1);
          if (splitColIdx < 0) {
            alert('분리할 열을 선택하세요.');
            return;
          }

          const columnLayout = window.AUIGrid.getColumnLayout(grid);
          const sourceCol = columnLayout[splitColIdx];
          if (!sourceCol?.dataField) return;

          const sourceField = sourceCol.dataField;
          const sourceHeader = sourceCol.headerText || sourceField;

          // 구분자 + 최대 분리 수 입력
          const delimiter = prompt(
            `[${sourceHeader}] 열 분리\n\n` +
            `구분자를 입력하세요:\n` +
            `• 공백 (기본값)\n` +
            `• 쉼표, 탭, 기타 자유 입력`,
            ' '
          );
          if (delimiter === null) return;

          const maxSplitInput = prompt(
            `최대 분리 수를 입력하세요:\n` +
            `• 2 (기본값) → 원본열이 2개 열로 분리\n` +
            `• 3 → 3개 열로 분리\n` +
            `• 첫 구분자 기준으로만 분리하려면 2`,
            '2'
          );
          if (maxSplitInput === null) return;
          const maxParts = Math.max(parseInt(maxSplitInput) || 2, 2);

          const gridData = window.AUIGrid.getGridData(grid);

          // 1. 모든 행을 분리 (최대 maxParts개로 제한)
          const allParts = gridData.map(row => {
            const val = String(row[sourceField] || '').trim();
            if (!val) return Array(maxParts).fill('');
            const parts = val.split(delimiter);
            // maxParts개로 제한: 앞부분은 개별 분리, 나머지는 마지막에 합침
            if (parts.length > maxParts) {
              const limited = parts.slice(0, maxParts - 1);
              limited.push(parts.slice(maxParts - 1).join(delimiter));
              return limited.map(s => s.trim());
            }
            return parts.map(s => s.trim());
          });

          // Undo 스냅샷: 원래 값 저장
          const beforeValues = gridData.map((row, idx) => ({
            rowIndex: idx,
            originalValue: row[sourceField] || ''
          }));

          // 2. 새 열 생성 (sourceHeader_1, sourceHeader_2, ...)
          const createdColumns = [];
          for (let i = 0; i < maxParts; i++) {
            const newField = `${sourceField}_split_${i + 1}`;
            const newHeader = `${sourceHeader}_${i + 1}`;
            const insertIdx = splitColIdx + 1 + i;
            window.AUIGrid.addColumn(grid, {
              dataField: newField,
              headerText: newHeader,
              width: sourceCol.width || 100,
              editable: true
            }, insertIdx);
            createdColumns.push({ dataField: newField, headerText: newHeader, columnIndex: insertIdx });
          }

          // 3. 데이터 분배
          gridData.forEach((row, idx) => {
            const parts = allParts[idx];
            const updateObj = { [sourceField]: '' }; // 원본 열은 비우기
            createdColumns.forEach((col, i) => {
              updateObj[col.dataField] = parts[i] || '';
            });
            window.AUIGrid.updateRow(grid, updateObj, idx);
          });

          // Undo 스택 저장 + 토스트 표시
          setUndoStack(prev => [...prev, {
            id: Date.now(),
            type: 'split_column',
            sourceField,
            createdColumns,
            beforeValues
          }]);
          showUndoToast();

          // 열 선택 초기화
          selectedColumnsRef.current = [];
          lastClickedColumnRef.current = -1;
          const existingStyle = document.getElementById('column-selection-style');
          if (existingStyle) existingStyle.remove();
          document.querySelectorAll('.selected-column-header').forEach(el => el.classList.remove('selected-column-header'));

          console.log(`✅ [GridMenu] 열 분리 완료: ${sourceHeader} → ${maxParts}개 열 (구분자: "${delimiter}")`);
          break;
        }

        case 'trimColumn': {
          // ✨ Trim — Excel TRIM과 동일: 앞뒤 공백 제거 + 내부 연속 공백을 1개로
          const trimCols = selectedColumnsRef.current?.length > 0
            ? selectedColumnsRef.current
            : (columnIndex >= 0 ? [columnIndex] : []);
          if (trimCols.length === 0) { alert('Trim할 열을 선택하세요.'); return; }

          const colLayout = window.AUIGrid.getColumnLayout(grid);
          const gridData = window.AUIGrid.getGridData(grid);
          const beforeVals = [];
          const changeLog = []; // 변경 내역 미리보기용
          let trimCount = 0;
          const processedHeaders = [];

          const truncate = (s, len = 30) => s.length > len ? s.slice(0, len) + '…' : s;

          trimCols.forEach(ci => {
            const field = colLayout[ci]?.dataField;
            const header = colLayout[ci]?.headerText || field;
            if (!field) return;
            processedHeaders.push(header);
            gridData.forEach((row, ri) => {
              const original = String(row[field] || '');
              // Excel TRIM: 앞뒤 공백 제거 + 내부 연속 공백 → 단일 공백
              const trimmed = original.trim().replace(/\s+/g, ' ');
              if (original !== trimmed) {
                beforeVals.push({ rowIndex: ri, field, value: original });
                changeLog.push({ row: ri + 1, before: original, after: trimmed });
                window.AUIGrid.updateRow(grid, { [field]: trimmed }, ri);
                trimCount++;
              }
            });
          });

          if (trimCount > 0) {
            setUndoStack(prev => [...prev, {
              id: Date.now(), type: 'cell_update', beforeValues: beforeVals
            }]);
            showUndoToast();

            // 미리보기: 5개 이하 → 전부, 5개 초과 → 처음 3개 + "...외 N개"
            const maxPreview = trimCount <= 5 ? trimCount : 3;
            const preview = changeLog.slice(0, maxPreview).map(c =>
              `  • 행 ${c.row}: "${truncate(c.before)}" → "${truncate(c.after)}"`
            ).join('\n');
            const suffix = trimCount > 5 ? `\n  ...외 ${trimCount - 3}개` : '';

            alert(
              `✅ ${trimCount}개 셀의 공백이 제거되었습니다.\n\n` +
              `대상 열: ${processedHeaders.join(', ')}\n\n` +
              `${preview}${suffix}\n\n` +
              `Ctrl+Z로 되돌릴 수 있습니다.`
            );
          } else {
            alert(`공백이 있는 셀이 없습니다.\n\n대상 열: ${processedHeaders.join(', ')}\n→ 이미 깨끗합니다 ✨`);
          }
          console.log(`✅ [GridMenu] Trim 완료: ${trimCount}개 셀 수정`);
          break;
        }

        case 'uppercaseColumn': {
          // 🔠 UPPERCASE — 선택 열의 영문을 대문자로 변환
          const ucCols = selectedColumnsRef.current?.length > 0
            ? selectedColumnsRef.current
            : (columnIndex >= 0 ? [columnIndex] : []);
          if (ucCols.length === 0) { alert('대문자 변환할 열을 선택하세요.'); return; }

          const ucLayout = window.AUIGrid.getColumnLayout(grid);
          const ucData = window.AUIGrid.getGridData(grid);
          const ucBeforeVals = [];
          const ucChangeLog = [];
          let ucCount = 0;
          const ucHeaders = [];
          const ucTruncate = (s, len = 30) => s.length > len ? s.slice(0, len) + '…' : s;

          ucCols.forEach(ci => {
            const field = ucLayout[ci]?.dataField;
            const header = ucLayout[ci]?.headerText || field;
            if (!field) return;
            ucHeaders.push(header);
            ucData.forEach((row, ri) => {
              const original = String(row[field] || '');
              const uppercased = original.toUpperCase();
              if (original !== uppercased) {
                ucBeforeVals.push({ rowIndex: ri, field, value: original });
                ucChangeLog.push({ row: ri + 1, before: original, after: uppercased });
                window.AUIGrid.updateRow(grid, { [field]: uppercased }, ri);
                ucCount++;
              }
            });
          });

          if (ucCount > 0) {
            setUndoStack(prev => [...prev, {
              id: Date.now(), type: 'cell_update', beforeValues: ucBeforeVals
            }]);
            showUndoToast();

            const maxP = ucCount <= 5 ? ucCount : 3;
            const preview = ucChangeLog.slice(0, maxP).map(c =>
              `  • 행 ${c.row}: "${ucTruncate(c.before)}" → "${ucTruncate(c.after)}"`
            ).join('\n');
            const sfx = ucCount > 5 ? `\n  ...외 ${ucCount - 3}개` : '';

            alert(
              `✅ ${ucCount}개 셀이 대문자로 변환되었습니다.\n\n` +
              `대상 열: ${ucHeaders.join(', ')}\n\n` +
              `${preview}${sfx}\n\n` +
              `Ctrl+Z로 되돌릴 수 있습니다.`
            );
          } else {
            alert(`변환할 소문자가 없습니다.\n\n대상 열: ${ucHeaders.join(', ')}\n→ 이미 모두 대문자입니다`);
          }
          console.log(`✅ [GridMenu] UPPERCASE 완료: ${ucCount}개 셀 수정`);
          break;
        }

        case 'sortColumn': {
          // 🔼 정렬 — 클릭한 열 기준 오름/내림차순
          const sortColIdx = columnIndex >= 0 ? columnIndex : (selectedColumnsRef.current?.[0] ?? -1);
          if (sortColIdx < 0) { alert('정렬할 열을 선택하세요.'); return; }

          const sortLayout = window.AUIGrid.getColumnLayout(grid);
          const sortField = sortLayout[sortColIdx]?.dataField;
          const sortHeader = sortLayout[sortColIdx]?.headerText || sortField;
          if (!sortField) return;

          const direction = prompt(
            `[${sortHeader}] 정렬 방향:\n\n` +
            `• 1 = 오름차순 (A→Z, 1→9)\n` +
            `• 2 = 내림차순 (Z→A, 9→1)`,
            '1'
          );
          if (direction === null) return;
          const isAsc = direction !== '2';

          const sortData = window.AUIGrid.getGridData(grid);
          // Undo: 전체 행 순서 스냅샷
          const beforeOrder = sortData.map((row, idx) => ({ rowIndex: idx, row: { ...row } }));

          const sorted = [...sortData].sort((a, b) => {
            const va = String(a[sortField] || '').trim();
            const vb = String(b[sortField] || '').trim();
            // 숫자 비교 시도
            const na = parseFloat(va), nb = parseFloat(vb);
            if (!isNaN(na) && !isNaN(nb)) return isAsc ? na - nb : nb - na;
            return isAsc ? va.localeCompare(vb) : vb.localeCompare(va);
          });

          window.AUIGrid.setGridData(grid, sorted);

          setUndoStack(prev => [...prev, {
            id: Date.now(), type: 'sort_column', beforeData: sortData
          }]);
          showUndoToast();
          console.log(`✅ [GridMenu] 정렬 완료: ${sortHeader} ${isAsc ? '오름차순' : '내림차순'}`);
          break;
        }

        case 'deleteEmptyRows': {
          // 🗑️ 빈 행 삭제 — 모든 셀이 비어있는 행 일괄 제거
          const emptyData = window.AUIGrid.getGridData(grid);
          const emptyLayout = window.AUIGrid.getColumnLayout(grid);
          const dataFields = emptyLayout.map(c => c.dataField).filter(Boolean);

          const emptyRowIndices = [];
          emptyData.forEach((row, idx) => {
            const allEmpty = dataFields.every(f => {
              const v = String(row[f] || '').trim();
              return v === '' || v === 'undefined' || v === 'null';
            });
            if (allEmpty) emptyRowIndices.push(idx);
          });

          if (emptyRowIndices.length === 0) {
            alert('빈 행이 없습니다.');
            return;
          }

          const confirmDel = window.confirm(`빈 행 ${emptyRowIndices.length}개를 삭제하시겠습니까?`);
          if (!confirmDel) return;

          // Undo: 전체 데이터 스냅샷
          const beforeEmptyData = JSON.parse(JSON.stringify(emptyData));

          // 뒤에서부터 삭제 (인덱스 변동 방지)
          [...emptyRowIndices].reverse().forEach(idx => {
            window.AUIGrid.removeRow(grid, idx);
          });

          setUndoStack(prev => [...prev, {
            id: Date.now(), type: 'delete_empty_rows', beforeData: beforeEmptyData
          }]);
          showUndoToast();
          console.log(`✅ [GridMenu] 빈 행 삭제 완료: ${emptyRowIndices.length}개 행`);
          break;
        }
        case 'addColumnBySelection': {
          const newColName = prompt('새 열 이름을 입력하세요:', 'NewColumn');
          if (!newColName) return;
          const newColumn = {
            dataField: newColName.replace(/\s/g, '_'),
            headerText: newColName,
            width: 100,
            editable: true
          };
          window.AUIGrid.addColumn(grid, newColumn, columnIndex + 1);
          console.log('✅ [GridMenu] 열 추가:', newColName);
          break;
        }

        case 'copyColumn': {
          if (!dataField) return;
          const newColName = prompt('복사할 열 이름을 입력하세요:', dataField + '_copy');
          if (!newColName) return;

          const gridData = window.AUIGrid.getGridData(grid);
          const newColumn = {
            dataField: newColName.replace(/\s/g, '_'),
            headerText: newColName,
            width: 100,
            editable: true
          };
          window.AUIGrid.addColumn(grid, newColumn, columnIndex + 1);

          gridData.forEach((row, idx) => {
            window.AUIGrid.updateRow(grid, { [newColName.replace(/\s/g, '_')]: row[dataField] || '' }, idx);
          });
          console.log('✅ [GridMenu] 열 복사:', dataField, '->', newColName);
          break;
        }

        case 'addColumn': {
          const newColName = prompt('새 열 이름을 입력하세요:', 'NewColumn');
          if (!newColName) return;
          const snap_addCol = takeSnapshot('열 추가');
          const newColumn = {
            dataField: newColName.replace(/\s/g, '_'),
            headerText: newColName,
            width: 100,
            editable: true
          };
          window.AUIGrid.addColumn(grid, newColumn, columnIndex + 1);
          setUndoStack(prev => [...prev, snap_addCol]);
          showUndoToast();
          console.log('✅ [GridMenu] 열 추가:', newColName);
          break;
        }

        case 'deleteColumn': {
          if (!dataField) {
            alert('삭제할 열을 선택하세요.');
            return;
          }
          const confirmDelete = window.confirm(`'${dataField}' 열을 삭제하시겠습니까?`);
          if (!confirmDelete) return;
          const snap_delCol = takeSnapshot('열 삭제');
          window.AUIGrid.removeColumn(grid, columnIndex);
          setUndoStack(prev => [...prev, snap_delCol]);
          showUndoToast();
          console.log('✅ [GridMenu] 열 삭제:', dataField);
          break;
        }

        case 'addAmountColumn': {
          const qtyField = prompt('수량 열 이름을 입력하세요:', 'qty');
          if (!qtyField) return;
          const priceField = prompt('단가 열 이름을 입력하세요:', 'uprice');
          if (!priceField) return;

          const snap_amt = takeSnapshot('금액 열 추가');
          const amountColName = 'amount_calc';
          const newColumn = {
            dataField: amountColName,
            headerText: '금액(계산)',
            width: 100,
            editable: false,
            style: 'text-align: right;'
          };
          window.AUIGrid.addColumn(grid, newColumn);

          const gridData = window.AUIGrid.getGridData(grid);
          gridData.forEach((row, idx) => {
            const qty = parseFloat(String(row[qtyField] || '0').replace(/,/g, '')) || 0;
            const price = parseFloat(String(row[priceField] || '0').replace(/,/g, '')) || 0;
            const amount = qty * price;
            window.AUIGrid.updateRow(grid, { [amountColName]: amount.toLocaleString() }, idx);
          });
          setUndoStack(prev => [...prev, snap_amt]);
          showUndoToast();
          console.log('✅ [GridMenu] 금액 열 추가');
          break;
        }

        case 'addUnitPriceColumn': {
          const qtyField = prompt('수량 열 이름을 입력하세요:', 'qty');
          if (!qtyField) return;
          const amountField = prompt('금액 열 이름을 입력하세요:', 'amount');
          if (!amountField) return;

          const snap_uprc = takeSnapshot('단가 열 추가');
          const unitPriceColName = 'uprice_calc';
          const newColumn = {
            dataField: unitPriceColName,
            headerText: '단가(계산)',
            width: 100,
            editable: false,
            style: 'text-align: right;'
          };
          window.AUIGrid.addColumn(grid, newColumn);

          const gridData = window.AUIGrid.getGridData(grid);
          gridData.forEach((row, idx) => {
            const qty = parseFloat(String(row[qtyField] || '0').replace(/,/g, '')) || 0;
            const amt = parseFloat(String(row[amountField] || '0').replace(/,/g, '')) || 0;
            const unitPrice = qty > 0 ? amt / qty : 0;
            window.AUIGrid.updateRow(grid, { [unitPriceColName]: unitPrice.toFixed(2) }, idx);
          });
          setUndoStack(prev => [...prev, snap_uprc]);
          showUndoToast();
          console.log('✅ [GridMenu] 단가 열 추가');
          break;
        }

        // ============ 행 관련 메뉴 ============
        case 'insertRow': {
          const count = targetData.rowCount || 1;
          const snap_insRow = takeSnapshot('행 삽입');
          for (let i = 0; i < count; i++) {
            window.AUIGrid.addRow(grid, {}, rowIndex);
          }
          setUndoStack(prev => [...prev, snap_insRow]);
          showUndoToast();
          console.log(`✅ [GridMenu] 행 ${count}개 삽입:`, rowIndex);
          break;
        }

        case 'addRow': {
          const snap_addRow = takeSnapshot('행 추가');
          window.AUIGrid.addRow(grid, {}, 'last');
          setUndoStack(prev => [...prev, snap_addRow]);
          showUndoToast();
          console.log('✅ [GridMenu] 행 추가');
          break;
        }

        case 'deleteSelectedRows': {
          const rowIndicesToDel = (selectedIndices && selectedIndices.length > 0)
            ? [...selectedIndices].sort((a, b) => b - a)
            : [rowIndex].filter(i => i >= 0);

          if (rowIndicesToDel.length === 0) {
            alert('삭제할 행을 선택하세요.');
            return;
          }

          const msg = rowIndicesToDel.length > 1
            ? `선택된 ${rowIndicesToDel.length}개 항목을 삭제하시겠습니까?`
            : `${rowIndicesToDel[0] + 1}번 행을 삭제하시겠습니까?`;

          if (window.confirm(msg)) {
            const snap_delRows = takeSnapshot('행 삭제');
            rowIndicesToDel.forEach(idx => {
              window.AUIGrid.removeRow(grid, idx);
            });
            setUndoStack(prev => [...prev, snap_delRows]);
            showUndoToast();
            console.log('✅ [GridMenu] 행 삭제 완료:', rowIndicesToDel.length, '개');
          }
          break;
        }

        case 'mergeUp': {
          if (rowIndex <= 0) {
            alert('위로 병합할 수 없습니다.');
            return;
          }
          const gridData = window.AUIGrid.getGridData(grid);
          const currentValue = gridData[rowIndex]?.[dataField] || '';
          const aboveValue = gridData[rowIndex - 1]?.[dataField] || '';
          const mergedValue = aboveValue ? `${aboveValue}, ${currentValue}` : currentValue;

          const snap_mergeUp = takeSnapshot('위로 병합');
          window.AUIGrid.updateRow(grid, { [dataField]: mergedValue }, rowIndex - 1);
          window.AUIGrid.updateRow(grid, { [dataField]: '' }, rowIndex);
          setUndoStack(prev => [...prev, snap_mergeUp]);
          showUndoToast();
          console.log('✅ [GridMenu] 위로 병합:', dataField);
          break;
        }

        case 'clearCell': {
          const items = window.AUIGrid.getSelectedItems(grid);
          const snap_clear = takeSnapshot('셀 내용 지우기');
          if (!items || items.length === 0) {
            if (dataField) {
              window.AUIGrid.updateRow(grid, { [dataField]: '' }, rowIndex);
              setUndoStack(prev => [...prev, snap_clear]);
              showUndoToast();
            }
            return;
          }
          items.forEach(item => {
            if (item.dataField) window.AUIGrid.updateRow(grid, { [item.dataField]: '' }, item.rowIndex);
          });
          setUndoStack(prev => [...prev, snap_clear]);
          showUndoToast();
          console.log('✅ [GridMenu] 셀 삭제:', items.length, '개');
          break;
        }

        case 'mergeLeft': {
          const columnLayout = window.AUIGrid.getColumnLayout(grid);
          if (columnIndex <= 0) {
            alert('왼쪽으로 병합할 수 없습니다.');
            return;
          }
          const leftField = columnLayout[columnIndex - 1]?.dataField;
          if (!leftField) return;

          const gridData = window.AUIGrid.getGridData(grid);
          const currentValue = gridData[rowIndex]?.[dataField] || '';
          const leftValue = gridData[rowIndex]?.[leftField] || '';
          const mergedValue = leftValue ? `${leftValue}, ${currentValue}` : currentValue;

          const snap_mergeLeft = takeSnapshot('왼쪽으로 병합');
          window.AUIGrid.updateRow(grid, { [leftField]: mergedValue, [dataField]: '' }, rowIndex);
          setUndoStack(prev => [...prev, snap_mergeLeft]);
          showUndoToast();
          console.log('✅ [GridMenu] 왼쪽으로 병합:', leftField, '<-', dataField);
          break;
        }

        case 'shiftCellsLeft': {
          const gridData = window.AUIGrid.getGridData(grid);
          const columnLayout = window.AUIGrid.getColumnLayout(grid);
          const row = gridData[rowIndex];
          if (!row) return;

          const snap_shiftL = takeSnapshot('셀 왼쪽 밀기');
          const updatedRow = { ...row };
          for (let i = columnIndex; i < columnLayout.length - 1; i++) {
            const currentField = columnLayout[i]?.dataField;
            const nextField = columnLayout[i + 1]?.dataField;
            if (currentField && nextField) updatedRow[currentField] = row[nextField] || '';
          }
          const lastField = columnLayout[columnLayout.length - 1]?.dataField;
          if (lastField) updatedRow[lastField] = '';

          window.AUIGrid.updateRow(grid, updatedRow, rowIndex);
          setUndoStack(prev => [...prev, snap_shiftL]);
          showUndoToast();
          console.log('✅ [GridMenu] 셀을 왼쪽으로 밀기:', rowIndex, columnIndex);
          break;
        }

        case 'shiftCellsUp': {
          const snap_shiftU = takeSnapshot('셀 위로 밀기');
          const gridData = window.AUIGrid.getGridData(grid);
          for (let i = rowIndex; i < gridData.length - 1; i++) {
            const nextValue = gridData[i + 1]?.[dataField] || '';
            window.AUIGrid.updateRow(grid, { [dataField]: nextValue }, i);
          }
          if (gridData.length > 0) window.AUIGrid.updateRow(grid, { [dataField]: '' }, gridData.length - 1);
          setUndoStack(prev => [...prev, snap_shiftU]);
          showUndoToast();
          console.log('✅ [GridMenu] 셀을 위로 밀기:', rowIndex, dataField);
          break;
        }

        case 'showDeleteDialog': {
          const deleteOption = window.prompt(
            '삭제 옵션을 선택하세요:\n\n' +
            '1: 셀을 왼쪽으로 밀기\n' +
            '2: 셀을 위로 밀기\n' +
            '3: 행 삭제\n' +
            '4: 열 삭제\n' +
            '5: 셀 내용만 지우기',
            '5'
          );
          if (!deleteOption) return;

          switch (deleteOption) {
            case '1': handleGridMenuAction('shiftCellsLeft', targetData); break;
            case '2': handleGridMenuAction('shiftCellsUp', targetData); break;
            case '3': handleGridMenuAction('deleteSelectedRows', targetData); break;
            case '4': handleGridMenuAction('deleteColumn', targetData); break;
            case '5': handleGridMenuAction('clearCell', targetData); break;
          }
          break;
        }

        default:
          console.warn('Unknown GridMenu action:', actionId);
          break;
      }
    } catch (err) {
      console.error('❌ [GridMenu] Action Error:', err);
    }

    // 🎨 액션 완료 후 열 선택 하이라이트 제거
    selectedColumnsRef.current = [];
    lastClickedColumnRef.current = -1;
    const existingStyle = document.getElementById('column-selection-style');
    if (existingStyle) existingStyle.remove();
    document.querySelectorAll('.selected-column-header').forEach(el => el.classList.remove('selected-column-header'));

    // 📐 모든 액션 완료 후 자동 정렬 갱신
    autoAlignColumns();
  }, []);

  // 🆕 그리드 셀 컨텍스트 메뉴 오버레이 상태 (Benchmarking)
  const [gridMenuVisible, setGridMenuVisible] = useState(false);
  const [gridMenuPosition, setGridMenuPosition] = useState({ x: 0, y: 0 });
  const [gridMenuTarget, setGridMenuTarget] = useState({
    rowIndex: -1,
    columnIndex: -1,
    dataField: '',
    rowCount: 1,
    rowRange: '',
    selectedIndices: []
  });

  // 페이지 수 (pageResults가 있으면 PDF, 없으면 단일 이미지)
  const pageCount = totalPages || 1;
  const isPDF = !!pageResults || (totalPages && totalPages > 1);
  const currentPage = selectedPageNumber || 1; // 선택된 페이지 번호


  // 디버깅 로그 제거 (무한 루프 방지)

  // 현재 페이지의 데이터 가져오기 (useMemo로 메모이제이션)
  const pageData = React.useMemo(() => {
    // 🔍 디버깅: 입력 props 확인
    console.log('🔍 [PageBasedGridPanel] pageData 계산:', {
      isPDF,
      pageResultsLength: pageResults?.length,
      currentPage,
      hasFlashData: !!flashData,
      hasProData: !!proData,
      hasExtractedData: !!extractedData,
      flashDataKeys: flashData ? Object.keys(flashData) : [],
      proDataKeys: proData ? Object.keys(proData) : [],
      extractedDataKeys: extractedData ? Object.keys(extractedData) : [],
      pageResults0: pageResults?.[0] ? {
        pageNumber: pageResults[0].pageNumber,
        hasFlashData: !!pageResults[0].flashData,
        hasProData: !!pageResults[0].proData,
        hasExtractedData: !!pageResults[0].extractedData,
        keys: Object.keys(pageResults[0])
      } : null
    });

    if (isPDF && pageResults && pageResults.length > 0) {
      // 📄 현재 페이지 결과 찾기
      let pageResult = pageResults.find(p => p.pageNumber === currentPage);

      // 🔧 페이지별 결과가 없으면 첫 번째 결과 사용 (모든 데이터가 page 1에 저장된 경우)
      // 이 경우 테이블 데이터는 P 필드로 필터링됨
      if (!pageResult && pageResults.length > 0) {
        console.log('📄 [PageBasedGridPanel] 현재 페이지 결과 없음, pageResults[0] 사용:', currentPage);
        pageResult = pageResults[0];
      }

      // ⭐ 검증 후 수정된 데이터(extractedData)가 있으면 우선 사용
      // extractedData는 검증 시 사용자가 수정한 최종 데이터
      let pageExtractedData = pageResult?.extractedData;

      // 🔧 FIX: extractedData에 중첩된 pageResults가 있으면 해당 페이지 데이터 추출
      if (pageExtractedData?.pageResults?.length > 0) {
        const nestedPageData = pageExtractedData.pageResults.find(
          p => p.pageNumber === currentPage || p.P === currentPage
        ) || pageExtractedData.pageResults[0];

        if (nestedPageData && Object.keys(nestedPageData).length > 0) {
          console.log('✅ [PageBasedGridPanel] Using nested pageResults data for page:', currentPage);
          pageExtractedData = nestedPageData;
        }
      }

      // 🔧 FIX: extractedData가 비어있으면 pageResult 자체를 사용 (단일 필드 템플릿 호환)
      // 백엔드가 pageResults[].CBM, pageResults[].길 이 형태로 직접 필드를 반환하는 경우
      if (!pageExtractedData || Object.keys(pageExtractedData).length === 0) {
        const metaKeys = ['pageNumber', 'P', 'p', 'page', 'flashData', 'proData', 'extractedData', 'documentType', 'differences', 'ocrImageSize', 'boundingBoxes', 'correctedImageUrl', 'pngImage'];
        const dataKeys = Object.keys(pageResult || {}).filter(k => !metaKeys.includes(k));
        if (dataKeys.length > 0) {
          console.log('📄 [pageData] pageResult 객체 자체를 extractedData로 사용:', currentPage, 'keys:', dataKeys.slice(0, 5));
          pageExtractedData = pageResult;
        }
      }

      console.log('🔍 [PageBasedGridPanel] PDF 모드 - pageResults 구조:', {
        totalPageResults: pageResults.length,
        currentPage,
        allPageNumbers: pageResults.map(p => p.pageNumber),
        usingFallback: !pageResults.find(p => p.pageNumber === currentPage),
        currentPageResult: pageResult ? {
          pageNumber: pageResult.pageNumber,
          hasFlashData: !!pageResult.flashData,
          hasProData: !!pageResult.proData,
          hasExtractedData: !!pageExtractedData,
          flashDataKeys: pageResult.flashData ? Object.keys(pageResult.flashData) : [],
          proDataKeys: pageResult.proData ? Object.keys(pageResult.proData) : []
        } : 'NOT FOUND'
      });

      console.log('🔍 [PageBasedGridPanel] PDF 모드 - pageResult:', {
        found: !!pageResult,
        pageNumber: pageResult?.pageNumber,
        hasFlashData: !!pageResult?.flashData,
        hasProData: !!pageResult?.proData,
        hasExtractedData: !!pageExtractedData
      });

      return {
        flashData: pageResult?.flashData,
        proData: pageResult?.proData,
        extractedData: pageExtractedData, // ⭐ 검증 후 수정된 데이터
        documentType: pageResult?.documentType || pageExtractedData?.documentType,
        partial: pageResult?.partial, // ⭐ 부분 결과 플래그 추가
        differences: pageResult?.differences, // ⭐ 백엔드에서 계산된 차이점 객체 추가
        _currentPage: currentPage // 📄 현재 페이지 정보 전달 (테이블 필터링용)
      };
    }
    // 단일 이미지인 경우 extractedData prop 사용 (props에서 가져옴)
    console.log('🔍 [PageBasedGridPanel] 단일 이미지 모드 - props 사용');
    return {
      flashData,
      proData,
      extractedData: extractedData || null, // ⭐ 단일 이미지의 경우 prop에서 가져옴
      documentType,
      partial: false
    };
  }, [isPDF, pageResults, currentPage, flashData, proData, extractedData, documentType]);


  // ⭐ 문서 타입에 따른 뷰 모드 전환 (matchedTemplate 선언 이후에 별도 useEffect로 처리)

  // 문서 타입 가져오기 - 🔧 editedData.documentType 우선 (드롭다운 선택 반영)
  const documentTypeForLabel = editedData.documentType || pageData.extractedData?.documentType || pageData.proData?.documentType || pageData.flashData?.documentType || pageData.documentType;

  // ⭐ 매칭되는 템플릿 찾기 (selectedTemplateId 우선, 없으면 문서타입으로 자동 매칭)
  const matchedTemplate = React.useMemo(() => {
    if (!templates) return null;

    // 1️⃣ 왼쪽 패널에서 명시적으로 선택한 템플릿이 있으면 우선 사용
    if (selectedTemplateId) {
      const selectedTemplate = Object.values(templates).find(t => t.id === selectedTemplateId);
      if (selectedTemplate) return selectedTemplate;
    }

    // 2️⃣ 선택된 템플릿이 없으면 문서 타입으로 자동 매칭
    const typeLabel = documentTypeForLabel?.toUpperCase().replace(/ /g, '_') || '';
    return Object.values(templates).find(t => {
      const templateName = t.name.toUpperCase().replace(/ /g, '_');
      // 정확 일치 또는 포함 관계 (Invoice <-> Commercial Invoice)
      return templateName === typeLabel || typeLabel.includes(templateName) || templateName.includes(typeLabel);
    });
  }, [templates, documentTypeForLabel, selectedTemplateId]);

  // ⭐ 템플릿 확정 후 뷰 모드 자동 전환
  useEffect(() => {
    // 📄 템플릿에 tableFields가 있으면 horizontal 모드로 전환
    if (matchedTemplate?.tableFields?.length > 0) {
      console.log('✅ [viewMode] Horizontal Grid Mode Auto-Switch (tableFields detected):', matchedTemplate.name);
      setViewMode('horizontal');
      return;
    }

    const type = documentType || pageData?.documentType || '';
    if (!type) return;

    // 인보이스, 패킹리스트 류 감지
    const isMultiRow = ['INVOICE', 'PACKING_LIST', 'COMMERCIAL_INVOICE', 'B/L', 'BILL_OF_LADING'].some(
      t => type.toUpperCase().replace(/ /g, '_').includes(t)
    );

    if (isMultiRow) {
      console.log('✅ [viewMode] Horizontal Grid Mode Auto-Switch for:', type);
      setViewMode('horizontal');
    }
  }, [matchedTemplate, documentType, pageData?.documentType]);
  // 필드명 한글 매핑 (문서 타입에 따라 다르게 표시)
  // 필드명 한글 매핑 (문서 타입에 따라 다르게 표시)
  const getFieldNameMap = () => {
    const baseMap = {
      documentType: '문서타입',
      plateNumber: '차량등록번호',
      vin: '차대번호',
      model: '차명',
      modelType: '형식',
      name: '이름',
      modelYear: '연식',
      registrationDate: '최초등록일',
      mileage: '주행거리',
      displacement: '배기량',
      deregistrationDate: '말소일자',
      disposalDate: '폐차일자',
      businessName: '상호명',
      businessNumber: '사업자번호',
      issueDate: '발급일자',
      licenseNumber: '면허번호',
      totalWeight: '총중량',
      width: '너비',
      height: '높이',
      length: '길이',
      cbm: 'CBM',
      openDate: '개업일자',
      supplierBusinessName: '공급자상호',
      supplierBusinessNumber: '공급자사업자번호',
      supplierOwnerName: '공급자성명',
      buyerBusinessName: '공급받는자상호',
      buyerBusinessNumber: '공급받는자사업자번호',
      taxAmount: '세액'
    };

    // ⭐ 템플릿이 있는 경우 모든 템플릿 필드 추가 (단일, 테이블, 합성)
    if (matchedTemplate) {
      const templateMap = { ...baseMap };

      // 단일 필드
      (matchedTemplate.singleFields || matchedTemplate.fields || []).forEach(field => {
        const key = typeof field === 'string' ? field : field.key;
        const displayName = typeof field === 'object' && field.displayName ? field.displayName : key;
        templateMap[key] = displayName;
      });

      // 테이블 컬럼
      (matchedTemplate.tableFields || []).forEach(table => {
        (table.columns || []).forEach(col => {
          templateMap[col.key] = col.displayName || col.key;
        });
      });

      // 합성 필드
      (matchedTemplate.computedFields || []).forEach(cf => {
        templateMap[cf.key] = cf.displayName || cf.key;
      });

      return templateMap;
    }

    // 경락확인서인 경우 특별 라벨 사용
    if (documentTypeForLabel === '경락확인서') {
      return {
        ...baseMap,
        ownerName: '성명',
        residentNumber: '주민(법인)등록번호',
        address: '주소(사용본거지)',
        totalAmount: '매입금액(VAT포)',
        auctionDate: '경락일자',
        buyerName: '상호',
        buyerBusinessNumber: '사업자등록번호'
      };
    }

    // 기본 매핑 (자동차등록증 등)
    return {
      ...baseMap,
      ownerName: '소유자명',
      residentNumber: '생년월일\n(법인등록번호)',
      address: '사용본거지',
      totalAmount: '총금액',
      auctionDate: '경락일자'
    };
  };

  const fieldNameMap = getFieldNameMap();

  // 체크된 페이지만 필터링
  // 📄 checkedPages가 비어있으면 모든 페이지를 사용
  // 🔧 타입 불일치 방지: pageNumber를 숫자로 변환하여 비교
  const checkedPageResults = isPDF && pageResults
    ? (checkedPages.length > 0
      ? pageResults.filter(p => {
        const pageNum = Number(p.pageNumber);
        const isChecked = checkedPages.some(cp => Number(cp) === pageNum);
        return isChecked;
      })
      : pageResults)  // 체크된 페이지 없으면 전체 사용
    : pageResults;

  console.log('🔍 [checkedPageResults]:', {
    isPDF,
    pageResultsLength: pageResults?.length,
    pageResultsPageNumbers: pageResults?.map(p => p.pageNumber),
    checkedPages,
    checkedPageResultsLength: checkedPageResults?.length
  });

  // 현재 페이지가 체크되지 않았는지 확인
  const isCurrentPageChecked = checkedPages.includes(currentPage);
  // 디버깅 로그 제거 (무한 루프 방지)

  // CBM 자동 계산 함수
  // CBM = FLOOR(길이 × 너비 × 높이 ÷ 1,000,000,000, 1)
  const calculateCBM = (length, width, height) => {
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;

    if (l === 0 || w === 0 || h === 0) return null;

  };

  // Flash와 Pro 데이터 비교 (extractedData 우선 사용)
  const compareData = (flash, pro, isPartial, extractedData = null, differences = null) => {
    const rows = [];
    // ⭐ extractedData가 있으면 모든 키를 포함하도록 함
    const allKeys = new Set([
      ...Object.keys(flash || {}),
      ...Object.keys(pro || {}),
      ...Object.keys(extractedData || {})
    ]);

    // ⭐ extractedData만 있고 다른 데이터가 없을 때도 필드 생성
    if (extractedData && Object.keys(extractedData).length > 0 && !flash && !pro) {
      // extractedData의 모든 키를 allKeys에 추가 (이미 추가되어 있지만 명시적으로 확인)
      Object.keys(extractedData).forEach(key => {
        if (key !== 'metadata' && key !== 'timestamp' && key !== 'source' && key !== 'pageNumber' && key !== 'docCode') {
          allKeys.add(key);
        }
      });
    }

    // ⭐ 문서 타입별 필수 필드 강제 추가
    const docType = extractedData?.documentType || pro?.documentType || flash?.documentType;

    // ⭐ 템플릿이 있는 경우 템플릿 필드 강제 추가
    if (matchedTemplate) {
      matchedTemplate.fields.forEach(field => allKeys.add(typeof field === 'string' ? field : field.key));
    } else if (docType === '경락확인서') {
      // 매도자 정보 필드 강제 추가 (경락확인서에 필수)
      const sellerFields = ['residentNumber', 'address'];
      sellerFields.forEach(field => allKeys.add(field));

      // 차량상세 필드 강제 추가 (경락확인서에 없을 수 있지만 템플릿 통일을 위해 표시)
      // 길이, 너비, 높이는 화면에서 제외 (스크롤 최소화)
      const vehicleDetailFields = ['modelYear', 'mileage', 'displacement', 'totalWeight', 'cbm'];
      vehicleDetailFields.forEach(field => allKeys.add(field));

      // 매수자 정보 필드 강제 추가 (경락확인서에 필수)
      const buyerFields = ['buyerName', 'buyerBusinessNumber'];
      buyerFields.forEach(field => allKeys.add(field));
    } else if (docType === '자동차등록증' || docType === '자동차등록원부' || docType === '말소등록증') {
      // ⭐ 자동차등록증/등록원부/말소등록증의 경우 차량 상세 필드 강제 추가
      // (검증 결과에는 있지만 추출 데이터에 없을 수 있음)
      // 연식, 주행거리, 배기량, 총중량, 길이, 너비, 높이 + 소유자 정보 항상 표시
      const vehicleSpecFields = [
        'ownerName', 'residentNumber', 'address', // 소유자 정보 추가
        'modelYear', 'mileage', 'displacement', 'totalWeight', 'length', 'width', 'height'
      ];
      vehicleSpecFields.forEach(field => allKeys.add(field));
    }

    // CBM 자동 계산 추가
    const flashCBM = calculateCBM(flash?.length, flash?.width, flash?.height);
    const proCBM = calculateCBM(pro?.length, pro?.width, pro?.height);
    const extractedCBM = calculateCBM(extractedData?.length, extractedData?.width, extractedData?.height);

    if (flashCBM) {
      flash = { ...flash, cbm: flashCBM };
      allKeys.add('cbm');
    }
    if (proCBM) {
      pro = { ...pro, cbm: proCBM };
      allKeys.add('cbm');
    }
    if (extractedCBM) {
      allKeys.add('cbm');
    }

    // ✅ 비교 로직 개선을 위한 정규화 함수 (백엔드와 동일하게 적용)
    const normalizeString = (str) => {
      if (!str) return '';
      let s = String(str).trim();
      if (!s) return '';
      if (/^(null|none|undefined)$/i.test(s)) return '';
      const cleanNumStr = s.replace(/,/g, '');
      const num = Number(cleanNumStr);
      if (!isNaN(num) && cleanNumStr !== '') {
        return String(num);
      }
      let normalized = s.normalize('NFC');
      normalized = normalized.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
      normalized = normalized.toUpperCase();
      normalized = normalized.replace(/[^a-zA-Z0-9가-힣]/g, '');
      return normalized;
    };

    // ⭐ 한글 필드명 <-> 영문 키 매핑 (템플릿 호환성용)
    const KEY_MAPPING = {
      '사업자등록번호': ['businessNumber', 'crn', 'regNumber'],
      '등록번호': ['businessNumber', 'plateNumber', 'regNumber'], // 문맥에 따라 다름
      '법인등록번호': ['corporateNumber', 'juridicalNumber', 'residentNumber'],
      '상호': ['businessName', 'companyName', 'name'],
      '상호(법인명)': ['businessName', 'companyName'],
      '법인명(단체명)': ['businessName', 'companyName'],
      '성명': ['ownerName', 'name', 'representative'],
      '대표자': ['ownerName', 'representative', 'ceo'],
      '개업연월일': ['openDate', 'startDate', 'establishmentDate'],
      '사업장소재지': ['address', 'businessAddress'],
      '사업장 소재지': ['address', 'businessAddress'],
      '본점소재지': ['headquartersAddress', 'address'],
      '본점 소재지': ['headquartersAddress', 'address'],
      '사업의종류': ['businessType', 'businessCategory'],
      '사업의 종류': ['businessType', 'businessCategory'],
      '업태': ['businessCondition', 'businessCategory', 'businessType'],
      '종목': ['businessItem', 'item'],
      '주소': ['address'],
      '발급일자': ['issueDate', 'date'],
      '차량등록번호': ['plateNumber'],
      '차대번호': ['vin'],
      '차명': ['model', 'carName'],
      '연식': ['modelYear', 'year'],
      '원동기형식': ['engineType'],
      '사용본거지': ['address', 'ownerAddress']
    };

    allKeys.forEach(key => {
      if (key === 'metadata' || key === 'timestamp' || key === 'source' || key === 'pageNumber' || key === 'docCode') return;

      // ⭐ 값 조회 시 매핑된 키도 함께 확인
      const getValue = (source, currentKey) => {
        if (!source) return undefined;

        // 1. 직접 키 조회
        if (source[currentKey] !== undefined) return source[currentKey];

        // 2. 매핑된 영문 키 조회 (currentKey가 한글인 경우)
        if (KEY_MAPPING[currentKey]) {
          for (const mappedKey of KEY_MAPPING[currentKey]) {
            if (source[mappedKey] !== undefined) return source[mappedKey];
          }
        }

        // 3. 역방향 조회 (currentKey가 영문이고 source가 한글 키를 가질 경우 - 드문 케이스)
        return undefined;
      };

      const flashValue = getValue(flash, key);
      const proValue = getValue(pro, key);
      const extractedValue = getValue(extractedData, key);

      const normalizedFlash = (flashValue === null || flashValue === undefined) ? '' : String(flashValue).trim();
      const normalizedPro = (proValue === null || proValue === undefined) ? '' : String(proValue).trim();
      const normalizedExtracted = (extractedValue === null || extractedValue === undefined) ? '' : String(extractedValue).trim();

      let currentValue = '';
      if (extractedData && (extractedData[key] !== undefined || getValue(extractedData, key) !== undefined)) {
        currentValue = normalizedExtracted;
      } else if (normalizedPro) {
        currentValue = normalizedPro;
      } else if (normalizedFlash) {
        currentValue = normalizedFlash;
      }

      // ⭐ 차이점 판단 로직 개선
      // 1. extractedData가 있으면 (검증 완료) 차이 없음 처리
      // 2. differences 객체가 있으면 (백엔드 계산) 그것을 우선 사용
      // 3. 없으면 프론트엔드에서 비교
      let isDifferent = false;

      if (extractedData) {
        isDifferent = false;
      } else if (differences && differences[key]) {
        // ⭐ 백엔드 차이점 정보 사용
        // match가 false이거나, flash/pro 값이 다른 경우
        isDifferent = differences[key].match === false || differences[key].different === true;
      } else if (isPartial) {
        isDifferent = false;
      } else {
        // 프론트엔드 비교 (백엔드 정보 없을 때 fallback)
        const normFlash = normalizeString(normalizedFlash);
        const normPro = normalizeString(normalizedPro);
        isDifferent = (normFlash && normPro && normFlash !== normPro);
      }

      rows.push({
        key: key,
        fieldName: key,
        flashValue: normalizedFlash,
        proValue: normalizedPro,
        currentValue: currentValue,
        isDifferent: isDifferent
      });
    });

    return rows;
  };

  // ⭐ extractedData를 포함하여 비교 (검증 후 수정된 데이터 우선 사용)
  // ⭐ useMemo로 메모이제이션하여 불필요한 재계산 방지
  const dataRows = React.useMemo(() => {
    const result = compareData(pageData.flashData, pageData.proData, pageData.partial, pageData.extractedData, pageData.differences);
    console.log('🔍 [dataRows] currentPage:', currentPage, '결과:', {
      dataRowsLength: result.length,
      hasFlashData: !!pageData.flashData,
      hasProData: !!pageData.proData,
      hasExtractedData: !!pageData.extractedData,
      flashDataKeys: pageData.flashData ? Object.keys(pageData.flashData).slice(0, 5) : [],
    });
    return result;
  }, [pageData, currentPage]);

  // ⭐ 디버깅 로그 제거 (무한 루프 방지)
  // useEffect의 dependency에 dataRows.length가 포함되어 있어 무한 루프 발생 가능

  // 필드 순서 정렬 (스키마 기반) - useMemo로 안정화
  // 경락확인서는 매수자 정보와 금액 정보 추가
  // 필드 순서 정렬 (스키마 기반) - useMemo로 안정화
  // 경락확인서는 매수자 정보와 금액 정보 추가
  const getFieldOrder = () => {
    // ⭐ 템플릿이 있는 경우 모든 필드 타입 포함, outputOrder로 정렬
    if (matchedTemplate) {
      const allFields = [];

      // 단일 필드
      (matchedTemplate.singleFields || matchedTemplate.fields || []).forEach((f, idx) => {
        const field = typeof f === 'string' ? { key: f } : f;
        const order = field.outputOrder !== undefined ? field.outputOrder : (idx + 1);
        if (order > 0) allFields.push({ key: field.key, order });
      });

      // 테이블 컬럼
      (matchedTemplate.tableFields || []).forEach((table, tableIdx) => {
        (table.columns || []).forEach((col, colIdx) => {
          const order = col.outputOrder || (100 + tableIdx * 100 + colIdx + 1);
          allFields.push({ key: col.key, order });
        });
      });

      // 합성 필드
      (matchedTemplate.computedFields || []).forEach((cf, idx) => {
        const order = cf.outputOrder ?? cf.order ?? (200 + idx + 1);
        if (order > 0) allFields.push({ key: cf.key, order });
      });

      // outputOrder로 정렬
      allFields.sort((a, b) => a.order - b.order);

      return ['documentType', ...allFields.map(f => f.key)];
    }

    if (documentTypeForLabel === '경락확인서') {
      return [
        'documentType', 'plateNumber', 'vin', 'model',
        'ownerName', 'residentNumber', 'address', // 매도자 정보
        'modelYear', 'mileage', 'displacement', 'totalWeight', 'cbm',
        'buyerName', 'buyerBusinessNumber', // 매수자 정보
        'totalAmount', 'auctionDate' // 금액 정보
      ];
    }

    // 기본 순서 (자동차등록증 등)
    return [
      'documentType', 'plateNumber', 'vin', 'model', 'ownerName', 'residentNumber', 'address',
      'modelYear', 'mileage', 'displacement', 'totalWeight', 'length', 'width', 'height', 'cbm',
      'totalAmount', 'auctionDate', 'deregistrationDate'
    ];
  };

  const fieldOrder = getFieldOrder();

  const sortedDataRows = React.useMemo(() => {
    let rows = [...dataRows];

    // 데이터 소스 (extractedData > proData > flashData)
    const sourceData = pageData.extractedData || pageData.proData || pageData.flashData || {};

    // 🔍 디버그: 현재 페이지 데이터 확인
    console.log('🔍 [sortedDataRows] currentPage:', currentPage, 'sourceData:', sourceData);

    // ⭐ 템플릿이 있으면 누락된 필드 및 합성 필드 추가
    if (matchedTemplate) {
      const existingKeys = new Set(rows.map(r => r.key));

      // 🔧 테이블 필드 처리: 배열 데이터에서 값 추출하여 "|" 로 조인
      const tableFieldsSource = matchedTemplate.tableFields || [];
      console.log('🔍 [sortedDataRows] 테이블 필드 처리 시작:', {
        tableFieldsCount: tableFieldsSource.length,
        tableNames: tableFieldsSource.map(t => t.tableName),
        sourceDataKeys: Object.keys(sourceData),
        currentPage,
        totalPages,
        // 🔍 직접 items 확인
        hasItems: !!sourceData.items,
        itemsLength: sourceData.items?.length || 0,
        firstItemSample: sourceData.items?.[0] ? JSON.stringify(sourceData.items[0]).substring(0, 150) : 'N/A'
      });

      tableFieldsSource.forEach(table => {
        const tableName = table.tableName;
        let tableData = sourceData[tableName] || []; // 테이블 배열 데이터

        // 🔧 FIX: tableName으로 찾지 못하면 'items' 배열을 폴백으로 사용
        if ((!tableData || tableData.length === 0) && sourceData.items && Array.isArray(sourceData.items)) {
          console.log(`📦 [sortedDataRows] '${tableName}' 없음, 'items' 폴백 사용`);
          tableData = sourceData.items;
        }

        console.log('📊 [sortedDataRows] 테이블 데이터:', {
          tableName,
          isArray: Array.isArray(tableData),
          tableDataLength: tableData?.length || 0,
          firstItemKeys: tableData?.[0] ? Object.keys(tableData[0]).join(', ') : 'EMPTY',  // 🔍 키 목록 (문자열)
          firstItemP: tableData?.[0]?.P || tableData?.[0]?.p || tableData?.[0]?.['P.'] || 'N/A',
          totalPages
        });

        // 📄 체크된 페이지들로 필터링 (다중 선택 지원)
        if (Array.isArray(tableData) && tableData.length > 0 && totalPages > 1) {
          // 🔧 checkedPages가 비어있으면 모든 페이지 표시, 있으면 선택된 페이지만 표시
          if (checkedPages && checkedPages.length > 0) {
            const filteredData = tableData.filter(item => {
              // 🔧 pageNumber로 통일
              const itemPage = item.pageNumber ?? 1;
              const pageNum = typeof itemPage === 'string' ? parseInt(itemPage, 10) : Number(itemPage);
              // 🔧 checkedPages 배열에 포함된 페이지만 표시
              return checkedPages.includes(pageNum);
            });
            console.log('📄 [sortedDataRows] 페이지 필터링 (다중선택):', {
              tableName,
              checkedPages,
              totalItems: tableData.length,
              filteredItems: filteredData.length,
              pageNumberValues: tableData.slice(0, 5).map(i => i.pageNumber ?? i.P ?? i.p ?? 'N/A')
            });
            tableData = filteredData;
          }
        }

        (table.columns || []).forEach(col => {
          // 테이블 행들에서 해당 컬럼 값 추출하여 "|" 로 조인
          let joinedValue = '';
          if (Array.isArray(tableData) && tableData.length > 0) {
            const values = tableData.map(item => {
              // 1. 계산된 컬럼 처리
              if (col.type === 'computed' && col.formula) {
                let computed = col.formula;
                const pattern = /\{([^}]+)\}/g;
                let hasReplacement = false;

                // 변수 치환 ({Qty} -> 5)
                computed = computed.replace(pattern, (_, key) => {
                  hasReplacement = true;
                  // 대소문자 무시 키 찾기
                  const valKey = Object.keys(item).find(k => k.toLowerCase() === key.toLowerCase());
                  let val = valKey ? item[valKey] : '';

                  // 숫자인 경우 콤마 제거
                  if (typeof val === 'string' && /^-?[\d,]+(\.\d+)?$/.test(val)) {
                    return val.replace(/,/g, '');
                  }
                  return val || 0; // 수학 계산을 위해 빈 값은 0으로 처리 (상황에 따라 다름)
                });

                // 수학 식 계산 시도 (eval 사용 - 주의 필요)
                // 단순히 문자열 결합을 원하는 경우도 있으므로, 숫자/연산자만 있을 때만 eval 시도
                if (hasReplacement) {
                  try {
                    // 안전을 위해 허용된 문자만 있는지 확인 (숫자, 연산자, 공백, 괄호)
                    if (/^[\d+\-*/.()\s]+$/.test(computed)) {
                      // eslint-disable-next-line no-eval
                      const result = eval(computed);
                      return String(result); // 결과를 문자열로 반환
                    }
                  } catch (e) {
                    console.warn('Formula eval failed, using raw string:', computed, e);
                  }
                }

                // eval 실패하거나 수학식이 아니면 치환된 문자열 그대로 반환 (문자열 결합)
                // 다시 한번 치환 (이번엔 0이 아닌 빈 문자열로, 문자열 결합용)
                let stringComputed = col.formula;
                stringComputed = stringComputed.replace(pattern, (_, key) => {
                  const valKey = Object.keys(item).find(k => k.toLowerCase() === key.toLowerCase());
                  return valKey ? item[valKey] : '';
                });
                return stringComputed;
              }

              // 2. 일반 컬럼 처리
              // 대소문자 무시 키 매칭
              const val = item[col.key] ||
                Object.keys(item).find(k => k.toLowerCase() === col.key.toLowerCase())
                ? item[Object.keys(item).find(k => k.toLowerCase() === col.key.toLowerCase())]
                : '';
              return val || '';
            }).filter(v => v !== '' && v !== null && v !== undefined);
            joinedValue = values.join(' | ');
          }

          // 기존 항목 업데이트 또는 새로 추가
          const existingIdx = rows.findIndex(r => r.key === col.key);
          if (existingIdx >= 0) {
            if (!rows[existingIdx].currentValue && joinedValue) {
              rows[existingIdx].currentValue = joinedValue;
            }
            rows[existingIdx].isTableField = true;
            rows[existingIdx].tableName = tableName;
          } else {
            rows.push({
              key: col.key,
              fieldName: col.displayName || col.key,
              flashValue: '',
              proValue: '',
              currentValue: joinedValue,
              isDifferent: false,
              isTableField: true,
              tableName: tableName
            });
            existingKeys.add(col.key);
          }
        });
      });

      // fieldOrder에 있지만 dataRows에 없는 필드 추가 (빈 값)
      fieldOrder.forEach(key => {
        if (key !== 'documentType' && !existingKeys.has(key)) {
          // sourceData에서 직접 값 가져오기 시도
          let value = sourceData[key] || '';
          if (typeof value === 'object') value = JSON.stringify(value);

          rows.push({
            key: key,
            fieldName: key,
            flashValue: '',
            proValue: '',
            currentValue: value,
            isDifferent: false
          });
          existingKeys.add(key);
        }
      });

      // 🔧 합성 필드 계산 및 추가/업데이트
      (matchedTemplate.computedFields || []).forEach((cf, idx) => {
        // ⭐ outputOrder가 없으면 900+idx 기본값 (항상 표시)
        const order = cf.outputOrder ?? cf.order ?? (900 + idx + 1);
        console.log('📋 [sortedDataRows] 합성필드:', cf.key, 'outputOrder:', cf.outputOrder, '=> order:', order);

        // outputOrder가 0이면 제외, 그 외 모두 포함
        if (order > 0) {
          // 포뮬러에서 값 계산
          let computedValue = cf.formula || '';
          const pattern = /\{([^}]+)\}/g;
          let match;
          while ((match = pattern.exec(cf.formula || '')) !== null) {
            const formulaKey = match[1];

            // 1. rows에서 값 찾기 (대소문자 무시)
            let val = '';
            const sourceRow = rows.find(r => r.key.toLowerCase() === formulaKey.toLowerCase());
            if (sourceRow?.currentValue) {
              val = sourceRow.currentValue;
            } else {
              // 2. sourceData에서 직접 찾기 (대소문자 무시)
              const dataKey = Object.keys(sourceData).find(k => k.toLowerCase() === formulaKey.toLowerCase());
              if (dataKey) {
                val = sourceData[dataKey];
                if (typeof val === 'object') val = ''; // 객체면 빈 값
              }

              // 3. 테이블 데이터에서 첫 번째 행 값 찾기
              if (!val) {
                tableFieldsSource.forEach(table => {
                  let tableData = sourceData[table.tableName] || [];
                  // 🔧 FIX: items 폴백
                  if (tableData.length === 0 && sourceData.items) tableData = sourceData.items;
                  if (Array.isArray(tableData) && tableData.length > 0) {
                    const firstItem = tableData[0];
                    const itemKey = Object.keys(firstItem).find(k => k.toLowerCase() === formulaKey.toLowerCase());
                    if (itemKey && firstItem[itemKey]) {
                      val = firstItem[itemKey];
                    }
                  }
                });
              }
            }

            computedValue = computedValue.replace(new RegExp(`\\{${formulaKey}\\}`, 'gi'), val || '');
          }

          // 기존 항목 찾기
          const existingIdx = rows.findIndex(r => r.key === cf.key);
          if (existingIdx >= 0) {
            rows[existingIdx].currentValue = computedValue;
            rows[existingIdx].isComputed = true;
            rows[existingIdx].displayName = cf.displayName || cf.key;
          } else {
            rows.push({
              key: cf.key,
              fieldName: cf.displayName || cf.key,
              flashValue: '',
              proValue: '',
              currentValue: computedValue,
              isDifferent: false,
              isComputed: true
            });
          }
        }
      });
    }

    // fieldOrder 기준으로 정렬
    const sorted = rows.sort((a, b) => {
      const indexA = fieldOrder.indexOf(a.key);
      const indexB = fieldOrder.indexOf(b.key);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return sorted;
  }, [dataRows, fieldOrder, documentTypeForLabel, matchedTemplate, pageData, currentPage, totalPages, checkedPages]); // 📄 checkedPages 추가 - 다중 페이지 선택용

  // 일치율 계산
  const matchedCount = sortedDataRows.filter(r => !r.isDifferent).length;
  const matchRate = sortedDataRows.length > 0 ? Math.round((matchedCount / sortedDataRows.length) * 100) : 100;

  // 편집 데이터 초기화 (extractedData 우선 사용)
  // ⭐ sortedDataRows를 의존성에서 제거하여 무한 루프 방지
  // 대신 pageData의 개별 필드를 직접 사용
  useEffect(() => {
    const initialData = {};
    sortedDataRows.forEach(r => {
      // ⭐ currentValue는 이미 extractedData > proData > flashData 우선순위로 설정됨
      initialData[r.key] = r.currentValue;
    });
    setEditedData(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageData.flashData, pageData.proData, pageData.extractedData]);

  // 필드 변경 핸들러
  const handleFieldChange = (key, value) => {
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 필드 그룹 정의 (문서 타입에 따라 다르게 구성)
  const getFieldGroups = () => {
    let groups = [];
    const definedFields = new Set();

    if (matchedTemplate) {
      // ⭐ 템플릿 기반 그룹 생성 - outputOrder 정렬된 통합 필드 리스트
      const allFields = [];

      // 단일 필드 (singleFields 또는 fields)
      const singleFieldsSource = matchedTemplate.singleFields || matchedTemplate.fields || [];
      singleFieldsSource.forEach((f, idx) => {
        const field = typeof f === 'string' ? { key: f, displayName: f } : f;
        const order = field.outputOrder !== undefined ? field.outputOrder : (idx + 1);
        if (order > 0) {
          allFields.push({
            key: field.key,
            displayName: field.displayName || field.key,
            order: order,
            fieldType: 'single'
          });
        }
      });

      // 테이블 컬럼들
      const tableFieldsSource = matchedTemplate.tableFields || [];
      let tableColIdx = 0;
      tableFieldsSource.forEach((table, tableIdx) => {
        (table.columns || []).forEach((col, colIdx) => {
          const order = col.outputOrder || (100 + tableIdx * 100 + colIdx + 1);
          allFields.push({
            key: col.key,
            displayName: col.displayName || col.key,
            order: order,
            fieldType: 'table',
            tableName: table.tableName
          });
          tableColIdx++;
        });
      });

      // 합성 필드 (computedFields)
      const computedFieldsSource = matchedTemplate.computedFields || [];
      computedFieldsSource.forEach((cf, idx) => {
        const order = cf.outputOrder ?? cf.order ?? (200 + idx + 1);
        if (order > 0) {
          allFields.push({
            key: cf.key,
            displayName: cf.displayName || cf.key,
            order: order,
            fieldType: 'computed',
            formula: cf.formula
          });
        }
      });

      // outputOrder로 정렬
      allFields.sort((a, b) => a.order - b.order);

      groups = [
        {
          name: '문서 정보',
          fields: allFields.map(f => f.key),
          fieldMetadata: allFields // 메타데이터 저장 (fieldType, formula 등)
        }
      ];
    } else if (documentTypeForLabel === '경락확인서') {
      groups = [
        { name: '차량 정보', fields: ['plateNumber', 'vin', 'model'] },
        { name: '매도자 정보', fields: ['ownerName', 'residentNumber', 'address'] },
        { name: '차량 상세', fields: ['modelYear', 'mileage', 'displacement', 'totalWeight', 'cbm'] },
        { name: '매수자정보(수출화주)', fields: ['buyerName', 'buyerBusinessNumber'] },
        { name: '금액 정보', fields: ['totalAmount', 'auctionDate'] },
      ];
    } else if (documentTypeForLabel === '신분증') {
      // ⭐ 신분증 전용 그룹
      groups = [
        { name: '신분증 정보', fields: ['name', 'residentNumber', 'address', 'issueDate', 'licenseNumber'] }
      ];
    } else {
      // 기본 그룹 (자동차등록증 등)
      groups = [
        { name: '차량 정보', fields: ['plateNumber', 'vin', 'model'] },
        { name: '소유자 정보', fields: ['ownerName', 'residentNumber', 'address'] },
        { name: '차량 상세', fields: ['modelYear', 'mileage', 'displacement', 'totalWeight', 'length', 'width', 'height', 'cbm'] },
      ];
    }

    // 이미 그룹에 포함된 필드들 수집
    groups.forEach(group => {
      group.fields.forEach(field => definedFields.add(field));
    });

    // ⭐ 그룹에 포함되지 않은 나머지 필드 찾기 (기타 정보)
    // 🔧 템플릿 모드에서는 기타 정보 표시하지 않음 (템플릿 필드만 표시)
    if (!matchedTemplate) {
      const remainingFields = sortedDataRows
        .filter(r => !definedFields.has(r.key) && r.key !== 'documentType')
        .map(r => r.key);

      if (remainingFields.length > 0) {
        groups.push({
          name: '기타 정보', // 템플릿에 없는 추가 필드들
          fields: remainingFields
        });
      }
    }

    return groups;
  };

  const fieldGroups = getFieldGroups();

  // 폼 렌더링 - 심플 한 줄 헤더 형식 (그룹핑)
  const renderForm = () => {
    // ⭐ 디버깅 로그 제거 (무한 루프 방지)

    if (sortedDataRows.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          <p>⚠️ 데이터가 없습니다.</p>
          <p className="text-xs mt-2">flashData: {pageData.flashData ? '있음' : '없음'}</p>
          <p className="text-xs">proData: {pageData.proData ? '있음' : '없음'}</p>
          <p className="text-xs">extractedData: {pageData.extractedData ? '있음' : '없음'}</p>
        </div>
      );
    }

    return (
      <div className="p-2 space-y-2">
        {fieldGroups.map((group, groupIdx) => {
          const groupFields = sortedDataRows.filter(r => group.fields.includes(r.key));

          // ⭐ 디버깅: 그룹 필드 확인
          if (group.name === '차량 상세') {
            // 디버깅 로그 제거 (무한 루프 방지)
          }

          // ⭐ 필드가 없으면 빈 값으로 필드 생성 (경락확인서, 자동차등록증 등)
          if (groupFields.length === 0) {
            const docType = pageData.extractedData?.documentType || pageData.proData?.documentType || pageData.flashData?.documentType;

            // 경락확인서: 차량상세 및 매수자정보 그룹은 항상 표시
            if (docType === '경락확인서' && (group.name === '차량 상세' || group.name === '매수자정보(수출화주)')) {
              // 필드를 빈 값으로 생성
              const emptyFields = group.fields.map(fieldKey => ({
                key: fieldKey,
                fieldName: fieldKey,
                flashValue: '',
                proValue: '',
                currentValue: '',
                isDifferent: false
              }));
              return (
                <div key={groupIdx} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-600">{group.name}</h3>
                  </div>
                  <div className="bg-white">
                    {emptyFields.map((r, idx) => {
                      const currentValue = editedData[r.key] ?? r.currentValue;
                      // ⭐ 숫자 필드에 천 단위 콤마 적용
                      const numericFieldsWithComma = ['mileage', 'displacement', 'totalWeight', 'length', 'width', 'height', 'cbm'];
                      const isNumericField = numericFieldsWithComma.includes(r.key);

                      let displayValue = currentValue || '';
                      if (isNumericField && currentValue !== null && currentValue !== undefined && currentValue !== '') {
                        try {
                          const cleanValue = String(currentValue).replace(/,/g, '').trim();
                          const numValue = parseFloat(cleanValue);
                          if (!isNaN(numValue) && isFinite(numValue)) {
                            displayValue = numValue.toLocaleString('ko-KR');
                          }
                        } catch (e) {
                          displayValue = currentValue || '';
                        }
                      }

                      const handleNumericChange = (e) => {
                        const rawValue = e.target.value.replace(/,/g, '');
                        handleFieldChange(r.key, rawValue);
                      };

                      return (
                        <div key={idx} className={idx < emptyFields.length - 1 ? 'border-b border-gray-100' : ''}>
                          <div className="flex items-center gap-2 py-1.5 px-3">
                            <span className="text-xs text-gray-600 w-32 flex-shrink-0">{fieldNameMap[r.key] || r.key}</span>
                            <input
                              type="text"
                              value={displayValue}
                              onChange={isNumericField ? handleNumericChange : (e) => handleFieldChange(r.key, e.target.value)}
                              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="값 없음"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // ⭐ 자동차등록증/등록원부/말소등록증: 차량 상세 그룹은 항상 표시 (연식, 주행거리, 배기량 등)
            if ((docType === '자동차등록증' || docType === '자동차등록원부' || docType === '말소등록증') && group.name === '차량 상세') {
              // 필드를 빈 값으로 생성
              const emptyFields = group.fields.map(fieldKey => ({
                key: fieldKey,
                fieldName: fieldKey,
                flashValue: '',
                proValue: '',
                currentValue: '',
                isDifferent: false
              }));
              return (
                <div key={groupIdx} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-600">{group.name}</h3>
                  </div>
                  <div className="bg-white">
                    {emptyFields.map((r, idx) => {
                      const currentValue = editedData[r.key] ?? r.currentValue;
                      // ⭐ 숫자 필드에 천 단위 콤마 적용
                      const numericFieldsWithComma = ['mileage', 'displacement', 'totalWeight', 'length', 'width', 'height', 'cbm'];
                      const isNumericField = numericFieldsWithComma.includes(r.key);

                      let displayValue = currentValue || '';
                      if (isNumericField && currentValue !== null && currentValue !== undefined && currentValue !== '') {
                        try {
                          const cleanValue = String(currentValue).replace(/,/g, '').trim();
                          const numValue = parseFloat(cleanValue);
                          if (!isNaN(numValue) && isFinite(numValue)) {
                            displayValue = numValue.toLocaleString('ko-KR');
                          }
                        } catch (e) {
                          displayValue = currentValue || '';
                        }
                      }

                      const handleNumericChange = (e) => {
                        const rawValue = e.target.value.replace(/,/g, '');
                        handleFieldChange(r.key, rawValue);
                      };

                      return (
                        <div key={idx} className={idx < emptyFields.length - 1 ? 'border-b border-gray-100' : ''}>
                          <div className="flex items-center gap-2 py-1.5 px-3">
                            <span className="text-xs text-gray-600 w-32 flex-shrink-0">{fieldNameMap[r.key] || r.key}</span>
                            <input
                              type="text"
                              value={displayValue}
                              onChange={isNumericField ? handleNumericChange : (e) => handleFieldChange(r.key, e.target.value)}
                              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="값 없음"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return null;
          }

          return (
            <div key={groupIdx} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* 그룹 헤더 */}
              <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
                <h3 className="text-xs font-semibold text-gray-600">{group.name}</h3>
              </div>

              {/* 그룹 필드들 */}
              <div className="bg-white">
                {groupFields.map((r, idx) => {
                  const currentValue = editedData[r.key] ?? r.currentValue;
                  const hasDifference = r.isDifferent;

                  // ⭐ [Grounding] 해당 필드에 위치 정보가 있는지 확인
                  // 🆕 테이블 필드: items[0].FieldName (셀별) 또는 items[0] (행별) 체크
                  const directGrounding = groundedFields[r.key]?.bbox?.coordinates;
                  const cellGrounding = r.isTableField && groundedFields[`items[0].${r.key}`]?.bbox?.coordinates;
                  const rowGrounding = r.isTableField && groundedFields['items[0]']?.bbox?.coordinates;
                  const hasGrounding = directGrounding || cellGrounding || rowGrounding;


                  return (
                    <div key={idx} className={idx < groupFields.length - 1 ? 'border-b border-gray-100' : ''}>
                      {/* 필드명: 값 (한 줄) */}
                      <div
                        className={`flex items-center gap-2 py-1.5 px-3 transition-all ${hasDifference ? 'bg-gray-50' : ''
                          } ${hoveredField === r.key ? 'bg-blue-100 border-l-4 border-l-blue-500' : ''
                          } ${hasGrounding ? 'cursor-pointer hover:bg-yellow-50 border-l-2 border-l-yellow-400' : 'cursor-default'
                          }`}
                        title={hasGrounding ? '🎯 클릭하면 PDF에서 해당 위치로 이동합니다' : ''}
                        onMouseEnter={() => {
                          setHoveredField(r.key);
                          // ⭐ 부모 컴포넌트에 호버 이벤트 전달
                          if (onFieldHover) {
                            onFieldHover(r.key, currentValue);
                          }
                        }}
                        onMouseLeave={() => {
                          setHoveredField(null);
                          if (onFieldHover) {
                            onFieldHover(null, null);
                          }
                        }}
                        onClick={() => {
                          // ⭐ [Grounding] 필드 클릭 시 해당 PDF 위치로 이동
                          let grounding = groundedFields[r.key];

                          // 🆕 [Table Grounding] 테이블 필드인 경우 grounding 조회
                          if (!grounding?.bbox?.coordinates && r.isTableField) {
                            // ⭐ 첫 번째 열(Item)은 행 전체 하이라이트
                            const isFirstColumn = r.key === 'Item';

                            if (isFirstColumn) {
                              // 행 레벨 grounding 사용 (행 전체 너비)
                              const rowKey = `items[0]`;
                              grounding = groundedFields[rowKey];
                              console.log(`📦 [Table Grounding] '${r.key}' → 행 전체 '${rowKey}':`, grounding?.bbox?.coordinates ? '✅' : '❌');
                            } else {
                              // 셀별 grounding 시도
                              const cellKey = `items[0].${r.key}`;
                              grounding = groundedFields[cellKey];

                              // 셀별 없으면 행 레벨 폴백
                              if (!grounding?.bbox?.coordinates) {
                                const rowKey = `items[0]`;
                                grounding = groundedFields[rowKey];
                                console.log(`📦 [Table Grounding] '${r.key}' → 행 폴백 '${rowKey}':`, grounding?.bbox?.coordinates ? '✅' : '❌');
                              } else {
                                console.log(`📦 [Table Grounding] '${r.key}' → 셀 '${cellKey}':`, grounding?.bbox?.coordinates ? '✅' : '❌');
                              }
                            }
                          }



                          const hasGroundingCoords = grounding?.bbox?.coordinates;

                          // 🔍 [DEBUG] 클릭 이벤트 디버깅
                          console.log('🖱️ [Grounding Click]', {
                            fieldKey: r.key,
                            isTableField: r.isTableField,
                            currentValue,
                            groundingData: grounding,
                            hasGrounding: !!hasGroundingCoords,
                            willNavigate: !!hasGroundingCoords && !!grounding?.bbox?.page !== undefined
                          });

                          if (onFieldClick) {
                            onFieldClick({
                              fieldKey: r.key,
                              fieldValue: currentValue,
                              // Grounding 정보 (LangExtract에서 반환)
                              page: hasGroundingCoords ? (grounding.bbox.page + 1) : null, // 0-indexed → 1-indexed
                              coordinates: hasGroundingCoords ? grounding.bbox.coordinates : null,
                              confidence: grounding?.confidence || null,
                              hasGrounding: !!hasGroundingCoords
                            });
                          }
                        }}

                      >
                        <label className="text-xs font-medium text-gray-600 whitespace-pre-wrap min-w-[120px]">
                          {fieldNameMap[r.fieldName] || r.fieldName}:
                        </label>
                        {r.key === 'documentType' ? (
                          <select
                            value={currentValue}
                            onChange={(e) => handleFieldChange(r.key, e.target.value)}
                            disabled={validatedPages[currentPage]}
                            className={`flex-1 px-2 py-0.5 border rounded text-xs focus:outline-none transition-colors ${validatedPages[currentPage]
                              ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                              : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50 focus:ring-1 focus:ring-gray-500 focus:border-gray-500'
                              }`}
                          >
                            <option value="">문서 타입 선택</option>
                            <option value="경락확인서">경락확인서</option>
                            <option value="자동차매매계약서">자동차매매계약서</option>
                            <option value="자동차등록원부">자동차등록원부</option>
                            <option value="자동차등록증">자동차등록증</option>
                            <option value="말소등록증">말소등록증</option>
                            <option value="사업자등록증">사업자등록증</option>
                            <option value="신분증">신분증</option>
                            <option value="폐차증">폐차증</option>
                            <option value="세금계산서">세금계산서</option>
                            <option value="기타">기타</option>
                          </select>
                        ) : (() => {
                          // ⭐ 숫자 필드에 천 단위 콤마 적용
                          const numericFieldsWithComma = ['mileage', 'displacement', 'totalWeight', 'length', 'width', 'height', 'cbm'];
                          const isNumericField = numericFieldsWithComma.includes(r.key);

                          let displayValue = currentValue;
                          if (isNumericField && currentValue !== null && currentValue !== undefined && currentValue !== '') {
                            try {
                              const cleanValue = String(currentValue).replace(/,/g, '').trim();
                              const numValue = parseFloat(cleanValue);
                              if (!isNaN(numValue) && isFinite(numValue)) {
                                displayValue = numValue.toLocaleString('ko-KR');
                              }
                            } catch (e) {
                              // 포맷팅 실패 시 원본 값 사용
                              displayValue = currentValue;
                            }
                          }

                          const handleNumericChange = (e) => {
                            const rawValue = e.target.value.replace(/,/g, '');
                            handleFieldChange(r.key, rawValue);
                          };

                          return (
                            <input
                              type="text"
                              value={displayValue}
                              onChange={isNumericField ? handleNumericChange : (e) => handleFieldChange(r.key, e.target.value)}
                              disabled={validatedPages[currentPage]}
                              className={`flex-1 px-2 py-0.5 border rounded text-xs focus:outline-none transition-colors ${validatedPages[currentPage]
                                ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                                : hasDifference
                                  ? 'border-[#DADCE0] bg-[#FEF7E0] text-[#202124] focus:ring-1 focus:ring-[#EA8600] focus:border-[#EA8600] font-medium' // 차이점 있을 때 부드러운 amber 스타일
                                  : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50 focus:ring-1 focus:ring-gray-500 focus:border-gray-500'
                                }`}
                              placeholder={validatedPages[currentPage] ? "검증 완료" : "값을 입력하세요"}
                            />
                          );
                        })()}
                        {hasDifference && (
                          <span className="text-xs text-[#EA8600] font-medium whitespace-nowrap ml-2">⚠ 차이</span>
                        )}
                      </div>
                      {/* 차이 비교 정보 - 부드러운 톤 */}
                      {hasDifference && (
                        <div className="px-3 pb-1.5 pt-0.5 flex gap-4 text-xs text-[#5F6368] bg-[#FEF7E0] rounded-b-lg mx-1 mb-1">
                          <div className="flex-1">
                            <span className="font-medium text-[#B06000]">
                              {getSourceNickname(pageData?.flashData?.source) || '추출1:'}:
                            </span>{' '}
                            <span className="text-[#5F6368]">{r.flashValue || '(없음)'}</span>
                          </div>
                          <div className="flex-1">
                            <span className="font-medium text-[#B06000]">
                              {getSourceNickname(pageData?.proData?.source) || '추출2:'}:
                            </span>{' '}
                            <span className="text-[#5F6368]">{r.proValue || '(없음)'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 가로 그리드 AUIGrid 초기화
  useEffect(() => {
    console.log('🔧 [HorizontalGrid useEffect] 조건 체크:', {
      hasAUIGrid: !!window.AUIGrid,
      viewMode,
      pageResultsLength: pageResults?.length  // 📄 checkedPageResults 대신 pageResults 사용
    });

    // 📄 pageResults 기준으로 체크 (체크 상태와 관계없이 그리드 유지)
    if (!window.AUIGrid || viewMode !== 'horizontal' || !pageResults || pageResults.length === 0) {
      console.log('🔧 [HorizontalGrid useEffect] 조건 불충족으로 return');
      return;
    }

    console.log('✅ [HorizontalGrid useEffect] 초기화 시작');
    const gridId = 'horizontal_grid';

    // 기존 그리드 제거
    if (gridRef.current) {
      try {
        window.AUIGrid.destroy(gridRef.current);
      } catch (e) {
        console.warn('그리드 제거 실패:', e);
      }
      gridRef.current = null;
    }

    // DOM이 준비될 때까지 대기
    const timer = setTimeout(() => {
      const gridElement = document.getElementById(gridId);
      if (!gridElement) {
        console.error(`그리드 DOM 요소를 찾을 수 없습니다: ${gridId}`);
        return;
      }

      // 🔧 템플릿이 있으면 템플릿 필드 사용, 없으면 추출된 데이터 키 사용
      // ⭐ 필드 타입 정보 포함: { key, fieldType: 'single' | 'table' }
      let allFieldsWithType = [];

      if (matchedTemplate) {
        // 단일 필드 추가
        const singleFieldsSource = matchedTemplate.singleFields || matchedTemplate.fields || [];
        singleFieldsSource.forEach(f => {
          const key = typeof f === 'string' ? f : f.key;
          allFieldsWithType.push({ key, fieldType: 'single' });
        });

        // 테이블 컬럼 추가
        const tableFieldsSource = matchedTemplate.tableFields || [];
        tableFieldsSource.forEach(table => {
          (table.columns || []).forEach(col => {
            allFieldsWithType.push({ key: col.key, fieldType: 'table', tableName: table.tableName });
          });
        });

        // 🔧 합성 필드 추가 (formula 포함)
        const computedFieldsSource = matchedTemplate.computedFields || [];
        computedFieldsSource.forEach(cf => {
          allFieldsWithType.push({
            key: cf.key,
            fieldType: 'computed',
            formula: cf.formula,
            displayName: cf.displayName
          });
        });

        console.log('📋 Using template fields for grid:', allFieldsWithType.map(f => `${f.key}(${f.fieldType})`));
      } else {
        // 🔧 Fallback: 추출된 데이터에서 필드 수집 (모두 single로 처리)
        const allFieldsSet = new Set();
        checkedPageResults.forEach(page => {
          const data = page.flashData || page.proData || {};
          Object.keys(data).forEach(key => {
            if (key !== 'metadata' && key !== 'timestamp' && key !== 'source' && key !== 'pageNumber' && key !== 'documentType') {
              allFieldsSet.add(key);
            }
          });
        });
        allFieldsWithType = Array.from(allFieldsSet).map(key => ({ key, fieldType: 'single' }));
      }

      // 🔧 컬럼 필터 적용
      let filteredFields = allFieldsWithType;
      if (columnFilter === 'single') {
        filteredFields = allFieldsWithType.filter(f => f.fieldType === 'single');
      } else if (columnFilter === 'table') {
        filteredFields = allFieldsWithType.filter(f => f.fieldType === 'table');
      }

      // 기존 allFields 변수 유지 (하위 호환성)
      const allFields = filteredFields.map(f => f.key);

      // 컬럼 정의 - 🔧 페이지 헤더 축소, 문서타입 열 삭제
      const horizontalColumns = [
        {
          dataField: "pageNumber",
          headerText: "P", // 🔧 "페이지" → "P" (가로폭 축소)
          width: 35,
          editable: false,
          style: "center-align",
          headerStyle: "center-align",
          footer: {
            text: "Σ",
            style: "text-align: center; font-weight: bold; color: #555;"
          }
        },
        {
          dataField: "idx",
          headerText: "#", // 🔧 페이지 내 순서
          width: 30,
          editable: false,
          style: "center-align",
          headerStyle: "center-align"
        },
        // 🔧 문서타입 열 삭제 (사용자 요청)
        // ⭐ getOptimalColumnWidth 사용하여 필드별 최적 너비 적용
        ...allFields.map(field => {
          // ⭐ 숫자형 필드 감지 (합계 표시용) - 영문 + 한글 패턴
          const isNumeric = /amount|price|qty|quantity|weight|cbm|count|total|vat|tax|cost|fee|charge|수량|단가|금액|중량|순중량|총중량|부피|합계|세금|비용|갯수|개수|Unit.*Price|Net.*Weight|Gross.*Weight/i.test(field);

          // 📐 계층적 너비 시스템 적용
          const optimalWidth = getOptimalColumnWidth(field, 'shipping');

          // 🔧 width가 0인 경우 기본값 200px 적용 (flex 대신 고정 너비)
          // Description 등 긴 필드도 고정 너비로 표시하고 스크롤바로 처리
          const finalWidth = optimalWidth === 0 ? 200 : optimalWidth;

          return {
            dataField: field,
            headerText: fieldNameMap[field] || field,
            // 🔧 모든 컬럼에 고정 너비 적용 (widthRate 제거)
            width: finalWidth,
            editable: true,
            editRenderer: {
              type: "InputEditRenderer"
            },
            // ⭐ 스타일 함수: OCR 불일치 + 페이지 그룹 하이라이트
            // 🔧 AUIGrid에서는 styleFunction 사용
            styleFunction: (rowIndex, columnIndex, value, headerText, item, dataField) => {
              let styleClass = isNumeric ? "right-align" : "left-align";

              // 불일치 감지 (differences 객체 확인)
              if (item?.differences && item.differences[dataField] && item.differences[dataField].match === false) {
                styleClass += " ocr-mismatch";
              }

              // 🎨 [Page Group] 같은 페이지 행 하이라이트
              const rowPage = parseInt(item?.pageNumber || item?.P || 0, 10);
              if (activePageRef.current && rowPage === activePageRef.current) {
                styleClass += " page-group-highlight";
              }
              // 🎨 선택된 행 강조
              if (selectedRowRef.current !== null && rowIndex === selectedRowRef.current) {
                styleClass += " page-group-selected-row";
              }

              return styleClass;
            },
            dataType: isNumeric ? "numeric" : "string",
            formatString: isNumeric ? "#,##0.##" : null,  // 🔧 소수점 2자리까지 표시
            // ⭐ 푸터 합계 설정
            footer: isNumeric ? {
              operation: "SUM",
              style: "text-align: right; font-weight: bold; color: #333;",
              formatString: "#,##0.##"  // 🔧 소수점 2자리까지 표시
            } : null
          };
        })
      ];


      // ⭐ 데이터 준비 - 🔧 테이블 데이터를 행 단위로 펼침
      let horizontalData = [];

      // 템플릿에 tableFields가 있는지 확인
      const tableFieldsSource = matchedTemplate?.tableFields || [];
      const hasTableFields = tableFieldsSource.length > 0;

      // 🔧 pageResults 사용 (checkedPageResults 대신) - checkedPageResults는 page 2,3에서 비어있을 수 있음
      pageResults.forEach((page, pageIdx) => {
        const flashData = page.flashData || {};
        const proData = page.proData || {};
        let extractedData = page.extractedData || proData || flashData;

        // 🔧 FIX: extractedData가 비어있으면 page 자체를 사용 (단일 필드 템플릿 호환)
        // 백엔드가 pageResults[].CBM, pageResults[].길 이 형태로 직접 필드를 반환하는 경우
        if (!extractedData || Object.keys(extractedData).length === 0) {
          // page에서 메타데이터 키를 제외한 실제 데이터가 있는지 확인
          const metaKeys = ['pageNumber', 'P', 'p', 'page', 'flashData', 'proData', 'extractedData', 'documentType', 'differences'];
          const dataKeys = Object.keys(page).filter(k => !metaKeys.includes(k));
          if (dataKeys.length > 0) {
            console.log('📄 [horizontalGrid] page 객체 자체를 extractedData로 사용:', page.pageNumber);
            extractedData = page;
          }
        }

        // 🔧 테이블 데이터가 있으면 행 단위로 펼침
        if (hasTableFields) {
          // 첫 번째 테이블 기준으로 행 생성
          const firstTable = tableFieldsSource[0];
          const tableName = firstTable.tableName;
          let tableData = extractedData[tableName] || [];

          // 🔧 FIX: tableName으로 찾지 못하면 'items' 배열을 폴백으로 사용
          if (tableData.length === 0 && extractedData.items) {
            console.log(`📦 [horizontalGrid] '${tableName}' 없음, 'items' 폴백 사용`);
            tableData = extractedData.items;
          }

          // 📄 멀티페이지 PDF인 경우, 체크된 페이지들로 필터링 (다중 선택 지원)
          if (Array.isArray(tableData) && tableData.length > 0 && totalPages > 1) {
            // 🔧 checkedPages가 비어있으면 모든 페이지 표시, 있으면 선택된 페이지만 표시
            if (checkedPages && checkedPages.length > 0) {
              tableData = tableData.filter(item => {
                // 🔧 pageNumber로 통일
                const itemPage = item.pageNumber ?? 1;
                const pageNum = typeof itemPage === 'string' ? parseInt(itemPage, 10) : Number(itemPage);
                // 🔧 checkedPages 배열에 포함된 페이지만 표시
                return checkedPages.includes(pageNum);
              });
              console.log('📄 [horizontalGrid] 페이지 필터링 (다중선택):', { tableName, checkedPages, totalItems: (extractedData[tableName] || []).length, filteredItems: tableData.length });
            }
          }

          if (Array.isArray(tableData) && tableData.length > 0) {
            // 테이블 행마다 그리드 행 생성
            tableData.forEach((item, itemIdx) => {
              // 🔧 pageNumber로 통일
              const resolvedPageNumber = item.pageNumber ?? page.pageNumber ?? 1;

              const rowData = {
                // 🔧 pageNumber: 페이지 번호 (그리드 P 열에 표시)
                pageNumber: typeof resolvedPageNumber === 'string' ? parseInt(resolvedPageNumber, 10) : Number(resolvedPageNumber),
                // 🔧 idx: 페이지 내 순서 (그리드 # 열에 표시)
                idx: item.idx ?? (itemIdx + 1),
                documentType: page.documentType || '',
                differences: page.differences || {},
                _rowIndex: itemIdx + 1 // 행 번호 표시용
              };

              // 🔧 필드별 값 설정 (단일/테이블/합성 필드 구분)
              filteredFields.forEach(fieldInfo => {
                const field = fieldInfo.key;

                if (fieldInfo.fieldType === 'computed' && fieldInfo.formula) {
                  // 🔧 합성 필드: 현재 행(item)의 데이터로 formula 계산
                  let computedValue = fieldInfo.formula;
                  const pattern = /\{([^}]+)\}/g;

                  computedValue = computedValue.replace(pattern, (_, formulaKey) => {
                    // 1. 현재 item에서 값 찾기 (테이블 컬럼 우선)
                    let val = '';
                    const itemKey = Object.keys(item).find(k => k.toLowerCase() === formulaKey.toLowerCase());
                    if (itemKey) {
                      val = item[itemKey];
                    } else {
                      // 2. 단일 필드(extractedData)에서 찾기
                      const dataKey = Object.keys(extractedData).find(k => k.toLowerCase() === formulaKey.toLowerCase());
                      if (dataKey) val = extractedData[dataKey];
                    }

                    // 숫자 콤마 제거
                    if (typeof val === 'string' && /^-?[\d,]+(\.\d+)?$/.test(val)) {
                      return val.replace(/,/g, '');
                    }
                    return val || 0;
                  });

                  // 수식 계산 시도
                  try {
                    if (/^[\d\s+\-*/().]+$/.test(computedValue)) {
                      computedValue = eval(computedValue);
                    }
                  } catch (e) {
                    // 계산 실패 시 원본 유지
                  }

                  rowData[field] = computedValue;

                } else if (fieldInfo.fieldType === 'table') {
                  // 테이블 컬럼: 해당 아이템의 값 사용
                  const val = item[field] ||
                    Object.keys(item).find(k => k.toLowerCase() === field.toLowerCase())
                    ? item[Object.keys(item).find(k => k.toLowerCase() === field.toLowerCase())]
                    : '';
                  rowData[field] = val || '';

                } else {
                  // 단일 필드: 추출 데이터에서 가져오기
                  rowData[field] = extractedData[field] || proData[field] || flashData[field] || '';
                }
              });

              horizontalData.push(rowData);
            });
          } else {
            // 테이블 데이터 없으면 단일 행
            const rowData = {
              pageNumber: page.pageNumber,
              documentType: page.documentType || '',
              differences: page.differences || {},
              _rowIndex: 1
            };

            // 🔧 필드별 값 설정 (합성 필드 포함)
            filteredFields.forEach(fieldInfo => {
              const field = fieldInfo.key;

              if (fieldInfo.fieldType === 'computed' && fieldInfo.formula) {
                // 합성 필드: extractedData로 formula 계산 (테이블 데이터 없음)
                let computedValue = fieldInfo.formula;
                const pattern = /\{([^}]+)\}/g;

                computedValue = computedValue.replace(pattern, (_, formulaKey) => {
                  const dataKey = Object.keys(extractedData).find(k => k.toLowerCase() === formulaKey.toLowerCase());
                  let val = dataKey ? extractedData[dataKey] : '';
                  if (typeof val === 'string' && /^-?[\d,]+(\.\d+)?$/.test(val)) {
                    return val.replace(/,/g, '');
                  }
                  return val || 0;
                });

                try {
                  if (/^[\d\s+\-*/().]+$/.test(computedValue)) {
                    computedValue = eval(computedValue);
                  }
                } catch (e) { }

                rowData[field] = computedValue;
              } else {
                rowData[field] = extractedData[field] || proData[field] || flashData[field] || '';
              }
            });

            horizontalData.push(rowData);
          }
        } else {
          // 테이블 필드 없음 - 기존 로직 (페이지당 1행)
          const rowData = {
            pageNumber: page.pageNumber,
            documentType: page.documentType || '',
            differences: page.differences || {}
          };

          // 🔧 필드별 값 설정 (합성 필드 포함)
          filteredFields.forEach(fieldInfo => {
            const field = fieldInfo.key;

            if (fieldInfo.fieldType === 'computed' && fieldInfo.formula) {
              // 합성 필드: extractedData로 formula 계산
              let computedValue = fieldInfo.formula;
              const pattern = /\{([^}]+)\}/g;

              computedValue = computedValue.replace(pattern, (_, formulaKey) => {
                const dataKey = Object.keys(extractedData).find(k => k.toLowerCase() === formulaKey.toLowerCase());
                let val = dataKey ? extractedData[dataKey] : '';
                if (typeof val === 'string' && /^-?[\d,]+(\.\d+)?$/.test(val)) {
                  return val.replace(/,/g, '');
                }
                return val || 0;
              });

              try {
                if (/^[\d\s+\-*/().]+$/.test(computedValue)) {
                  computedValue = eval(computedValue);
                }
              } catch (e) { }

              rowData[field] = computedValue;
            } else {
              rowData[field] = extractedData[field] || proData[field] || flashData[field] || '';
            }
          });

          horizontalData.push(rowData);
        }
      });

      // 🔧 빈 값은 빈 문자열("")로 유지 (진짜 빈 셀로 표시)

      // 🔧 컴팩트 그리드 스타일 적용
      const horizontalGridProps = {
        headerHeight: 26,
        rowHeight: 22,
        // 🔧 fillColumnSizeMode 제거 - 모든 컬럼이 자신의 너비대로 표시되도록
        // fillColumnSizeMode: true, // 이 옵션이 Description 컬럼을 전체 너비로 확장시켜 다른 컬럼 숨김
        hScrollPolicy: "auto", // 🔧 가로 스크롤바: 컬럼이 넘치면 자동 표시
        enableFilter: false,
        enableSorting: false, // 🔧 헤더 클릭 시 열 전체 선택으로 변경 (정렬 비활성화)
        showRowNumColumn: true, // 행 번호 표시
        editable: true,
        // 🆕 열 드래그 이동 활성화
        enableMovingColumn: true,
        // ⭐ 셀 다중 선택 모드: Ctrl+클릭 = 개별, 드래그 = 범위 선택
        selectionMode: "multipleCells",
        selectionStyle: {
          backgroundColor: "#DBEAFE", // 연한 파란색 (선택된 셀)
          color: "#1A1B1E" // 로고 색상 텍스트
        },
        // ⭐ 푸터 활성화
        showFooter: true,
        footerPosition: "bottom",
        footerHeight: 26,  // 선택 합계 표시용 높이 증가
        // 🆕 컨텍스트 메뉴 활성화 (기본 메뉴 = 셀 관련)
        useContextMenu: true,
        contextMenuItems: [
          {
            label: "행 추가",
            callback: function (event) {
              const position = window.prompt('행 추가 위치:\n1: 위에 추가\n2: 아래에 추가', '2');
              if (position === '1') {
                window.AUIGrid.addRow(grid, {}, "selectionUp");
                console.log('✅ [ContextMenu] 행 위에 추가');
              } else if (position === '2') {
                window.AUIGrid.addRow(grid, {}, "selectionDown");
                console.log('✅ [ContextMenu] 행 아래에 추가');
              }
            }
          },
          {
            label: "행 삭제",
            callback: function (event) {
              const selectedItems = window.AUIGrid.getSelectedItems(grid);

              // 선택된 행들의 인덱스 추출 (중복 제거 및 내림차순 정렬)
              const selectedRowIndices = selectedItems && selectedItems.length > 0
                ? Array.from(new Set(selectedItems.map(item => item.rowIndex))).sort((a, b) => b - a)
                : [];

              // 1. 다중 선택된 경우 (우클릭한 행이 선택 범위에 포함되어 있거나, 다른 행들이 선택된 경우)
              if (selectedRowIndices.length > 1) {
                if (window.confirm(`선택된 ${selectedRowIndices.length}개 행을 삭제하시겠습니까?`)) {
                  selectedRowIndices.forEach(idx => {
                    window.AUIGrid.removeRow(grid, idx);
                  });
                  console.log('✅ [ContextMenu] 다중 행 삭제:', selectedRowIndices.length, '개');
                }
              }
              // 2. 단일 선택 또는 선택 없음 (우클릭한 행만 삭제)
              else {
                const { rowIndex } = event;
                if (rowIndex < 0) {
                  alert('삭제할 행을 선택하세요.');
                  return;
                }
                if (window.confirm(`${rowIndex + 1}번 행을 삭제하시겠습니까?`)) {
                  window.AUIGrid.removeRow(grid, rowIndex);
                  console.log('✅ [ContextMenu] 단일 행 삭제:', rowIndex);
                }
              }
            }
          },
          {
            label: "열 삭제",
            callback: function (event) {
              const { dataField, columnIndex } = event;
              if (!dataField || columnIndex < 0) {
                alert('삭제할 열을 선택하세요.');
                return;
              }
              if (window.confirm(`'${dataField}' 열을 삭제하시겠습니까?`)) {
                window.AUIGrid.removeColumn(grid, columnIndex);
                console.log('✅ [ContextMenu] 열 삭제:', dataField);
              }
            }
          },
          { label: "-" },
          {
            label: "현재 셀을 위 셀에 병합",
            callback: function (event) {
              const { rowIndex, dataField } = event;
              if (rowIndex <= 0) {
                alert('위로 병합할 수 없습니다. (첫 번째 행)');
                return;
              }
              const gridData = window.AUIGrid.getGridData(grid);
              const currentValue = gridData[rowIndex]?.[dataField] || '';
              const aboveValue = gridData[rowIndex - 1]?.[dataField] || '';
              const mergedValue = aboveValue ? `${aboveValue}, ${currentValue}` : currentValue;

              // Undo 저장
              setUndoStack(prev => [...prev, {
                id: Date.now(),
                type: 'merge_up',
                beforeValues: [
                  { rowIndex: rowIndex - 1, field: dataField, value: aboveValue },
                  { rowIndex: rowIndex, field: dataField, value: currentValue }
                ]
              }]);

              window.AUIGrid.updateRow(grid, { [dataField]: mergedValue }, rowIndex - 1);
              window.AUIGrid.updateRow(grid, { [dataField]: '' }, rowIndex);
              console.log('✅ [ContextMenu] 위로 병합:', dataField);
            }
          },
          {
            label: "현재 셀을 왼쪽 셀에 병합",
            callback: function (event) {
              const { rowIndex, dataField, columnIndex } = event;
              const columnLayout = window.AUIGrid.getColumnLayout(grid);
              if (columnIndex <= 0) {
                alert('왼쪽으로 병합할 수 없습니다. (첫 번째 열)');
                return;
              }
              const leftField = columnLayout[columnIndex - 1]?.dataField;
              if (!leftField) return;

              const gridData = window.AUIGrid.getGridData(grid);
              const currentValue = gridData[rowIndex]?.[dataField] || '';
              const leftValue = gridData[rowIndex]?.[leftField] || '';
              const mergedValue = leftValue ? `${leftValue}, ${currentValue}` : currentValue;

              // Undo 저장
              setUndoStack(prev => [...prev, {
                id: Date.now(),
                type: 'merge_left',
                beforeValues: [
                  { rowIndex, field: leftField, value: leftValue },
                  { rowIndex, field: dataField, value: currentValue }
                ]
              }]);

              window.AUIGrid.updateRow(grid, { [leftField]: mergedValue, [dataField]: '' }, rowIndex);
              console.log('✅ [ContextMenu] 왼쪽으로 병합:', leftField, '<-', dataField);
            }
          },
          { label: "-" },
          {
            label: "🔗 열 전체를 왼쪽 열에 병합",
            callback: function (event) {
              const { dataField, columnIndex } = event;
              const columnLayout = window.AUIGrid.getColumnLayout(grid);
              if (columnIndex <= 0) {
                alert('왼쪽으로 병합할 수 없습니다. (첫 번째 열)');
                return;
              }
              const leftField = columnLayout[columnIndex - 1]?.dataField;
              if (!leftField) return;

              const gridData = window.AUIGrid.getGridData(grid);

              if (!window.confirm(`'${dataField}' 열 전체를 '${leftField}' 열에 병합하시겠습니까?`)) {
                return;
              }

              // Undo 저장 (열 전체 병합)
              const beforeValues = [];
              gridData.forEach((row, idx) => {
                const currentValue = row[dataField] || '';
                const leftValue = row[leftField] || '';
                if (currentValue) {
                  beforeValues.push({
                    rowIndex: idx,
                    targetValue: leftValue,
                    sourceValue: currentValue
                  });
                }
              });

              // 열 전체 병합 실행
              beforeValues.forEach(({ rowIndex, targetValue, sourceValue }) => {
                const mergedValue = targetValue ? `${targetValue}, ${sourceValue}` : sourceValue;
                window.AUIGrid.updateRow(grid, { [leftField]: mergedValue, [dataField]: '' }, rowIndex);
              });

              if (beforeValues.length > 0) {
                setUndoStack(prev => [...prev, {
                  id: Date.now(),
                  type: 'merge_column_left',
                  sourceField: dataField,
                  targetField: leftField,
                  beforeValues
                }]);
              }
              console.log('✅ [ContextMenu] 열 전체 왼쪽 병합:', leftField, '<-', dataField, '(', beforeValues.length, '행)');
            }
          }
        ]
      };

      const grid = window.AUIGrid.create(`#${gridId}`, horizontalColumns, horizontalGridProps);
      gridRef.current = grid;

      // 데이터 설정
      window.AUIGrid.setGridData(grid, horizontalData);

      // ⭐ 푸터 합계 설정 - setFooter API 사용
      const footerLayout = [];
      allFields.forEach(field => {
        // 숫자형 필드 감지
        const isNumericField = /amount|price|qty|quantity|weight|cbm|count|total|vat|tax|cost|fee|charge|수량|단가|금액|중량|순중량|총중량|부피|합계|세금|비용|갯수|개수|Unit.*Price|Net.*Weight|Gross.*Weight/i.test(field);
        if (isNumericField) {
          footerLayout.push({
            dataField: field,
            positionField: field,
            operation: "SUM",
            formatString: "#,##0.##",
            style: "text-align: right; font-weight: bold; color: #333;"
          });
        }
      });
      if (footerLayout.length > 0) {
        window.AUIGrid.setFooter(grid, footerLayout);
        console.log('📊 [Footer] 합계 설정:', footerLayout.map(f => f.dataField));
      }

      // 🔧 컬럼 너비 자동 맞춤 - 컨테이너에 맞게 확장
      setTimeout(() => {
        try {
          window.AUIGrid.resize(grid);
          // fitColumnWidthToContainer가 있으면 사용
          if (window.AUIGrid.fitColumnWidthToContainer) {
            window.AUIGrid.fitColumnWidthToContainer(grid);
            console.log('📐 [Grid] fitColumnWidthToContainer 적용');
          }
        } catch (e) {
          console.warn('📐 [Grid] auto-fit 실패:', e);
        }
      }, 100);

      // 셀 편집 완료 이벤트 + Undo 저장
      window.AUIGrid.bind(grid, "cellEditEnd", function (event) {
        if (event.oldValue !== event.value) {
          setUndoStack(prev => [...prev, {
            id: Date.now(),
            type: 'cell_update',
            beforeValues: [{
              rowIndex: event.rowIndex,
              field: event.dataField,
              value: event.oldValue
            }]
          }]);
          console.log('📝 [HorizontalGrid] 셀 편집:', event.dataField, event.oldValue, '->', event.value);
        }
      });

      // Ctrl+Z는 별도 useEffect에서 처리 (stale closure 방지)

      // ╔══════════════════════════════════════════════════════════════════════════╗
      // ║  🔒 필수 기능: Google Sheets 스타일 열 선택 (Column Selection)           ║
      // ║                                                                          ║
      // ║  ⚠️ 이 코드를 수정할 때 반드시 아래 사항을 확인하세요:                   ║
      // ║                                                                          ║
      // ║  1. setSelectionBlock 사용 금지: AUIGrid 네이티브 선택은 녹색/파란       ║
      // ║     테두리를 생성하므로 사용하지 않음. 순수 CSS로만 하이라이트.          ║
      // ║                                                                          ║
      // ║  2. CSS 클래스(.column-selected-cell) 직접 추가 금지:                    ║
      // ║     AUIGrid는 hover 시 셀 DOM을 재생성하여 클래스가 제거됨.             ║
      // ║     반드시 <style> 태그 + nth-child 구조적 셀렉터 사용.                 ║
      // ║                                                                          ║
      // ║  3. nth-child 오프셋 하드코딩 금지:                                      ║
      // ║     getCellElementByIndex(grid, 0, colIdx)로 실제 td 위치를             ║
      // ║     자동 감지한 후 nth-child 값을 결정.                                  ║
      // ║                                                                          ║
      // ║  관련 파일:                                                               ║
      // ║  - PageBasedGridPanel.jsx: L228-229 (useRef 선언)                        ║
      // ║  - PageBasedGridPanel.jsx: 이 블록 (이벤트 핸들러)                       ║
      // ║  - index.css: .selected-column-header (헤더 하이라이트)                  ║
      // ║  - index.css: .aui-grid-selection-* 오버라이드 (녹색→파란색)             ║
      // ╚══════════════════════════════════════════════════════════════════════════╝
      // Ctrl+Click: 개별 열 추가/토글, Shift+Click: 범위 선택
      let isColumnSelecting = false; // 가드 플래그
      const COLUMN_STYLE_ID = 'column-selection-style';

      // 열 하이라이트 제거 (헤더 CSS + <style> 태그)
      const clearColumnHighlight = () => {
        document.querySelectorAll('.aui-grid-header-renderer.selected-column-header').forEach(el => {
          el.classList.remove('selected-column-header');
        });
        const existingStyle = document.getElementById(COLUMN_STYLE_ID);
        if (existingStyle) existingStyle.remove();
      };

      // 열 전체 하이라이트 적용 (헤더 CSS + <style> 태그 주입)
      // getCellElementByIndex로 실제 td 위치를 자동 감지 → nth-child 오프셋 정확
      const applyColumnHighlight = (columns) => {
        clearColumnHighlight();
        const gridEl = document.getElementById('horizontal_grid');
        if (!gridEl) return;

        // 1. 헤더 하이라이트
        const headerCells = gridEl.querySelectorAll('.aui-grid-header-renderer');
        columns.forEach(colIdx => {
          if (headerCells[colIdx]) {
            headerCells[colIdx].classList.add('selected-column-header');
          }
        });

        // 2. 바디 셀 하이라이트: getCellElementByIndex로 nth-child 위치 자동 감지
        //    → <style> 태그 주입 (hover 시 셀 재생성에도 유지됨)
        const rowCount = window.AUIGrid.getRowCount(grid);
        if (columns.length > 0 && rowCount > 0) {
          const nthChildPositions = [];
          columns.forEach(colIdx => {
            try {
              const cellEl = window.AUIGrid.getCellElementByIndex(grid, 0, colIdx);
              if (cellEl && cellEl.parentElement) {
                // 실제 <td>의 형제들 중 몇 번째인지 계산
                const siblings = cellEl.parentElement.children;
                for (let i = 0; i < siblings.length; i++) {
                  if (siblings[i] === cellEl) {
                    nthChildPositions.push(i + 1); // nth-child는 1-based
                    break;
                  }
                }
              }
            } catch (e) { /* 감지 실패 시 무시 */ }
          });

          if (nthChildPositions.length > 0) {
            const style = document.createElement('style');
            style.id = COLUMN_STYLE_ID;
            // .aui-grid-body-panel 한정으로 적용하여 헤더/푸터 영향 없음
            const selectors = nthChildPositions.map(n =>
              `#horizontal_grid .aui-grid-body-panel .aui-grid-table td:nth-child(${n})`
            );
            style.textContent = `${selectors.join(',\n')} {
  background-color: #DBEAFE !important;
}`;
            document.head.appendChild(style);
          }
        }
      };

      window.AUIGrid.bind(grid, "headerClick", function (e) {
        const columnIndex = e.columnIndex;
        const dataField = e.dataField;
        if (!dataField || dataField === '_rownum') return false;

        const rowCount = window.AUIGrid.getRowCount(grid);
        if (rowCount <= 0) return false;

        isColumnSelecting = true;

        if (e.orgEvent?.shiftKey && lastClickedColumnRef.current >= 0) {
          // Shift+Click: 범위 선택
          const start = Math.min(lastClickedColumnRef.current, columnIndex);
          const end = Math.max(lastClickedColumnRef.current, columnIndex);
          selectedColumnsRef.current = [];
          for (let i = start; i <= end; i++) {
            selectedColumnsRef.current.push(i);
          }
          applyColumnHighlight(selectedColumnsRef.current);
          window.dispatchEvent(new CustomEvent('columnSelectionChanged', { detail: { selectedIndices: [...selectedColumnsRef.current] } }));
          console.log('📎 [HorizontalGrid] Shift+클릭 범위 선택:', start, '~', end);

        } else if (e.orgEvent?.ctrlKey || e.orgEvent?.metaKey) {
          // Ctrl+Click: 개별 열 토글
          const idx = selectedColumnsRef.current.indexOf(columnIndex);
          if (idx >= 0) {
            selectedColumnsRef.current.splice(idx, 1);
          } else {
            selectedColumnsRef.current.push(columnIndex);
          }
          applyColumnHighlight(selectedColumnsRef.current);
          window.dispatchEvent(new CustomEvent('columnSelectionChanged', { detail: { selectedIndices: [...selectedColumnsRef.current] } }));
          console.log('📌 [HorizontalGrid] Ctrl+클릭 다중 선택:', selectedColumnsRef.current);

        } else {
          // 일반 클릭: 단일 열 선택
          selectedColumnsRef.current = [columnIndex];
          applyColumnHighlight([columnIndex]);
          window.dispatchEvent(new CustomEvent('columnSelectionChanged', { detail: { selectedIndices: [columnIndex] } }));
          console.log('📐 [HorizontalGrid] 헤더 클릭 - 열 전체 선택:', dataField);
        }

        lastClickedColumnRef.current = columnIndex;
        setTimeout(() => { isColumnSelecting = false; }, 50);
        return false; // 기본 정렬 동작 방지
      });

      // 그리드 본문 클릭 시 하이라이트 해제
      // DOM-level mousedown 사용: AUIGrid cellClick보다 신뢰성 높음
      window.AUIGrid.bind(grid, "cellClick", function () {
        if (isColumnSelecting) return;
        if (selectedColumnsRef.current.length > 0) {
          selectedColumnsRef.current = [];
          lastClickedColumnRef.current = -1;
          clearColumnHighlight();
          window.dispatchEvent(new CustomEvent('columnSelectionChanged', { detail: { selectedIndices: [] } }));
          console.log('📐 [HorizontalGrid] cellClick → 열 선택 해제');
        }
      });

      // 🔒 보조 해제: 그리드 DOM 클릭으로도 해제 (cellClick 미발화 대비)
      const gridEl = document.getElementById('horizontal_grid');
      if (gridEl) {
        gridEl.addEventListener('mousedown', (e) => {
          if (isColumnSelecting) return;
          // 헤더 클릭이면 무시 (headerClick에서 처리)
          const isHeaderClick = e.target.closest('.aui-grid-header-renderer, .aui-grid-header-panel');
          if (isHeaderClick) return;
          if (selectedColumnsRef.current.length > 0) {
            selectedColumnsRef.current = [];
            lastClickedColumnRef.current = -1;
            clearColumnHighlight();
            window.dispatchEvent(new CustomEvent('columnSelectionChanged', { detail: { selectedIndices: [] } }));
            console.log('📐 [HorizontalGrid] mousedown → 열 선택 해제 (보조)');
          }
        });
      }

      // 🆕 헤더 컨텍스트 메뉴 설정 (열 관련 메뉴)
      const headerContextMenuItems = [
        { label: "🔗 선택 열 병합...", callback: "mergeColumns" },
        { label: "🤖 AI로 열 변환...", callback: "mergeColumnsPrompt" },
        { label: "-" },
        { label: "열 복사", callback: "copyColumn" },
        { label: "열 추가", callback: "addColumn" },
        { label: "열 삭제", callback: "deleteColumn" },
        { label: "-" },
        { label: "수량×단가 금액 열 추가", callback: "addAmountColumn" },
        { label: "수량/금액으로 단가 열 추가", callback: "addUnitPriceColumn" }
      ];

      // 🆕 셀 컨텍스트 메뉴 설정 (행 관련 메뉴)
      const cellContextMenuItems = [
        { label: "행 삽입", callback: "insertRow" },
        { label: "행 추가", callback: "addRow" },
        { label: "-" },
        { label: "선택된 행 삭제", callback: "deleteSelectedRows" },
        { label: "-" },
        { label: "선택된 열 위로 병합", callback: "mergeUp" },
        { label: "삭제", callback: "clearCell" },
        { label: "선택된 열 왼쪽으로 병합", callback: "mergeLeft" }
      ];


      // 🆕 컨텍스트 메뉴 이벤트 핸들러 (레거시 패턴 적용 + Benchmarking 고도화)
      window.AUIGrid.bind(grid, "contextMenu", function (event) {
        const { dataField, rowIndex, columnIndex, target } = event;
        console.log('📋 [ContextMenu] event:', { target, dataField, rowIndex, columnIndex });

        // 뷰포트 경계 처리 공통 함수
        const getMenuPosition = (pageX, pageY, menuWidth, menuHeight) => {
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          let posX = pageX;
          let posY = pageY;

          if (posX + menuWidth > viewportWidth) posX = viewportWidth - menuWidth;
          if (posY + menuHeight > viewportHeight) posY = viewportHeight - menuHeight;
          return { x: posX, y: posY };
        };

        // 헤더 영역 우클릭 시
        if (target === "header") {
          const pos = getMenuPosition(event.pageX, event.pageY, 180, 280);
          setHeaderMenuVisible(true);
          setGridMenuVisible(false); // 셀 메뉴는 닫기
          setHeaderMenuPosition(pos);
          setHeaderMenuTarget({
            columnIndex: event.columnIndex,
            dataField: event.dataField || '',
            headerText: event.headerText || event.dataField || ''
          });
          return false;
        }

        // 그리드 영역(셀/행번호) 우클릭 시 - 고도화된 동적 메뉴 표시
        else {
          console.log('📋 [ContextMenu] 그리드 본문 클릭 - 동적 메뉴 표시');

          // 선택 정보 분석
          const selectedItems = window.AUIGrid.getSelectedItems(grid);
          const selectedIndices = Array.from(new Set(selectedItems.map(item => item.rowIndex))).sort((a, b) => a - b);

          let rowCount = 1;
          let rowRange = '';

          if (selectedIndices.length > 1) {
            rowCount = selectedIndices.length;
            const minIdx = Math.min(...selectedIndices) + 1;
            const maxIdx = Math.max(...selectedIndices) + 1;
            rowRange = `${minIdx} - ${maxIdx}`;
          } else {
            rowRange = `${rowIndex + 1}`;
          }

          const pos = getMenuPosition(event.pageX, event.pageY, 220, 350);
          setGridMenuVisible(true);
          setHeaderMenuVisible(false); // 헤더 메뉴는 닫기
          setGridMenuPosition(pos);
          setGridMenuTarget({
            rowIndex,
            columnIndex,
            dataField,
            rowCount,
            rowRange,
            selectedIndices
          });

          return false; // 기본 메뉴 표시 안함
        }
      });

      // 🆕 키보드 이벤트 핸들러 (Delete 키 + ⌨️ 화살표 행 이동)
      window.AUIGrid.bind(grid, "keyDown", function (event) {
        // Delete 키 (46)
        if (event.keyCode === 46) {
          const selectedItems = window.AUIGrid.getSelectedItems(grid);
          if (selectedItems && selectedItems.length > 0) {
            const selectedIndices = Array.from(new Set(selectedItems.map(item => item.rowIndex))).sort((a, b) => a - b);
            handleGridMenuAction('deleteSelectedRows', { selectedIndices });
            return false; // 기본 동작 방지
          }
        }

        // ⌨️ [Keyboard Navigation] Up(38), Down(40) 화살표 키
        // multipleCells 모드에서는 화살표가 포커스만 이동하고 선택(Selection)은 변경하지 않음
        // setSelectionByIndex는 selectionChange만 발생 (cellClick은 발생하지 않음)
        // → 페이지 이동 + ref 업데이트를 직접 수행
        if (event.keyCode === 38 || event.keyCode === 40) {
          const currentRow = selectedRowRef.current !== null ? selectedRowRef.current : 0;
          const gridData = window.AUIGrid.getGridData(grid);
          const totalRows = gridData ? gridData.length : 0;
          if (totalRows === 0) return false;

          const targetRow = event.keyCode === 40
            ? Math.min(currentRow + 1, totalRows - 1)
            : Math.max(currentRow - 1, 0);

          // 1. AUIGrid 시각적 선택 업데이트
          window.AUIGrid.setSelectionByIndex(grid, targetRow, 0);

          // 2. 타겟 행 데이터 읽기 (동기 — gridData에서 직접)
          const item = gridData[targetRow];
          if (item) {
            const rowPageNumber = parseInt(item.P || item.pageNumber || item.page, 10);

            // 3. 📄 PDF 페이지 이동
            if (rowPageNumber && !isNaN(rowPageNumber) && onPageChangeRef.current) {
              onPageChangeRef.current(rowPageNumber);
            }

            // 4. 🎨 ref 업데이트 + 그리드 리프레시 (cellClick과 동일)
            activePageRef.current = rowPageNumber || null;
            selectedRowRef.current = targetRow;
            try { window.AUIGrid.refresh(grid); } catch (e) { }

            // 5. 🔍 PDF 텍스트 하이라이트 (cellClick의 onFieldClick과 동일 패턴)
            // 가장 긴 텍스트 필드를 선택 (Description 등 — 짧은 "#" 같은 값은 제외)
            if (onFieldClick) {
              try {
                const colInfoList = window.AUIGrid.getColumnInfoList(grid);
                let bestField = null, bestValue = '', bestLen = 0;
                for (const col of colInfoList) {
                  const df = col.dataField;
                  if (!df || df === 'No.' || df === 'P' || df === 'pageNumber' || df === '#') continue;
                  const val = item[df];
                  if (val && typeof val === 'string' && val.length > bestLen) {
                    bestField = df;
                    bestValue = val;
                    bestLen = val.length;
                  }
                }
                if (bestField && bestLen > 3) {
                  onFieldClick({
                    fieldKey: bestField,
                    fieldValue: bestValue,
                    page: rowPageNumber || null,
                    coordinates: null,
                    confidence: null,
                    hasGrounding: false
                  });
                }
              } catch (e) { }
            }
          }

          return false; // AUIGrid 기본 화살표 동작 차단
        }
      });




      // ⭐ 셀 선택 변경 시 선택된 셀 합계 계산
      window.AUIGrid.bind(grid, "selectionChange", function (event) {
        try {
          const selectedItems = window.AUIGrid.getSelectedItems(grid);
          console.log('📊 [Selection] event:', event, 'selectedItems:', selectedItems);

          if (!selectedItems || selectedItems.length === 0) {
            // 선택 해제 시 기본 푸터 복원
            if (footerLayout.length > 0) {
              window.AUIGrid.setFooter(grid, footerLayout);
            }
            return;
          }

          // 선택된 셀의 값 수집 및 컬럼별 합계 계산
          let sum = 0;
          let count = 0;
          let hasNumeric = false;
          const selectedColumns = new Set(); // 선택된 컬럼 추적

          selectedItems.forEach(sel => {
            // AUIGrid multipleCells 모드: sel 자체가 item이거나 sel.item이 있을 수 있음
            const item = sel.item || sel;
            const dataField = sel.dataField || sel.columnIndex;

            // ⌨️ [키보드 네비게이션] 페이지 이동 + 하이라이트 (첫 번째 선택 아이템만)
            if (sel === selectedItems[0] && item) {
              const rowPageNumber = parseInt(item.P || item.pageNumber || item.page, 10);
              if (rowPageNumber && !isNaN(rowPageNumber) && onPageChangeRef.current) {
                // 페이지가 변경된 경우에만 페이지 이동 (불필요한 호출 방지)
                if (activePageRef.current !== rowPageNumber) {
                  onPageChangeRef.current(rowPageNumber);
                  console.log(`⌨️ [Selection→Page] 행 선택 → 페이지 ${rowPageNumber} 이동`);
                }
                activePageRef.current = rowPageNumber;
              }
              selectedRowRef.current = sel.rowIndex;
              // 🎨 페이지 그룹 하이라이트 리프레시
              try { window.AUIGrid.refresh(grid); } catch (e) { }
            }

            if (dataField) {
              selectedColumns.add(dataField);
            }

            // dataField가 있으면 해당 필드 값 사용
            let value = null;
            if (dataField && item[dataField] !== undefined) {
              value = item[dataField];
            } else if (sel.value !== undefined) {
              value = sel.value;
            }

            console.log('📊 [Selection] 셀:', { dataField, value, sel });

            if (value !== undefined && value !== null && value !== '') {
              // 콤마 제거 후 숫자 변환
              const cleanValue = String(value).replace(/,/g, '').trim();
              const numValue = parseFloat(cleanValue);
              if (!isNaN(numValue)) {
                sum += numValue;
                count++;
                hasNumeric = true;
              }
            }
          });

          console.log(`📊 [Selection Sum] count=${count}, sum=${sum}, hasNumeric=${hasNumeric}, columns:`, [...selectedColumns]);

          if (hasNumeric && count > 0) {
            // ⭐ 선택된 컬럼에 선택 합계 표시 (기존 컬럼 합계 대체)
            const selectionFooter = [];

            // 기존 footerLayout을 복사하되, 선택된 컬럼만 선택 합계로 대체
            footerLayout.forEach(footer => {
              if (selectedColumns.has(footer.dataField)) {
                // 선택된 컬럼: 선택 합계로 대체
                selectionFooter.push({
                  dataField: footer.dataField,
                  positionField: footer.positionField,
                  labelText: `[${count}] ${sum.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}`,
                  style: "text-align: right; font-weight: bold; color: #DC2626; font-size: 11px; background-color: #FEF2F2;"
                });
              } else {
                // 미선택 컬럼: 기존 합계 유지
                selectionFooter.push(footer);
              }
            });

            // 선택되었지만 기존 footerLayout에 없는 경우 (비숫자 컬럼 등)
            selectedColumns.forEach(col => {
              const exists = selectionFooter.some(f => f.dataField === col);
              if (!exists && col !== 'pageNumber' && col !== 'idx') {
                selectionFooter.push({
                  dataField: col,
                  positionField: col,
                  labelText: `[${count}] ${sum.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}`,
                  style: "text-align: right; font-weight: bold; color: #DC2626; font-size: 11px; background-color: #FEF2F2;"
                });
              }
            });

            window.AUIGrid.setFooter(grid, selectionFooter);
            // ⭐ 푸터 UI 강제 갱신
            if (window.AUIGrid.updateFooter) {
              window.AUIGrid.updateFooter(grid);
            } else if (window.AUIGrid.refresh) {
              window.AUIGrid.refresh(grid);
            }
            console.log(`📊 [Selection Sum] 푸터 업데이트 완료: ${count}개, Σ${sum}`);
          }
        } catch (e) {
          console.warn('📊 [Selection Sum] 오류:', e);
        }
      });

      // ⭐ [Grounding] 셀 클릭 시 PDF 하이라이트 + 📄 페이지 이동
      window.AUIGrid.bind(grid, "cellClick", function (event) {
        const { dataField, rowIndex, item, columnIndex } = event;
        const tableRowIndex = item._rowIndex !== undefined ? (item._rowIndex - 1) : rowIndex;

        // 📄 [Page Navigation] 클릭한 행의 P(페이지) 값으로 PDF 뷰어 페이지 이동
        const rowPageNumber = parseInt(item.P || item.pageNumber || item.page, 10);
        if (rowPageNumber && !isNaN(rowPageNumber) && onPageChangeRef.current) {
          onPageChangeRef.current(rowPageNumber);
          console.log(`📄 [Grid→Page] 행 클릭 → 페이지 ${rowPageNumber} 이동`);
        }

        // 🎨 [Page Highlight] ref 업데이트 + 그리드 리프레시
        activePageRef.current = rowPageNumber || null;
        selectedRowRef.current = rowIndex;
        try { window.AUIGrid.refresh(grid); } catch (e) { }

        // 첫 번째 데이터 열인지 확인 (No., P 제외, 실제 데이터 컬럼)
        // columnIndex: 0=No., 1=P, 2=첫번째데이터필드(Invoice No. 또는 Item)
        const isFirstDataColumn = columnIndex === 2;

        let grounding = null;

        // 🔧 [Step 1] 단일 필드인 경우 먼저 확인 (Invoice No 등 테이블 외부 필드)
        // groundedFields에 직접 키가 있으면 단일 필드
        if (groundedFields[dataField]?.bbox?.coordinates) {
          grounding = groundedFields[dataField];
          console.log(`📦 [Grid Grounding] 단일필드 '${dataField}':`, grounding?.bbox?.coordinates ? '✅' : '❌');
        }
        // 🔧 [Step 2] 테이블 필드 처리
        else if (isFirstDataColumn) {
          // 첫 번째 테이블 컬럼 (Item No. 등) → 행 전체 하이라이트
          const rowKey = `items[${tableRowIndex}]`;
          grounding = groundedFields[rowKey];
          console.log(`📦 [Grid Grounding] Row ${tableRowIndex} 전체 → '${rowKey}':`, grounding?.bbox?.coordinates ? '✅' : '❌');

          // 행 레벨 없으면 셀 레벨 시도
          if (!grounding?.bbox?.coordinates) {
            const cellKey = `items[${tableRowIndex}].${dataField}`;
            grounding = groundedFields[cellKey];
            console.log(`📦 [Grid Grounding] Row ${tableRowIndex} 폴백 → 셀 '${cellKey}':`, grounding?.bbox?.coordinates ? '✅' : '❌');
          }
        } else {
          // 다른 열 클릭 → 셀별 하이라이트
          const cellKey = `items[${tableRowIndex}].${dataField}`;
          grounding = groundedFields[cellKey];

          // 셀별 없으면 행 레벨 폴백
          if (!grounding?.bbox?.coordinates) {
            const rowKey = `items[${tableRowIndex}]`;
            grounding = groundedFields[rowKey];
            console.log(`📦 [Grid Grounding] '${dataField}' → 행 폴백 '${rowKey}':`, grounding?.bbox?.coordinates ? '✅' : '❌');
          } else {
            console.log(`📦 [Grid Grounding] '${dataField}' → 셀 '${cellKey}':`, grounding?.bbox?.coordinates ? '✅' : '❌');
          }
        }

        // onFieldClick 호출 — 항상 호출 (bbox 없어도 텍스트 검색 하이라이트용)
        if (onFieldClick) {
          if (grounding?.bbox?.coordinates) {
            onFieldClick({
              fieldKey: dataField,
              fieldValue: item[dataField] || '',
              page: (grounding.bbox.page || 0) + 1, // 0-indexed → 1-indexed
              coordinates: grounding.bbox.coordinates,
              confidence: grounding.confidence || null,
              hasGrounding: true
            });
          } else {
            // 🔍 [Text Search Fallback] bbox 좌표 없어도 텍스트 값 + 페이지 전달
            // ⚠️ 클릭한 셀 값이 "EA", "1" 등 짧으면 PDF 전체에서 다수 매칭 → 색반전 문제
            // → 항상 가장 긴 텍스트 필드 사용 (Description of Goods 등)으로 고유 라인 식별
            const fallbackPage = parseInt(item.P || item.pageNumber || item.page, 10) || null;
            let bestField = dataField, bestValue = item[dataField] || '', bestLen = bestValue.length;
            try {
              const colInfoList = window.AUIGrid.getColumnInfoList(grid);
              for (const col of colInfoList) {
                const df = col.dataField;
                if (!df || df === 'No.' || df === 'P' || df === 'pageNumber' || df === '#') continue;
                const val = item[df];
                if (val && typeof val === 'string' && val.length > bestLen) {
                  bestField = df;
                  bestValue = val;
                  bestLen = val.length;
                }
              }
            } catch (e) { }
            onFieldClick({
              fieldKey: bestField,
              fieldValue: bestValue,
              page: fallbackPage,
              coordinates: null,
              confidence: null,
              hasGrounding: false
            });
          }
        }
      });

      // (⌨️ Keyboard nav 로직은 위의 keyDown 핸들러에 통합됨)

    }, 100); // 100ms 대기

    return () => {
      clearTimeout(timer);
      // Ctrl+Z 정리는 별도 useEffect에서 처리
      if (gridRef.current) {
        try {
          window.AUIGrid.destroy(gridRef.current);
        } catch (e) {
          console.warn('그리드 cleanup 실패:', e);
        }
        gridRef.current = null;
      }
    };
  }, [viewMode, pageResults, matchedTemplate, columnFilter, totalPages, checkedPages]); // 🔧 currentPage 제거 - 페이지 변경 시 그리드 재초기화 방지 (flickering fix)

  // AUIGrid 초기화
  useEffect(() => {
    if (!window.AUIGrid || viewMode !== 'grid') return;

    const gridId = 'current_page_grid';

    // 기존 그리드 제거
    if (gridRef.current) {
      try {
        window.AUIGrid.destroy(gridRef.current);
      } catch (e) {
        console.warn('그리드 제거 실패:', e);
      }
      gridRef.current = null;
    }

    // DOM이 준비될 때까지 대기
    const timer = setTimeout(() => {
      const gridElement = document.getElementById(gridId);
      if (!gridElement) {
        console.error(`그리드 DOM 요소를 찾을 수 없습니다: ${gridId}`);
        return;
      }

      const columnLayout = [
        {
          dataField: "fieldName",
          headerText: "필드명",
          width: "15%",
          editable: false,
          style: "left-align"
        },
        {
          dataField: "flashValue",
          headerText: "Flash",
          width: "28%",
          editable: false,
          style: "left-align"
        },
        {
          dataField: "proValue",
          headerText: "Pro",
          width: "28%",
          editable: false,
          style: "left-align"
        },
        {
          dataField: "currentValue",
          headerText: "최종값",
          width: "29%",
          editable: true,
          editRenderer: {
            type: "InputEditRenderer"
          },
          style: "left-align"
        }
      ];

      // 🔧 컴팩트 그리드 스타일 적용
      const gridProps = {
        headerHeight: 26,
        rowHeight: 22,
        fillColumnSizeMode: true,
        enableFilter: false,
        enableSorting: false,
        showRowNumColumn: true,
        rowNumHeaderText: "No",
        editable: true,
        selectionMode: "singleRow",
        selectionStyle: {
          backgroundColor: "#E5E7EB", // 연한 노란색 (선택된 행)
          color: "#1A1B1E" // 로고 색상 텍스트
        }
      };

      const grid = window.AUIGrid.create(`#${gridId}`, columnLayout, gridProps);
      gridRef.current = grid;

      // 데이터 정렬 및 설정
      const sortedData = [...dataRows].sort((a, b) => {
        const indexA = fieldOrder.indexOf(a.key);
        const indexB = fieldOrder.indexOf(b.key);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
      window.AUIGrid.setGridData(grid, sortedData);

      // 셀 편집 완료 이벤트
      window.AUIGrid.bind(grid, "cellEditEnd", function (event) {
        setEditedData(prev => ({
          ...prev,
          [event.item.fieldName]: event.value
        }));
      });
    }, 100); // 100ms 대기

    return () => {
      clearTimeout(timer);
      if (gridRef.current) {
        try {
          window.AUIGrid.destroy(gridRef.current);
        } catch (e) {
          console.warn('그리드 cleanup 실패:', e);
        }
        gridRef.current = null;
      }
    };
  }, [viewMode, currentPage, dataRows]);

  // ResizeObserver를 사용한 자동 리사이즈
  useEffect(() => {
    if (viewMode === 'form' || !window.AUIGrid) {
      return;
    }

    const gridId = viewMode === 'grid' ? 'current_page_grid' : 'horizontal_grid';

    // 그리드가 생성될 때까지 대기
    const setupObserver = () => {
      const gridElement = document.getElementById(gridId);

      if (!gridElement || !gridElement.parentElement) {
        console.warn(`⚠️ 그리드 요소를 찾을 수 없음: ${gridId}`);
        return null;
      }

      if (!window.AUIGrid.isCreated(`#${gridId}`)) {
        console.warn(`⚠️ 그리드가 아직 생성되지 않음: ${gridId}`);
        return null;
      }

      // ResizeObserver로 부모 컨테이너 크기 변화 감지
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (window.AUIGrid.isCreated(`#${gridId}`)) {
            // 약간의 지연을 두고 리사이즈 (DOM 업데이트 완료 대기)
            setTimeout(() => {
              try {
                window.AUIGrid.resize(`#${gridId}`);
                // 🔧 컬럼 너비 자동 맞춤 - 분할선 드래그 시 그리드 확장
                if (window.AUIGrid.fitColumnWidthToContainer) {
                  window.AUIGrid.fitColumnWidthToContainer(`#${gridId}`);
                }
              } catch (e) {
                // 리사이즈 실패는 조용히 처리
              }
            }, 50);
          }
        }
      });

      // 부모 컨테이너 관찰 시작
      resizeObserver.observe(gridElement.parentElement);

      return resizeObserver;
    };

    // 그리드 생성 후 Observer 설정 (여러 번 시도)
    let observer = null;
    const timer1 = setTimeout(() => {
      observer = setupObserver();
    }, 200);

    const timer2 = setTimeout(() => {
      if (!observer) observer = setupObserver();
    }, 500);

    const timer3 = setTimeout(() => {
      if (!observer) observer = setupObserver();
    }, 1000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [viewMode]);

  // 오른쪽 패널 접기/펼치기에 따른 추가 리사이즈 (ResizeObserver 보조)
  useEffect(() => {
    if (viewMode === 'form' || !window.AUIGrid) {
      return;
    }

    const gridId = viewMode === 'grid' ? 'current_page_grid' : 'horizontal_grid';

    // CSS transition 완료 후 강제 리사이즈
    const timer = setTimeout(() => {
      if (window.AUIGrid.isCreated(`#${gridId}`)) {
        window.AUIGrid.resize(`#${gridId}`);
        // 🔧 컬럼 너비 자동 맞춤
        if (window.AUIGrid.fitColumnWidthToContainer) {
          window.AUIGrid.fitColumnWidthToContainer(`#${gridId}`);
        }
        console.log('📐 [Grid] Resize triggered by panel change');

        // 🔧 리사이즈 후 열 하이라이트 복원 (resize가 DOM을 재생성하므로)
        if (selectedColumnsRef.current && selectedColumnsRef.current.length > 0 && gridRef.current) {
          const grid = gridRef.current;
          const COLUMN_STYLE_ID = 'column-selection-style';
          // 기존 스타일 제거
          const existingStyle = document.getElementById(COLUMN_STYLE_ID);
          if (existingStyle) existingStyle.remove();
          // 헤더 하이라이트 복원
          const gridEl = document.getElementById(gridId);
          if (gridEl) {
            const headerCells = gridEl.querySelectorAll('.aui-grid-header-renderer');
            selectedColumnsRef.current.forEach(colIdx => {
              if (headerCells[colIdx]) headerCells[colIdx].classList.add('selected-column-header');
            });
          }
          // 바디 셀 하이라이트 복원
          const rowCount = window.AUIGrid.getRowCount(grid);
          if (rowCount > 0) {
            const nthChildPositions = [];
            selectedColumnsRef.current.forEach(colIdx => {
              try {
                const cellEl = window.AUIGrid.getCellElementByIndex(grid, 0, colIdx);
                if (cellEl?.parentElement) {
                  const siblings = cellEl.parentElement.children;
                  for (let i = 0; i < siblings.length; i++) {
                    if (siblings[i] === cellEl) { nthChildPositions.push(i + 1); break; }
                  }
                }
              } catch (e) { /* ignore */ }
            });
            if (nthChildPositions.length > 0) {
              const style = document.createElement('style');
              style.id = COLUMN_STYLE_ID;
              const selectors = nthChildPositions.map(n =>
                `#${gridId} .aui-grid-body-panel .aui-grid-table td:nth-child(${n})`
              );
              style.textContent = `${selectors.join(',\n')} { background-color: #DBEAFE !important; }`;
              document.head.appendChild(style);
            }
          }
          console.log('📐 [Grid] Column highlights restored after resize');
        }
      }
    }, 350);

    // 🔧 2차 하이라이트 복원 — AUIGrid 가상 렌더링이 나중에 완료될 수 있으므로
    const timer2 = setTimeout(() => {
      if (!window.AUIGrid.isCreated(`#${gridId}`)) return;
      if (selectedColumnsRef.current && selectedColumnsRef.current.length > 0 && gridRef.current) {
        const grid = gridRef.current;
        const COLUMN_STYLE_ID = 'column-selection-style';
        const gridEl = document.getElementById(gridId);
        if (!gridEl) return;

        // 헤더 하이라이트 재적용
        const headerCells = gridEl.querySelectorAll('.aui-grid-header-renderer');
        selectedColumnsRef.current.forEach(colIdx => {
          if (headerCells[colIdx]) headerCells[colIdx].classList.add('selected-column-header');
        });

        // 바디 셀 CSS 재적용
        const rowCount = window.AUIGrid.getRowCount(grid);
        if (rowCount > 0) {
          // 기존 스타일 제거 후 재삽입
          const existingStyle = document.getElementById(COLUMN_STYLE_ID);
          if (existingStyle) existingStyle.remove();

          const nthChildPositions = [];
          selectedColumnsRef.current.forEach(colIdx => {
            try {
              const cellEl = window.AUIGrid.getCellElementByIndex(grid, 0, colIdx);
              if (cellEl?.parentElement) {
                const siblings = cellEl.parentElement.children;
                for (let i = 0; i < siblings.length; i++) {
                  if (siblings[i] === cellEl) { nthChildPositions.push(i + 1); break; }
                }
              }
            } catch (e) { /* ignore */ }
          });
          if (nthChildPositions.length > 0) {
            const style = document.createElement('style');
            style.id = COLUMN_STYLE_ID;
            const selectors = nthChildPositions.map(n =>
              `#${gridId} .aui-grid-body-panel .aui-grid-table td:nth-child(${n})`
            );
            style.textContent = `${selectors.join(',\n')} { background-color: #DBEAFE !important; }`;
            document.head.appendChild(style);
          }
        }
        console.log('📐 [Grid] Column highlights restored (2nd pass)');
      }
    }, 650);

    return () => { clearTimeout(timer); clearTimeout(timer2); };
  }, [isRightPanelCollapsed, viewMode, showAIPanel]); // 🤖 showAIPanel 추가

  // 그리드 렌더링
  const renderGrid = () => {
    return (
      <div className="w-full h-full" style={{ minWidth: 0, minHeight: 0 }}>
        <div
          id="current_page_grid"
          style={{
            width: '100%',
            height: '100%',
            minWidth: 0
          }}
        />
      </div>
    );
  };

  // 가로 그리드 렌더링 (필드명이 헤더, 페이지가 행)
  const renderHorizontalGrid = () => {
    console.log('🔍 [renderHorizontalGrid] 조건 확인:', {
      isPDF,
      pageResultsLength: pageResults?.length,  // 📄 pageResults 사용
      willRenderEmpty: !isPDF || !pageResults || pageResults.length === 0
    });

    // 📄 pageResults 기준으로 체크 (체크 상태와 관계없이 그리드 렌더링)
    if (!isPDF || !pageResults || pageResults.length === 0) {
      return (
        <div className="p-2 text-center text-gray-500">
          페이지 결과가 없습니다. (isPDF: {String(isPDF)}, results: {pageResults?.length || 0})
        </div>
      );
    }

    return (
      <div className="w-full h-full flex flex-col" style={{ minWidth: 0, minHeight: 0 }}>
        {/* 그리드 영역 */}
        <div className="flex-1" style={{ minHeight: 0 }}>
          <div
            id="horizontal_grid"
            style={{
              width: '100%',
              height: '100%',
              minWidth: 0
            }}
          />
        </div>
      </div>
    );
  };

  // 엑셀 다운로드 - 🔧 AUIGrid 동적 열 지원 추가
  const handleExcelDownload = () => {
    const fileName = `${documentType || 'data'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    console.log('🔍 [Excel] 다운로드 시작, viewMode:', viewMode);

    // 🔧 AUIGrid 데이터 우선 사용 (동적 열 포함!)
    if (window.AUIGrid && window.AUIGrid.isCreated('#horizontal_grid')) {
      try {
        const gridData = window.AUIGrid.getGridData('#horizontal_grid');
        const columnLayout = window.AUIGrid.getColumnLayout('#horizontal_grid');

        if (gridData && gridData.length > 0 && columnLayout && columnLayout.length > 0) {
          console.log('📊 [Excel] AUIGrid 직접 내보내기:', gridData.length, '행,', columnLayout.length, '열');

          // 열 순서에 맞게 데이터 재구성 (숫자는 number 타입으로 변환)
          const exportData = gridData.map(row => {
            const exportRow = {};
            columnLayout.forEach(col => {
              if (col.dataField) {
                const headerText = col.headerText || col.dataField;
                let value = row[col.dataField] ?? '';

                // 🔧 숫자 변환: 콤마 제거 후 숫자인지 확인
                if (typeof value === 'string' && value.trim() !== '') {
                  const cleanValue = value.replace(/,/g, '').trim();
                  // 숫자 패턴: 정수 또는 소수
                  if (/^-?\d+(\.\d+)?$/.test(cleanValue)) {
                    const numValue = parseFloat(cleanValue);
                    if (!isNaN(numValue)) {
                      value = numValue; // 숫자 타입으로 변환
                    }
                  }
                }

                exportRow[headerText] = value;
              }
            });
            return exportRow;
          });

          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(exportData);

          // 열 너비 설정
          const colWidths = columnLayout.map(col => ({ wch: col.width ? Math.floor(col.width / 7) : 15 }));
          ws['!cols'] = colWidths;

          XLSX.utils.book_append_sheet(wb, ws, 'Items');
          XLSX.writeFile(wb, fileName);

          console.log('✅ [Excel] AUIGrid 데이터 내보내기 완료');
          return;
        }
      } catch (err) {
        console.warn('⚠️ [Excel] AUIGrid 직접 내보내기 실패, 템플릿 방식으로 폴백:', err);
      }
    }

    // 📋 폴백: 템플릿 기반 내보내기 (기존 로직)
    // 선택된 템플릿 정보 가져오기
    const selectedTemplate = selectedTemplateId ? templates[selectedTemplateId] : null;
    const singleFields = selectedTemplate?.singleFields || selectedTemplate?.fields || [];
    const tableFields = selectedTemplate?.tableFields || [];
    const computedFields = selectedTemplate?.computedFields || [];

    console.log('📋 [Excel] 템플릿 기반 폴백:', {
      templateId: selectedTemplateId,
      templateName: selectedTemplate?.name,
      singleFieldsCount: singleFields.length,
      tableFieldsCount: tableFields.length,
      computedFieldsCount: computedFields.length
    });

    // 합성 필드 계산 함수 (대소문자 무시 매칭)
    const evaluateComputedField = (formula, sourceData) => {
      if (!formula) return '';
      let result = formula;

      // formula에서 {placeholder} 형태를 모두 찾음
      const placeholders = formula.match(/\{([^}]+)\}/g) || [];

      placeholders.forEach(placeholder => {
        const keyInFormula = placeholder.slice(1, -1); // {key} -> key

        // sourceData에서 대소문자 무시하고 키 찾기
        const matchingKey = Object.keys(sourceData).find(
          k => k.toLowerCase() === keyInFormula.toLowerCase()
        );

        if (matchingKey && sourceData[matchingKey]) {
          result = result.replace(placeholder, sourceData[matchingKey]);
        } else {
          result = result.replace(placeholder, ''); // 매칭 안되면 빈 문자열
        }
      });

      return result.trim();
    };

    try {
      const wb = XLSX.utils.book_new();

      // pageResults 또는 현재 페이지 데이터로 vehicles 배열 생성
      const vehicles = [];
      if (pageResults && pageResults.length > 0) {
        pageResults.forEach((pr, idx) => {
          const extractedData = pr.extractedData || pr;
          vehicles.push({
            fileName: documentType || 'Document',
            pageNumber: pr.pageNumber || idx + 1,
            current: { ...extractedData }
          });
        });
      } else if (editedData && Object.keys(editedData).length > 0) {
        vehicles.push({
          fileName: documentType || 'Document',
          pageNumber: currentPage,
          current: { ...editedData }
        });
      }

      if (vehicles.length === 0) {
        alert('내보낼 데이터가 없습니다.');
        return;
      }

      // 🔧 테이블 필드가 있으면 펼치기 로직 적용
      if (tableFields.length > 0) {
        // 각 테이블별로 별도 시트 생성
        tableFields.forEach((table, tableIndex) => {
          const flattenedData = [];

          vehicles.forEach(v => {
            // 단일 필드 데이터 수집 (파일명, 페이지)
            const baseSingleData = {
              '파일명': v.fileName,
              '페이지': v.pageNumber,
            };

            // 최상위 단일 필드 추가
            singleFields.forEach(field => {
              const fieldKey = typeof field === 'string' ? field : field.key;
              const displayName = typeof field === 'string' ? field : (field.displayName || field.key);
              baseSingleData[displayName] = v.current[fieldKey] || '-';
            });

            // 테이블 데이터 가져오기 (🔧 FIX: items 폴백 추가)
            let tableData = v.current[table.tableName] || [];
            if (tableData.length === 0 && v.current.items) {
              tableData = v.current.items;
            }

            if (Array.isArray(tableData) && tableData.length > 0) {
              // 테이블 행마다 단일 필드 반복
              tableData.forEach((row, rowIdx) => {
                const flatRow = { ...baseSingleData, '행번호': rowIdx + 1 };

                // 🔧 합성 필드는 각 ROW 데이터로 계산
                computedFields.forEach(cf => {
                  const displayName = cf.displayName || cf.key;
                  flatRow[displayName] = evaluateComputedField(cf.formula, row);
                });

                // 테이블 컬럼 추가
                table.columns.forEach(col => {
                  const displayName = col.displayName || col.key;
                  if (col.type === 'computed' && col.formula) {
                    // 🔧 테이블 합성 필드 계산
                    let computed = col.formula;
                    const pattern = /\{([^}]+)\}/g;
                    let hasReplacement = false;

                    computed = computed.replace(pattern, (_, key) => {
                      hasReplacement = true;
                      const valKey = Object.keys(row).find(k => k.toLowerCase() === key.toLowerCase());
                      let val = valKey ? row[valKey] : '';
                      if (typeof val === 'string' && /^-?[\d,]+(\.\d+)?$/.test(val)) {
                        return val.replace(/,/g, '');
                      }
                      return val || 0;
                    });

                    if (hasReplacement && /^[\d+\-*/.() \s]+$/.test(computed)) {
                      try {
                        // eslint-disable-next-line no-eval
                        flatRow[displayName] = String(eval(computed));
                      } catch (e) {
                        flatRow[displayName] = computed;
                      }
                    } else {
                      // 문자열 결합 (재치환)
                      let stringComputed = col.formula;
                      stringComputed = stringComputed.replace(pattern, (_, key) => {
                        const valKey = Object.keys(row).find(k => k.toLowerCase() === key.toLowerCase());
                        return valKey ? row[valKey] : '';
                      });
                      flatRow[displayName] = stringComputed;
                    }
                  } else {
                    flatRow[displayName] = row[col.key] || '-';
                  }
                });
                flattenedData.push(flatRow);
              });
            } else {
              // 테이블 데이터 없으면 단일 필드만 출력
              const flatRow = { ...baseSingleData, '행번호': '-' };
              computedFields.forEach(cf => {
                const displayName = cf.displayName || cf.key;
                flatRow[displayName] = '-';
              });
              table.columns.forEach(col => {
                const displayName = col.displayName || col.key;
                flatRow[displayName] = '-';
              });
              flattenedData.push(flatRow);
            }
          });

          const ws = XLSX.utils.json_to_sheet(flattenedData);
          const sheetName = table.tableName.substring(0, 31); // Excel 시트 이름 제한
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        });
      } else {
        // 기존 로직: 테이블 필드 없음 (단일 필드만)
        const data = vehicles.map(v => {
          const baseData = {
            '파일명': v.fileName,
            '페이지': v.pageNumber,
          };

          // 합성 필드 추가
          computedFields.forEach(cf => {
            const displayName = cf.displayName || cf.key;
            baseData[displayName] = evaluateComputedField(cf.formula, v.current);
          });

          if (singleFields.length > 0) {
            singleFields.forEach(field => {
              const fieldKey = typeof field === 'string' ? field : field.key;
              const displayName = typeof field === 'string' ? field : (field.displayName || field.key);
              baseData[displayName] = v.current[fieldKey] || '-';
            });
          } else {
            // 템플릿이 없으면 모든 필드 출력
            Object.entries(v.current).forEach(([key, value]) => {
              if (!['documentType', 'pageResults', 'metadata', 'items', 'pageNumber', 'P', 'page'].includes(key)) {
                baseData[key] = value || '-';
              }
            });
          }
          return baseData;
        });

        const ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, '추출결과');
      }

      XLSX.writeFile(wb, fileName);
      console.log('✅ 엑셀 다운로드 완료:', fileName);
    } catch (e) {
      console.error('❌ 엑셀 다운로드 실패:', e);
      alert('엑셀 다운로드 실패: ' + e.message);
    }
  };

  // 차량명 매핑 확인 및 검증 진행
  const proceedWithCarNameMapping = async (selectedMasterData, similarity, matchType) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '');
    const finalData = { ...editedData };

    // 그리드 모드인 경우 그리드에서 데이터 가져오기
    if (viewMode === 'grid' && gridRef.current) {
      const gridData = window.AUIGrid.getGridData(gridRef.current);
      gridData.forEach(row => {
        finalData[row.fieldName] = row.currentValue;
      });
    }

    // ⭐ 차량명을 선택한 기초자료의 영문명으로 변경
    if (selectedMasterData && selectedMasterData.car_name_en) {
      finalData.model = selectedMasterData.car_name_en.toUpperCase();

      // 매핑 저장 (학습)
      try {
        await fetch(`${API_BASE_URL}/api/vehicle-master/save-mapping`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ocrRecognizedName: carNameMappingData.ocrRecognizedName,
            masterId: selectedMasterData.master_id,
            carNameKr: selectedMasterData.car_name_kr,
            carNameEn: selectedMasterData.car_name_en,
            similarityScore: similarity,
            matchType: matchType,
            userId: 'system'
          })
        });
      } catch (err) {
        console.error('❌ 차량명 매핑 저장 실패:', err);
      }
    }

    // 실제 검증 진행
    await performValidation(finalData);
  };

  // 실제 검증 수행
  const performValidation = async (finalData) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '');

    // ⭐ 1. 주소 검증 가중치 저장
    if (finalData.address) {
      const ocrAddress = pageData.flashData?.address || pageData.proData?.address || finalData.address;
      const verifiedAddress = finalData.address;

      if (ocrAddress !== verifiedAddress && ocrAddress) {
        try {
          await fetch(`${API_BASE_URL}/api/address/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ocrRecognized: ocrAddress,
              verifiedAddress: verifiedAddress,
              userId: 'system'
            })
          });
        } catch (err) {
          console.error('❌ 주소 검증 가중치 저장 실패:', err);
        }
      }
    }

    finalData.documentType = pageData.documentType;
    finalData.pageNumber = currentPage;
    finalData.metadata = {
      ...finalData.metadata,
      matchRate: matchRate,
      userEdited: true,
      editedAt: new Date().toISOString(),
      flashData: pageData.flashData,
      proData: pageData.proData
    };

    // 검증 완료 처리
    if (fileId && onValidatePage) {
      await onValidatePage(fileId, currentPage, finalData);
    }

    // 팝업 닫기
    setShowCarNameMappingModal(false);
    setCarNameMappingData(null);
    setSimilarMatches([]);
  };

  // 검증 버튼 클릭 핸들러 (차량명 매핑 팝업 표시)
  const handleValidateClick = async () => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '');
    const currentData = { ...editedData };

    // 그리드 모드인 경우 그리드에서 데이터 가져오기
    if (viewMode === 'grid' && gridRef.current) {
      const gridData = window.AUIGrid.getGridData(gridRef.current);
      gridData.forEach(row => {
        currentData[row.fieldName] = row.currentValue;
      });
    }

    // 차량명이 있으면 매핑 팝업 표시
    if (currentData.model) {
      const ocrCarName = currentData.model;

      // 기초자료 조회
      try {
        const response = await fetch(`${API_BASE_URL}/api/vehicle-master/search/${encodeURIComponent(ocrCarName)}`);

        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data) {
            // 정확한 매칭 또는 학습된 매핑이 있는 경우
            // 단, 영문명이 있는 경우에만 masterData로 설정 (저장된 데이터만 표시)
            const masterData = result.data;
            const hasEnglishName = (masterData.BASE_CAR_NM_ENG && masterData.BASE_CAR_NM_ENG.trim() !== '') ||
              (masterData.car_name_en && masterData.car_name_en.trim() !== '');

            setCarNameMappingData({
              ocrRecognizedName: ocrCarName,
              currentData: currentData,
              masterData: hasEnglishName ? masterData : null, // 영문명이 없으면 null로 설정
              isExactMatch: !result.isSimilar
            });
            setShowCarNameMappingModal(true);
          } else {
            // 유사도 검색
            setSearchingSimilarity(true);
            const similarResponse = await fetch(`${API_BASE_URL}/api/vehicle-master/search-similar/${encodeURIComponent(ocrCarName)}?threshold=0.6`);

            if (similarResponse.ok) {
              const similarResult = await similarResponse.json();
              if (similarResult.success && similarResult.results.length > 0) {
                setCarNameMappingData({
                  ocrRecognizedName: ocrCarName,
                  currentData: currentData,
                  masterData: null,
                  isExactMatch: false
                });
                setSimilarMatches(similarResult.results);
                setShowCarNameMappingModal(true);
              } else {
                // 매핑 없음 - 직접 입력 모드
                setCarNameMappingData({
                  ocrRecognizedName: ocrCarName,
                  currentData: currentData,
                  masterData: null,
                  isExactMatch: false
                });
                setShowCarNameMappingModal(true);
              }
            } else {
              // 검색 실패 - 직접 입력 모드
              setCarNameMappingData({
                ocrRecognizedName: ocrCarName,
                currentData: currentData,
                masterData: null,
                isExactMatch: false
              });
              setShowCarNameMappingModal(true);
            }
            setSearchingSimilarity(false);
          }
        } else {
          // 검색 실패 - 직접 입력 모드
          setCarNameMappingData({
            ocrRecognizedName: ocrCarName,
            currentData: currentData,
            masterData: null,
            isExactMatch: false
          });
          setShowCarNameMappingModal(true);
        }
      } catch (err) {
        console.error('❌ 차량 기초자료 조회 실패:', err);
        // 오류 발생 시 직접 입력 모드
        setCarNameMappingData({
          ocrRecognizedName: ocrCarName,
          currentData: currentData,
          masterData: null,
          isExactMatch: false
        });
        setShowCarNameMappingModal(true);
      }
    } else {
      // 차량명이 없으면 바로 검증 진행
      await performValidation(currentData);
    }
  };

  // 검증 완료 (차량등록은 3.차량등록매핑 탭에서 수행) - 레거시 호환
  const handleConfirm = async () => {
    await handleValidateClick();
  };

  // 기초자료 저장 후 모달 갱신
  useEffect(() => {
    const handleVehicleMasterSaved = (event) => {
      if (showCarNameMappingModal && carNameMappingData) {
        const savedData = event.detail;
        // 저장된 차량명이 OCR 인식 차량명과 일치하는 경우에만 모달 갱신
        if (savedData.BASE_CAR_NM && savedData.BASE_CAR_NM.trim() === carNameMappingData.ocrRecognizedName.trim()) {
          // 저장된 데이터로 masterData 업데이트
          setCarNameMappingData(prev => ({
            ...prev,
            masterData: savedData // 저장된 데이터로 갱신
          }));
        }
      }
    };

    window.addEventListener('vehicleMasterSaved', handleVehicleMasterSaved);
    return () => {
      window.removeEventListener('vehicleMasterSaved', handleVehicleMasterSaved);
    };
  }, [showCarNameMappingModal, carNameMappingData]);

  // 데이터가 없으면 처리 대기 중 상태 표시 (우선순위 1)
  // 🔧 단, selectedTemplateId가 있고 matchedTemplate이 있으면 템플릿 필드 미리보기 표시
  const hasData = pageData.flashData || pageData.proData;
  const hasTemplatePreview = selectedTemplateId && matchedTemplate;

  if (!hasData && !hasTemplatePreview) {
    return (
      <div className="h-full flex flex-col bg-white overflow-hidden">
        <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200" style={{ backgroundColor: '#F8F9FA' }}>
          <h2 className="text-sm font-medium text-gray-600 flex items-center gap-2">
            <div className="animate-pulse inline-block text-xs">⏳</div>
            문서 처리 실행 대기 중
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">📄</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">파일 업로드 완료</h3>
            <p className="text-xs text-gray-500 mb-3 leading-relaxed">
              <span className="font-medium text-gray-700">일괄 처리</span> 버튼을 클릭하면<br />
              실시간으로 처리 결과가 여기에 표시됩니다.
            </p>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="text-xs text-gray-600 space-y-0.5">
                <p><span className="font-medium">분류</span> → <span className="font-medium">추출</span> → <span className="font-medium">검증</span></p>
                <p className="text-gray-400 text-[11px]">각 단계가 실시간으로 업데이트됩니다.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 🔧 템플릿 미리보기 모드: 데이터는 없지만 템플릿이 선택된 경우
  if (!hasData && hasTemplatePreview) {
    const singleFields = matchedTemplate.singleFields || matchedTemplate.fields || [];
    const tableFields = matchedTemplate.tableFields || [];
    const computedFields = matchedTemplate.computedFields || [];

    // 🚀 모든 필드를 outputOrder 기준으로 통합
    const allPreviewFields = [];

    // 단일 필드
    singleFields.forEach((f, idx) => {
      const field = typeof f === 'string' ? { key: f } : f;
      const order = field.outputOrder ?? (idx + 1);
      if (order > 0) {
        allPreviewFields.push({
          key: field.key,
          displayName: field.displayName || field.key,
          type: field.type || 'string',
          order: order,
          fieldType: 'single'
        });
      }
    });

    // 테이블 필드 컬럼
    tableFields.forEach(table => {
      (table.columns || []).forEach((col, colIdx) => {
        const order = col.outputOrder ?? (100 + colIdx + 1);
        allPreviewFields.push({
          key: col.key,
          displayName: col.displayName || col.key,
          type: col.type || 'string',
          order: order,
          fieldType: 'table',
          tableName: table.tableName
        });
      });
    });

    // 합성 필드
    computedFields.forEach((cf, idx) => {
      const order = cf.outputOrder ?? cf.order ?? (200 + idx + 1);
      if (order > 0) {
        allPreviewFields.push({
          key: cf.key,
          displayName: cf.displayName || cf.key,
          type: 'computed',
          order: order,
          fieldType: 'computed',
          formula: cf.formula
        });
      }
    });

    // outputOrder 기준 정렬
    allPreviewFields.sort((a, b) => a.order - b.order);

    return (
      <div className="h-full flex flex-col bg-white overflow-hidden">
        {/* 헤더 - AI Studio 스타일 (흰색 배경, 회색 테두리) */}
        <div className="flex-shrink-0 px-3 py-1 border-b border-gray-200 bg-white">
          <h2 className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
            <span className="text-gray-400">📋</span>
            {matchedTemplate.name}
            <span className="text-gray-300 ml-1">({allPreviewFields.length}개 필드)</span>
          </h2>
        </div>
        <div className="flex-1 overflow-auto">
          {/* 통합 필드 미리보기 - 슬림 테이블 형식 */}
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100 bg-gray-50 sticky top-0">
                <th className="py-0.5 px-2 text-left w-6 font-normal">NO.</th>
                <th className="py-0.5 px-2 text-left font-normal">필드명</th>
                <th className="py-0.5 px-2 text-left w-12 font-normal">타입</th>
                <th className="py-0.5 px-2 text-left font-normal">값</th>
              </tr>
            </thead>
            <tbody>
              {allPreviewFields.map((field, idx) => (
                <tr key={idx} className={`border-b border-gray-50 hover:bg-gray-50/50 ${field.fieldType === 'computed' ? 'bg-gray-50/30' :
                  field.fieldType === 'table' ? 'bg-blue-50/30' : ''
                  }`}>
                  <td className="py-0.5 px-2 text-gray-300">{idx + 1}</td>
                  <td className="py-0.5 px-2 text-gray-700">
                    {field.displayName}
                    {field.fieldType === 'computed' && <span className="ml-1 text-gray-500 text-[9px]">합성</span>}
                    {field.fieldType === 'table' && <span className="ml-1 text-blue-400 text-[9px]">{field.tableName}</span>}
                  </td>
                  <td className="py-0.5 px-2 text-gray-400">{field.type}</td>
                  <td className="py-0.5 px-2">
                    <span className="text-gray-300 italic">실행 후 표시</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // 체크된 페이지가 하나도 없을 때만 안내 메시지 표시
  // 🔧 변경: 현재 페이지 해제 여부가 아닌, 체크된 페이지가 있는지 확인
  const hasCheckedPages = checkedPages && checkedPages.length > 0;
  if (!hasCheckedPages) {
    return (
      <div className="h-full flex flex-col bg-white items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-xl">📄</span>
          </div>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">페이지가 선택되지 않음</h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">
            왼쪽 페이지 목록에서 체크박스를 선택하면<br />
            해당 페이지의 결과를 확인할 수 있습니다.
          </p>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
            <p className="text-xs text-gray-600">
              💡 <span className="font-medium">전체 선택</span>을 클릭하면 모든 페이지를 확인할 수 있습니다.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-row bg-white overflow-hidden">
      {/* 🔧 그리드 영역 - AI 패널이 열리면 자동으로 축소 */}
      <div
        className="flex-1 flex flex-col overflow-hidden"
        style={{ minWidth: 0 }}
      >
        {/* 통합 컨트롤 바 (탭 버튼 + 필터 + 뷰모드) */}
        <div className="flex items-center gap-1 px-1 py-1 border-b border-gray-200 bg-gray-50 flex-shrink-0 text-xs">
          {/* 탭 버튼 */}
          {onTabChange && (
            <>
              <button
                onClick={() => onTabChange('fields')}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${activeTab === 'fields'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <Settings className="w-3.5 h-3.5" />
                템플릿
                {hasTabChanges && <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />}
              </button>
              <button
                onClick={() => onTabChange('results')}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${activeTab === 'results'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <Table2 className="w-3.5 h-3.5" />
                추출결과
              </button>
              <div className="w-px h-3.5 bg-gray-300" />
            </>
          )}
          {/* P1 검증 상태 + 전체/단일/테이블 필터 제거 — 공간 절약, columnFilter='all' 기본값 유지 */}
          <div className="flex-1" />
          {/* 뷰 모드 토글 */}
          <button onClick={() => setViewMode('form')} className={`p-0.5 rounded transition ${viewMode === 'form' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:bg-gray-200'}`} title="폼 모드">
            <List className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setViewMode('horizontal')} className={`p-0.5 rounded transition ${viewMode === 'horizontal' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:bg-gray-200'}`} title="가로 그리드">
            <Table className="w-3.5 h-3.5" />
          </button>
          <button onClick={handleExcelDownload} className="p-0.5 rounded transition text-green-600 hover:bg-green-50" title="엑셀 다운로드">
            <Download className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* 결과 표시 영역 */}
        {console.log('🎯 [Render] viewMode:', viewMode)}
        <div className="flex-1 overflow-hidden">
          {viewMode === 'form' ? (
            <div className="h-full overflow-auto">
              {renderForm()}
            </div>
          ) : (
            viewMode === 'grid' ? renderGrid() : renderHorizontalGrid()
          )}
        </div>

        {/* 하단 버튼 */}
        <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-gray-50">
          {/* 검증 버튼 영역 - 🔧 TODO: 추후 삭제 예정, 현재 hidden */}
          <div className="hidden flex gap-2">
            {/* 현재 페이지 검증 */}
            <button
              onClick={handleValidateClick}
              disabled={validatedPages[currentPage]}
              className={`flex-1 px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${validatedPages[currentPage]
                ? 'bg-gray-300 text-gray-600 cursor-default'
                : 'bg-gray-600 text-white hover:bg-gray-700 active:bg-gray-800'
                }`}
              title={validatedPages[currentPage] ? '이미 검증 완료됨' : '현재 페이지 검증'}
            >
              {validatedPages[currentPage] ? '✓ 검증완료' : `현재 페이지 검증`}
            </button>

            {/* 전체 검증 */}
            {totalPages > 1 && (
              <button
                onClick={() => {
                  if (fileId && onValidateAllPages) {
                    // ⭐ editedData를 전달하여 수정된 데이터로 검증
                    onValidateAllPages(fileId, totalPages, editedData);
                  }
                }}
                className="flex-1 px-4 py-2 rounded text-sm font-medium transition-colors bg-gray-500 text-white hover:bg-gray-600 active:bg-gray-700"
                title={`전체 ${totalPages}개 페이지 일괄 검증`}
              >
                전체 검증
              </button>
            )}
          </div>

          {/* 검증 완료 안내 - 회색계열 */}
          {validatedPages[currentPage] && (
            <div className="mt-2 space-y-2">
              <div className="text-xs text-gray-600 bg-gray-100 px-3 py-2 rounded border border-gray-300 flex items-center gap-2">
                <span className="text-base">✓</span>
                <span>페이지 {currentPage} 검증 완료 - 3.차량등록매핑 탭에서 차량을 등록하세요</span>
              </div>

              {/* ⭐ 기초자료에서 가져온 필드 표시 */}
              {pageData.extractedData?.metadata?.filledFromMaster && pageData.extractedData.metadata.filledFromMaster.length > 0 && (
                <div className="text-xs bg-blue-50 px-3 py-2 rounded border border-blue-200 flex items-start gap-2">
                  <span className="text-blue-600 font-semibold">📋</span>
                  <div className="flex-1">
                    <span className="text-blue-700 font-semibold">기초자료에서 자동으로 가져온 필드:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {pageData.extractedData.metadata.filledFromMaster.map((field, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ⭐ 기초자료에 있는 차량이지만 현재 값으로 업데이트할 수 있는 옵션 */}
              {pageData.extractedData?.metadata?.masterDataFound && (
                <div className="text-xs bg-green-50 px-3 py-2 rounded border border-green-200 flex items-start gap-2">
                  <span className="text-green-600 font-semibold">✓</span>
                  <div className="flex-1">
                    <span className="text-green-700 font-semibold">차량명이 기초자료에 있습니다. 현재 값으로 기초자료를 업데이트하시겠습니까?</span>
                    <div className="mt-1">
                      <button
                        onClick={() => {
                          const currentData = pageData.extractedData;
                          const masterData = currentData.metadata?.masterData;
                          setEditingMasterId(masterData?.BASE_CAR_NO || null);
                          setNewVehicleData({
                            BASE_CAR_NM: masterData?.BASE_CAR_NM || currentData.model || '',
                            BASE_CAR_NM_ENG: currentData.model || masterData?.BASE_CAR_NM_ENG || '',
                            BASE_CAR_MAKER: currentData.brand || masterData?.BASE_CAR_MAKER || '',
                            BASE_CAR_YEAR: currentData.modelYear || masterData?.BASE_CAR_YEAR || '',
                            BASE_CAR_G_PUM: masterData?.BASE_CAR_G_PUM || '',
                            BASE_CAR_PUM: masterData?.BASE_CAR_PUM || '',
                            BASE_CAR_CC: currentData.displacement || masterData?.BASE_CAR_CC || '',
                            BASE_CAR_WEIGHT: currentData.totalWeight || masterData?.BASE_CAR_WEIGHT || '',
                            BASE_CAR_CBM: currentData.cbm || masterData?.BASE_CAR_CBM || ''
                          });
                          setShowAddVehicleModal(true);
                        }}
                        className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700"
                      >
                        현재 값으로 기초자료 업데이트
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ⭐ 차량명이 기초자료에 없을 경우 추가 옵션 */}
              {pageData.extractedData?.metadata?.masterDataNotFound && (
                <div className="text-xs bg-gray-50 px-3 py-2 rounded border border-gray-200 flex items-start gap-2">
                  <span className="text-gray-600 font-semibold">⚠️</span>
                  <div className="flex-1">
                    <span className="text-yellow-700 font-semibold">차량명 "{pageData.extractedData.metadata.carName}"이 기초자료에 없습니다.</span>
                    <div className="mt-1">
                      <button
                        onClick={() => {
                          setNewVehicleData({
                            car_name_kr: pageData.extractedData.metadata.carName || '',
                            car_name_en: '',
                            brand: pageData.extractedData.brand || '',
                            model_year: pageData.extractedData.modelYear || '',
                            trade_item_name: '',
                            item_name: '',
                            displacement: pageData.extractedData.displacement || '',
                            weight: pageData.extractedData.totalWeight || '',
                            cbm: pageData.extractedData.cbm || ''
                          });
                          setShowAddVehicleModal(true);
                        }}
                        className="px-2 py-1 bg-yellow-600 text-white rounded text-xs font-medium hover:bg-yellow-700"
                      >
                        기초자료에 추가
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 차량명 매핑 팝업 */}
        {showCarNameMappingModal && carNameMappingData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg shadow-xl w-[52vw] h-[80vh] flex flex-col">
              {/* 헤더 */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold" style={{ color: '#0A1037' }}>
                  차량명 매핑 확인
                </h3>
                <div className="flex items-center gap-2">
                  {/* 확인/취소 버튼 */}
                  {carNameMappingData.masterData && carNameMappingData.isExactMatch && (
                    <button
                      onClick={() => proceedWithCarNameMapping(carNameMappingData.masterData, 1.0, 'exact')}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm"
                    >
                      확인 (영문명: {carNameMappingData.masterData.BASE_CAR_NM_ENG || carNameMappingData.masterData.car_name_en?.toUpperCase() || ''})
                    </button>
                  )}
                  {similarMatches.length > 0 && !carNameMappingData.masterData && (
                    <button
                      onClick={() => {
                        if (similarMatches[0]) {
                          proceedWithCarNameMapping(similarMatches[0].data, similarMatches[0].similarity, 'similar');
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm"
                    >
                      확인 (영문명: {similarMatches[0]?.data?.BASE_CAR_NM_ENG || similarMatches[0]?.data?.car_name_en?.toUpperCase() || ''})
                    </button>
                  )}
                  {!carNameMappingData.masterData && similarMatches.length === 0 && carNameMappingData.manualCarNameEn && (
                    <button
                      onClick={() => {
                        if (carNameMappingData.manualCarNameEn) {
                          const finalData = { ...carNameMappingData.currentData };
                          finalData.model = carNameMappingData.manualCarNameEn;
                          performValidation(finalData);
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm"
                    >
                      확인 (영문명: {carNameMappingData.manualCarNameEn})
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowCarNameMappingModal(false);
                      setCarNameMappingData(null);
                      setSimilarMatches([]);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm"
                  >
                    취소
                  </button>
                  <button
                    onClick={() => {
                      setShowCarNameMappingModal(false);
                      setCarNameMappingData(null);
                      setSimilarMatches([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* 상단 정보 영역 */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="grid grid-cols-2 gap-4">
                  {/* OCR 인식 차량명 */}
                  <div>
                    <div className="text-sm text-gray-600 mb-2">OCR 인식 차량명:</div>
                    <div className="text-lg font-semibold text-gray-900 bg-white px-4 py-2 rounded border border-gray-200">
                      {carNameMappingData.ocrRecognizedName}
                    </div>
                  </div>

                  {/* 기초자료 매핑 영문차량명 */}
                  <div>
                    <div className="text-sm text-gray-600 mb-2">기초자료 매핑 영문차량명:</div>
                    <div className="text-lg font-semibold text-gray-900 bg-white px-4 py-2 rounded border border-gray-200">
                      {/* 기초자료에 영문명이 있고, 저장된 데이터인 경우에만 표시 */}
                      {(carNameMappingData.masterData?.BASE_CAR_NM_ENG && carNameMappingData.masterData.BASE_CAR_NM_ENG.trim() !== '') ||
                        (carNameMappingData.masterData?.car_name_en && carNameMappingData.masterData.car_name_en.trim() !== '') ||
                        (similarMatches[0]?.data?.BASE_CAR_NM_ENG && similarMatches[0].data.BASE_CAR_NM_ENG.trim() !== '') ||
                        (similarMatches[0]?.data?.car_name_en && similarMatches[0].data.car_name_en.trim() !== '')
                        ? (carNameMappingData.masterData?.BASE_CAR_NM_ENG ||
                          carNameMappingData.masterData?.car_name_en ||
                          similarMatches[0]?.data?.BASE_CAR_NM_ENG ||
                          similarMatches[0]?.data?.car_name_en)
                        : ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* 하단 그리드 영역 */}
              <div className="flex-1 overflow-hidden px-0 py-0 min-h-0">
                <VehicleMasterManagement
                  API_BASE_URL={process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '')}
                  ocrRecognizedName={carNameMappingData.ocrRecognizedName}
                  currentData={carNameMappingData.currentData}
                />
              </div>
            </div>
          </div>
        )}

        {/* 기초설정 모달 */}
        {showMasterSettingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg shadow-xl w-[52vw] h-[80vh] flex flex-col">
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold" style={{ color: '#0A1037' }}>
                    기초설정
                  </h3>
                  <span className="text-sm text-gray-600">
                    차량 기초자료관리(그룹코드: {localStorage.getItem('groupCode') || '0001'})
                  </span>
                  <button
                    onClick={() => {
                      // VehicleMasterManagement 컴포넌트의 갱신 기능을 호출
                      window.dispatchEvent(new CustomEvent('refreshVehicleMaster'));
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm"
                    title="새로고침"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>갱신</span>
                  </button>
                </div>
                <button
                  onClick={() => setShowMasterSettingsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden min-h-0">
                <VehicleMasterManagement API_BASE_URL={process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '')} />
              </div>
            </div>
          </div>
        )}

        {/* 차량 추가 모달 */}
        {showAddVehicleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold" style={{ color: '#0A1037' }}>
                  {editingMasterId ? '차량 기초자료 업데이트' : '차량 기초자료 추가'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddVehicleModal(false);
                    setEditingMasterId(null);
                    setNewVehicleData({
                      car_name_kr: '',
                      car_name_en: '',
                      brand: '',
                      model_year: '',
                      trade_item_name: '',
                      item_name: '',
                      displacement: '',
                      weight: '',
                      cbm: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    차량명(한글) *
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.car_name_kr}
                    onChange={(e) => setNewVehicleData({ ...newVehicleData, car_name_kr: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 쏘나타"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    차량명(영문) *
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.car_name_en}
                    onChange={(e) => setNewVehicleData({ ...newVehicleData, car_name_en: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: SONATA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    브랜드
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.brand}
                    onChange={(e) => setNewVehicleData({ ...newVehicleData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 현대"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    연식
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.model_year}
                    onChange={(e) => setNewVehicleData({ ...newVehicleData, model_year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 2011"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    배기량
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.displacement}
                    onChange={(e) => setNewVehicleData({ ...newVehicleData, displacement: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 1998"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    중량
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.weight}
                    onChange={(e) => setNewVehicleData({ ...newVehicleData, weight: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 1740"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CBM
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.cbm}
                    onChange={(e) => setNewVehicleData({ ...newVehicleData, cbm: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: 13"
                  />
                </div>
              </div>
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAddVehicleModal(false);
                    setEditingMasterId(null);
                    setNewVehicleData({
                      car_name_kr: '',
                      car_name_en: '',
                      brand: '',
                      model_year: '',
                      trade_item_name: '',
                      item_name: '',
                      displacement: '',
                      weight: '',
                      cbm: ''
                    });
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={async () => {
                    const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '');
                    try {
                      let response;
                      if (editingMasterId) {
                        // 기존 데이터 업데이트
                        response = await fetch(`${API_BASE_URL}/api/vehicle-master/${editingMasterId}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(newVehicleData)
                        });
                      } else {
                        // 새 데이터 추가
                        response = await fetch(`${API_BASE_URL}/api/vehicle-master/add`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(newVehicleData)
                        });
                      }

                      if (response.ok) {
                        const result = await response.json();
                        if (result.success) {
                          alert(editingMasterId ? '차량 기초자료가 업데이트되었습니다.' : '차량이 기초자료에 추가되었습니다.');
                          setShowAddVehicleModal(false);
                          setEditingMasterId(null);
                          setNewVehicleData({
                            car_name_kr: '',
                            car_name_en: '',
                            brand: '',
                            model_year: '',
                            trade_item_name: '',
                            item_name: '',
                            displacement: '',
                            weight: '',
                            cbm: ''
                          });
                        } else {
                          alert((editingMasterId ? '차량 업데이트' : '차량 추가') + '에 실패했습니다: ' + (result.message || '알 수 없는 오류'));
                        }
                      } else {
                        alert((editingMasterId ? '차량 업데이트' : '차량 추가') + '에 실패했습니다.');
                      }
                    } catch (error) {
                      console.error('차량 처리 실패:', error);
                      alert((editingMasterId ? '차량 업데이트' : '차량 추가') + ' 중 오류가 발생했습니다.');
                    }
                  }}
                  disabled={!newVehicleData.car_name_kr || !newVehicleData.car_name_en}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {editingMasterId ? '업데이트' : '추가'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* 🔧 그리드 영역 닫힘 */}

      {/* 🤖 AI 인라인 사이드바 — Claude-in-Excel 스타일 */}
      {showAIPanel && (
        <div
          className="flex-shrink-0 h-full bg-white border-l border-gray-200 flex flex-col shadow-lg"
          style={{ width: 360, minWidth: 320, maxWidth: 420 }}
        >
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-gray-900 text-sm">AI 어시스턴트</span>
            </div>
            <button
              onClick={() => { setShowAIPanel(false); setAiResult(null); setAiError(null); setAiMessages([]); window.dispatchEvent(new CustomEvent('aiPanelStateChange', { detail: { open: false } })); }}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* 대화 영역 (스크롤) */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" style={{ minHeight: 0 }}>
            {/* 대화가 없을 때 안내 + 예시 힌트 */}
            {aiMessages.length === 0 && !aiLoading && !aiError && (
              <div className="space-y-3">
                <div className="text-center py-3">
                  <Sparkles className="w-7 h-7 mx-auto mb-2 text-gray-300" />
                  <p className="text-xs text-gray-500">자연어로 데이터를 변환하세요</p>
                  <p className="text-[10px] text-gray-400 mt-1">Enter로 전송 · Shift+Enter로 줄바꿈</p>
                </div>

                {/* ⚡ 빠른 작업 칩 — 카테고리별 */}
                {[
                  {
                    label: '🚢 무역 · 관세',
                    actions: [
                      { text: '인코텀즈 추출', prompt: '이 열에서 인코텀즈(FOB, CIF, FCA 등)만 추출해줘' },
                      { text: 'HS코드 10자리 생성', prompt: '품명을 기반으로 한국 관세율표 기준 HS코드 10자리를 추가열에 생성해줘' },
                      { text: 'HS코드 검증', prompt: '현재 HS코드가 품명과 맞는지 검토하고 수정이 필요하면 올바른 코드를 제안해줘' },
                      { text: '원산지 추출', prompt: '이 열에서 국가명/원산지 정보만 추출해줘' },
                    ]
                  },
                  {
                    label: '🔧 데이터 정리',
                    actions: [
                      { text: '대소문자 변환', prompt: '모두 대문자로 변환해줘' },
                      { text: '날짜 형식 통일', prompt: '날짜를 YYYY-MM-DD 형식으로 통일해줘' },
                      { text: '열 분리', prompt: ', 기준으로 2개 열로 분리해줘' },
                      { text: '빈 셀 채우기', prompt: '빈 셀을 이전 행의 값으로 채워줘' },
                    ]
                  },
                  {
                    label: '🗑️ 필터 · 삭제',
                    actions: [
                      { text: '조건부 행 삭제', prompt: '빈 행을 모두 삭제해줘' },
                      { text: '중복 제거', prompt: '중복된 행을 제거해줘' },
                      { text: '특정 값만 유지', prompt: 'FCA인 행만 남기고 나머지 삭제해줘' },
                    ]
                  }
                ].map((category, ci) => (
                  <div key={ci} className="space-y-1">
                    <div className="text-[10px] text-gray-400 font-medium px-1">{category.label}</div>
                    <div className="flex flex-wrap gap-1">
                      {category.actions.map((action, ai) => (
                        <button
                          key={ai}
                          onClick={() => setAiPrompt(action.prompt)}
                          className="text-[11px] text-gray-600 hover:bg-blue-50 hover:text-blue-700 px-2 py-1 rounded-md transition-colors border border-gray-200 hover:border-blue-300 bg-white"
                          title={action.prompt}
                        >
                          {action.text}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 대화 메시지 목록 */}
            {aiMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'user' ? (
                  /* 사용자 메시지 */
                  <div className="max-w-[85%] px-3 py-2 rounded-xl rounded-tr-sm bg-blue-50 text-gray-800 text-xs whitespace-pre-wrap shadow-sm border border-blue-200">
                    {msg.content}
                  </div>
                ) : msg.role === 'assistant' ? (
                  /* AI 응답 메시지 */
                  <div className="max-w-[95%] space-y-2">
                    <div className="px-3 py-2.5 rounded-xl rounded-tl-sm bg-gray-100 text-gray-800 text-xs shadow-sm">
                      <div className="whitespace-pre-wrap">{msg.content}</div>

                      {/* Diff 미리보기 */}
                      {msg.previews && msg.previews.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <div className="text-[10px] text-gray-500 mb-1 font-medium">변경 미리보기:</div>
                          <div className="space-y-0.5">
                            {msg.previews.map((p, i) => (
                              <div key={i} className="flex items-start gap-1 text-[10px]">
                                <span className="text-gray-400 w-5 text-right flex-shrink-0">#{p.rowIndex}</span>
                                <span className="text-red-500 line-through truncate max-w-[100px]" title={p.oldVal}>{p.oldVal || '(빈)'}</span>
                                <span className="text-gray-400">→</span>
                                <span className="text-blue-600 font-medium truncate max-w-[100px]" title={p.newVal}>{p.newVal || '(빈)'}</span>
                              </div>
                            ))}
                            {msg.changedCount > 5 && (
                              <div className="text-[10px] text-gray-400">...외 {msg.changedCount - 5}개 변경</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 적용/취소 버튼 (pendingTransform이 이 메시지의 것일 때만) */}
                    {msg.transformedData && aiResult?.msgIdx === idx && (
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => {
                            // 🔧 AI 결과 적용
                            const grid = gridRef.current || '#horizontal_grid';
                            if (!window.AUIGrid?.isCreated(grid) || !msg.transformedData) return;

                            const transformedData = msg.transformedData;
                            const currentData = window.AUIGrid.getGridData(grid);

                            // 📸 스냅샷
                            const snap = {
                              type: 'full_snapshot',
                              label: 'AI 열 변환',
                              beforeData: currentData.map(r => ({ ...r })),
                              beforeLayout: window.AUIGrid.getColumnLayout(grid).map(c => ({ ...c }))
                            };

                            // 병합
                            const transformMap = new Map();
                            transformedData.forEach(row => {
                              if (row._rowIndex !== undefined) transformMap.set(row._rowIndex, row);
                            });

                            const hasFilteredRows = transformedData.length < currentData.length &&
                              transformedData.every(item => item._rowIndex !== undefined);

                            let newData;
                            if (hasFilteredRows) {
                              const keptIndices = new Set(transformedData.map(item => item._rowIndex));
                              newData = currentData
                                .filter((_, idx2) => keptIndices.has(idx2))
                                .map((row, newIdx) => {
                                  const originalIdx = [...keptIndices].sort((a, b) => a - b)[newIdx];
                                  const transformed = transformMap.get(originalIdx);
                                  if (transformed) {
                                    const merged = { ...row };
                                    Object.keys(transformed).forEach(key => { if (key !== '_rowIndex') merged[key] = transformed[key]; });
                                    return merged;
                                  }
                                  return row;
                                });
                            } else {
                              newData = currentData.map((row, idx2) => {
                                const transformed = transformMap.get(idx2);
                                if (transformed) {
                                  const merged = { ...row };
                                  Object.keys(transformed).forEach(key => { if (key !== '_rowIndex') merged[key] = transformed[key]; });
                                  return merged;
                                }
                                return { ...row };
                              });
                            }

                            // 새 열 감지
                            const existingColumns = window.AUIGrid.getColumnLayout(grid).map(c => c.dataField);
                            const allAiKeys = new Set();
                            transformedData.forEach(row => { Object.keys(row).forEach(k => { if (k !== '_rowIndex') allAiKeys.add(k); }); });
                            const missingColumns = [...allAiKeys].filter(k => !existingColumns.includes(k));
                            if (missingColumns.length > 0) {
                              const firstExistingKey = [...allAiKeys].find(k => existingColumns.includes(k));
                              let insertIdx = firstExistingKey ? existingColumns.indexOf(firstExistingKey) + 1 : existingColumns.length;
                              missingColumns.forEach((colName, i) => {
                                try { window.AUIGrid.addColumn(grid, { dataField: colName, headerText: colName, width: 150, editable: true, style: 'aui-left' }, insertIdx + i); } catch (e) { /* */ }
                              });
                            }

                            window.AUIGrid.setGridData(grid, newData);
                            setUndoStack(prev => [...prev, { id: Date.now(), ...snap }]);
                            showUndoToast();

                            // autoAlign
                            try {
                              const layout = window.AUIGrid.getColumnLayout(grid);
                              const data = window.AUIGrid.getGridData(grid);
                              if (layout && data && data.length > 0) {
                                const numRegex = /^-?[\d,]+\.?\d*$/;
                                const skipFields = new Set(['No', 'P', 'rowNum', 'pageNumber']);
                                let cssRules = '';
                                layout.forEach((col, colIdx2) => {
                                  if (!col.dataField || skipFields.has(col.dataField)) return;
                                  let numCount = 0, totalCount = 0;
                                  for (let i = 0; i < Math.min(data.length, 20); i++) {
                                    const val = String(data[i]?.[col.dataField] || '').trim();
                                    if (val === '') continue;
                                    totalCount++;
                                    if (numRegex.test(val.replace(/\s/g, ''))) numCount++;
                                  }
                                  if (totalCount > 0) cssRules += `.aui-grid-renderer-base[data-column-index="${colIdx2}"] { text-align: ${(numCount / totalCount) >= 0.6 ? 'right' : 'left'} !important; }\n`;
                                });
                                if (cssRules) {
                                  let styleEl = document.getElementById('auto-align-style');
                                  if (!styleEl) { styleEl = document.createElement('style'); styleEl.id = 'auto-align-style'; document.head.appendChild(styleEl); }
                                  styleEl.textContent = cssRules;
                                }
                              }
                            } catch (e) { /* */ }

                            // 메시지 업데이트 (적용 완료 표시)
                            setAiMessages(prev => prev.map((m, i) => i === idx ? { ...m, applied: true } : m));
                            setAiResult(null);
                            console.log('✅ [AI] 변환 적용 완료');
                          }}
                          className="flex-1 px-2.5 py-1.5 bg-blue-600 text-white text-[11px] font-medium rounded-lg hover:bg-blue-700 transition shadow-sm flex items-center justify-center gap-1"
                        >
                          <Check className="w-3 h-3" /> 적용
                        </button>
                        <button
                          onClick={() => { setAiResult(null); }}
                          className="px-2.5 py-1.5 bg-gray-100 text-gray-600 text-[11px] font-medium rounded-lg hover:bg-gray-200 transition border border-gray-200"
                        >
                          건너뛰기
                        </button>
                      </div>
                    )}

                    {/* 적용 완료 표시 */}
                    {msg.applied && (
                      <div className="flex items-center gap-1 text-[10px] text-green-600 px-1">
                        <Check className="w-3 h-3" /> 적용됨
                        <button
                          onClick={() => {
                            // Undo 실행
                            if (undoStack.length > 0) {
                              const lastSnap = undoStack[undoStack.length - 1];
                              const grid = gridRef.current || '#horizontal_grid';
                              if (window.AUIGrid?.isCreated(grid) && lastSnap.beforeData) {
                                // 🔧 먼저 추가된 열 제거 (beforeLayout에 없는 열을 역순으로 제거)
                                if (lastSnap.beforeLayout) {
                                  const beforeFields = new Set(lastSnap.beforeLayout.map(c => c.dataField));
                                  const currentLayout = window.AUIGrid.getColumnLayout(grid);
                                  // 역순으로 제거해야 인덱스가 밀리지 않음
                                  for (let i = currentLayout.length - 1; i >= 0; i--) {
                                    if (currentLayout[i].dataField && !beforeFields.has(currentLayout[i].dataField)) {
                                      try {
                                        window.AUIGrid.removeColumn(grid, i);
                                        console.log('🔄 [Undo] Removed column:', currentLayout[i].dataField, 'at index', i);
                                      } catch (e) { console.warn('⚠️ [Undo] removeColumn failed:', e); }
                                    }
                                  }
                                }
                                window.AUIGrid.setGridData(grid, lastSnap.beforeData);
                                setUndoStack(prev => prev.slice(0, -1));
                                setAiMessages(prev => prev.map((m, i) => i === idx ? { ...m, applied: false, undone: true } : m));
                                console.log('✅ [Undo] 복원 완료');
                              }
                            }
                          }}
                          className="ml-1 text-[10px] text-orange-600 hover:text-orange-800 underline cursor-pointer"
                        >
                          되돌리기
                        </button>
                      </div>
                    )}

                    {/* 되돌리기 완료 표시 */}
                    {msg.undone && (
                      <div className="flex items-center gap-1 text-[10px] text-orange-600 px-1">
                        <Undo2 className="w-3 h-3" /> 되돌림
                      </div>
                    )}
                  </div>
                ) : (
                  /* 시스템 메시지 */
                  <div className="text-[10px] text-gray-400 text-center w-full">
                    {msg.content}
                  </div>
                )}
              </div>
            ))}

            {/* AI 에러 */}
            {aiError && (
              <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                ❌ {aiError}
              </div>
            )}

            {/* 로딩 */}
            {aiLoading && (
              <div className="flex items-center gap-2 text-gray-500">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span className="text-xs">AI가 분석 중...</span>
                <button
                  onClick={() => {
                    if (aiAbortRef.current) { aiAbortRef.current.abort(); }
                    setAiLoading(false);
                    setAiError(null);
                  }}
                  className="ml-auto text-[10px] text-red-500 hover:text-red-700 underline"
                >
                  취소
                </button>
              </div>
            )}

            <div ref={aiChatEndRef} />
          </div>

          {/* 하단 입력 영역 — Claude-in-Excel 스타일 */}
          <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50/80">
            {/* 선택된 열 배지 (프롬프트 바로 위) */}
            <div className="px-3 pt-2 pb-1">
              <div className="flex flex-wrap items-center gap-1">
                {aiTargetColumns.length > 0 ? (
                  <>
                    <span className="text-[10px] text-gray-400">Column {aiTargetColumns.length} selected</span>
                    {aiTargetColumns.map(c => (
                      <span key={c.field} className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-medium">
                        {c.headerText}
                        <button
                          onClick={() => setAiTargetColumns(prev => prev.filter(p => p.field !== c.field))}
                          className="ml-0.5 text-blue-400 hover:text-blue-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </>
                ) : (
                  <span className="text-[10px] text-gray-400">전체 데이터 대상</span>
                )}
              </div>
            </div>

            {/* 프롬프트 입력 (textarea) */}
            <div className="px-3 pb-2.5">
              <div className="relative flex items-end border border-gray-300 rounded-xl bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition shadow-sm">
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (aiPrompt.trim() && !aiLoading) {
                        // 폼 제출 트리거
                        e.target.closest('.relative')?.querySelector('button[data-submit]')?.click();
                      }
                    }
                    // Shift+Enter는 기본 동작 (줄바꿈)
                  }}
                  onInput={(e) => {
                    // 자동 높이 조절 (min 2줄, max ~5줄)
                    e.target.style.height = 'auto';
                    const lineH = 18;
                    const minH = lineH * 2 + 12; // 2줄 + padding
                    const maxH = lineH * 5 + 12; // 5줄 + padding
                    e.target.style.height = Math.max(minH, Math.min(maxH, e.target.scrollHeight)) + 'px';
                  }}
                  placeholder="변환 명령을 입력하세요..."
                  className="flex-1 px-3 py-2 text-xs resize-none bg-transparent focus:outline-none"
                  style={{ minHeight: '48px', maxHeight: '102px', lineHeight: '18px' }}
                  disabled={aiLoading}
                  rows={2}
                  autoFocus
                />
                <button
                  data-submit="true"
                  onClick={async () => {
                    if (!aiPrompt.trim() || aiLoading) return;

                    const promptText = aiPrompt.trim();
                    // 사용자 메시지 추가
                    setAiMessages(prev => [...prev, { role: 'user', content: promptText, timestamp: Date.now() }]);
                    setAiPrompt('');

                    // 🎯 액션 커맨드 감지 — 기존 기능 직접 실행
                    const lowerPrompt = promptText.toLowerCase();
                    const isExcelCmd = /엑셀|excel|xlsx|다운로드|download|내보내기|export/.test(lowerPrompt);
                    const isUndoCmd = /되돌리기|되돌려|undo|취소|원복|rollback/.test(lowerPrompt) && !/변환|변경|삭제|분리/.test(lowerPrompt);

                    if (isExcelCmd) {
                      // ✅ 엑셀 다운로드 직접 실행
                      setTimeout(() => {
                        try {
                          handleExcelDownload();
                          setAiMessages(prev => [...prev, {
                            role: 'assistant',
                            content: '✅ 엑셀 파일 다운로드를 실행했습니다! 브라우저 다운로드 폴더를 확인해주세요.',
                            timestamp: Date.now()
                          }]);
                        } catch (e) {
                          setAiMessages(prev => [...prev, {
                            role: 'assistant',
                            content: `❌ 엑셀 다운로드 실패: ${e.message}`,
                            isError: true,
                            timestamp: Date.now()
                          }]);
                        }
                        setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                      }, 300);
                      return;
                    }

                    if (isUndoCmd) {
                      // ✅ 되돌리기 직접 실행
                      setTimeout(() => {
                        if (undoStack.length > 0) {
                          performUndo();
                          setAiMessages(prev => [...prev, {
                            role: 'assistant',
                            content: '✅ 마지막 작업을 되돌렸습니다.',
                            timestamp: Date.now()
                          }]);
                        } else {
                          setAiMessages(prev => [...prev, {
                            role: 'assistant',
                            content: '⚠️ 되돌릴 작업이 없습니다.',
                            timestamp: Date.now()
                          }]);
                        }
                        setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                      }, 300);
                      return;
                    }

                    // 🗑️ 열 삭제 커맨드 감지
                    const isDeleteColCmd = /열\s*삭제|열\s*제거|컬럼\s*삭제|column\s*delete|remove\s*column|drop\s*column|현재\s*열\s*삭제/.test(lowerPrompt);
                    if (isDeleteColCmd) {
                      setTimeout(() => {
                        const grid = gridRef.current || '#horizontal_grid';
                        if (!window.AUIGrid?.isCreated(grid)) {
                          setAiMessages(prev => [...prev, { role: 'assistant', content: '❌ 그리드가 없습니다.', isError: true, timestamp: Date.now() }]);
                          return;
                        }

                        // 삭제 대상 열 결정 (AI 사이드바에 선택된 열 또는 selectedColumnsRef)
                        let targetCols = aiTargetColumns;
                        if (targetCols.length === 0 && selectedColumnsRef.current?.length > 0) {
                          const layout = window.AUIGrid.getColumnLayout(grid);
                          targetCols = selectedColumnsRef.current.map(idx => ({
                            field: layout[idx]?.dataField,
                            headerText: layout[idx]?.headerText || layout[idx]?.dataField
                          })).filter(c => c.field);
                        }

                        if (targetCols.length === 0) {
                          setAiMessages(prev => [...prev, { role: 'assistant', content: '⚠️ 삭제할 열을 선택해주세요. (헤더 클릭으로 열 선택 후 다시 시도)', timestamp: Date.now() }]);
                          setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                          return;
                        }

                        try {
                          // 📸 스냅샷 저장 (되돌리기 지원)
                          const currentData = window.AUIGrid.getGridData(grid);
                          const currentLayout = window.AUIGrid.getColumnLayout(grid);
                          const snap = {
                            id: Date.now(),
                            type: 'full_snapshot',
                            label: `열 삭제: ${targetCols.map(c => c.headerText).join(', ')}`,
                            beforeData: currentData.map(r => ({ ...r })),
                            beforeLayout: currentLayout.map(c => ({ ...c }))
                          };

                          // 열 삭제 (역순으로)
                          const layout = window.AUIGrid.getColumnLayout(grid);
                          const deletedNames = [];
                          for (let i = layout.length - 1; i >= 0; i--) {
                            if (targetCols.some(tc => tc.field === layout[i].dataField)) {
                              window.AUIGrid.removeColumn(grid, i);
                              deletedNames.push(layout[i].headerText || layout[i].dataField);
                            }
                          }

                          setUndoStack(prev => [...prev, snap]);
                          showUndoToast();

                          // 선택 상태 초기화
                          selectedColumnsRef.current = [];
                          setAiTargetColumns([]);

                          setAiMessages(prev => [...prev, {
                            role: 'assistant',
                            content: `✅ 열 삭제 완료: ${deletedNames.join(', ')}\n\n되돌리려면 "되돌리기"를 입력하세요.`,
                            timestamp: Date.now()
                          }]);
                          console.log('🗑️ [AI Action] 열 삭제:', deletedNames);
                        } catch (e) {
                          setAiMessages(prev => [...prev, {
                            role: 'assistant',
                            content: `❌ 열 삭제 실패: ${e.message}`,
                            isError: true,
                            timestamp: Date.now()
                          }]);
                        }
                        setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                      }, 300);
                      return;
                    }

                    // 🤖 일반 AI 변환 요청
                    setAiLoading(true);
                    setAiResult(null);
                    setAiError(null);

                    // 자동 스크롤
                    setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

                    const abortController = new AbortController();
                    aiAbortRef.current = abortController;

                    try {
                      const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '');
                      const grid = gridRef.current || '#horizontal_grid';
                      const gridData = window.AUIGrid.getGridData(grid);

                      let cols = aiTargetColumns;
                      if (cols.length === 0) {
                        const layout = window.AUIGrid.getColumnLayout(grid);
                        cols = layout.filter(c => c.dataField && c.dataField !== '__checkbox' && c.dataField !== '__rownum')
                          .map(c => ({ field: c.dataField, headerText: c.headerText || c.dataField }));
                      }

                      const columns = cols.map(c => c.field);
                      const data = gridData.map((row, i) => {
                        const obj = { _rowIndex: i };
                        cols.forEach(c => { obj[c.field] = row[c.field] || ''; });
                        return obj;
                      });

                      const res = await fetch(`${API_BASE_URL}/api/ai/transform-data`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ prompt: promptText, columns, data, context: { totalRows: data.length, columnCount: columns.length } }),
                        signal: abortController.signal
                      });
                      const result = await res.json();

                      if (result.success) {
                        // Diff 미리보기 생성
                        const firstCol = cols[0];
                        let previews = [];
                        let changedCount = 0;
                        if (firstCol && result.transformedData) {
                          for (const row of result.transformedData) {
                            if (row._rowIndex === undefined) continue;
                            const oldRow = gridData[row._rowIndex];
                            if (!oldRow) continue;
                            const oldVal = String(oldRow[firstCol.field] || '');
                            const newVal = String(row[firstCol.field] || '');
                            if (oldVal !== newVal) {
                              changedCount++;
                              if (previews.length < 5) previews.push({ rowIndex: row._rowIndex, oldVal, newVal });
                            }
                          }
                        }

                        const msgIdx = aiMessages.length + 1; // +1 because user msg was just added
                        setAiMessages(prev => [...prev, {
                          role: 'assistant',
                          content: result.explanation || `✅ 변환 완료! ${changedCount}개 셀 변경`,
                          transformedData: result.transformedData,
                          previews,
                          changedCount,
                          timestamp: Date.now()
                        }]);
                        setAiResult({ transformedData: result.transformedData, msgIdx });
                      } else {
                        setAiError(result.error || '변환 실패');
                        setAiMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${result.error || '변환 실패'}`, isError: true, timestamp: Date.now() }]);
                      }
                    } catch (err) {
                      if (err.name === 'AbortError') {
                        setAiMessages(prev => [...prev, { role: 'system', content: '요청이 취소되었습니다.', timestamp: Date.now() }]);
                      } else {
                        setAiError(err.message);
                        setAiMessages(prev => [...prev, { role: 'assistant', content: `❌ ${err.message}`, isError: true, timestamp: Date.now() }]);
                      }
                    } finally {
                      setAiLoading(false);
                      aiAbortRef.current = null;
                      setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 200);
                    }
                  }}
                  disabled={!aiPrompt.trim() || aiLoading}
                  className="flex-shrink-0 p-2 mr-1 mb-1 text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-1 px-0.5">
                <span className="text-[9px] text-gray-400">⏎ 전송 · ⇧⏎ 줄바꿈</span>
                <span className="text-[9px] text-gray-400">⊘ Ask before edits</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🔄 Undo 토스트 (3초 자동 숨김) */}
      {undoToastVisible && undoStack.length > 0 && (
        <div style={{
          position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, display: 'flex', alignItems: 'center', gap: '12px',
          background: '#1e293b', color: '#fff', padding: '10px 20px',
          borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          fontSize: '13px', fontWeight: 500, animation: 'fadeInUp 0.2s ease-out',
          overflow: 'hidden'
        }}>
          <Undo2 className="w-4 h-4" style={{ color: '#60a5fa' }} />
          <span>작업이 실행되었습니다</span>
          <button
            onClick={performUndo}
            style={{
              background: '#3b82f6', color: '#fff', border: 'none',
              padding: '4px 12px', borderRadius: '4px', cursor: 'pointer',
              fontSize: '12px', fontWeight: 600
            }}
          >
            되돌리기
          </button>
          <span style={{ color: '#94a3b8', fontSize: '11px' }}>Ctrl+Z</span>
          {/* 프로그레스 바 */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, height: '2px',
            background: '#3b82f6', animation: 'undoProgress 5s linear forwards'
          }} />
          <style>{`
            @keyframes fadeInUp { from { opacity: 0; transform: translateX(-50%) translateY(10px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
            @keyframes undoProgress { from { width: 100%; } to { width: 0%; } }
          `}</style>
        </div>
      )}

      {/* 🆕 헤더 컨텍스트 메뉴 오버레이 (Google Sheets 스타일) */}
      {headerMenuVisible && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setHeaderMenuVisible(false)} />
          <div
            className="fixed z-50 bg-white rounded-lg py-1.5 select-none"
            style={{
              left: headerMenuPosition.x, top: headerMenuPosition.y,
              minWidth: '220px',
              boxShadow: '0 2px 6px 2px rgba(60,64,67,0.15), 0 1px 2px 0 rgba(60,64,67,0.3)',
              border: '1px solid #dadce0'
            }}
          >
            <div className="px-4 py-1.5 text-[11px] text-gray-500 border-b border-gray-200 flex items-center gap-2" style={{ fontWeight: 500 }}>
              열: {headerMenuTarget.headerText}
            </div>
            {[
              { label: "선택 열 병합...", icon: <Link className="w-4 h-4" />, callback: "mergeColumns", help: "선택한 열들을 하나로 합칩니다\n예: [A열]+[B열] → [A, B]" },
              { label: "열 분리...", icon: <Scissors className="w-4 h-4" />, callback: "splitColumn", help: "1열을 구분자로 여러 열로 나눕니다\n예: \"A, B, C\" → [A] [B] [C]" },
              { label: "AI로 열 변환...", icon: <Sparkles className="w-4 h-4" />, callback: "mergeColumnsPrompt", help: "AI에게 자연어로 변환 지시\n예: \"날짜 형식을 YYYY-MM-DD로 변환\"" },
              { type: "divider" },
              { label: "Trim", icon: <Eraser className="w-4 h-4" />, callback: "trimColumn", help: "앞뒤 공백 + 내부 연속 공백 정리\n예: \"  hello   world  \" → \"hello world\"" },
              { label: "UPPERCASE (대문자)", icon: <CaseSensitive className="w-4 h-4" />, callback: "uppercaseColumn", help: "영문 소문자를 대문자로 변환\n예: \"Cleaning Compound\" → \"CLEANING COMPOUND\"" },
              { label: "정렬...", icon: <ArrowUpDown className="w-4 h-4" />, callback: "sortColumn", help: "열 기준 오름/내림차순 정렬\n예: [3,1,2] → [1,2,3] 또는 [3,2,1]" },
              { label: "빈 행 삭제", icon: <Trash2 className="w-4 h-4" />, callback: "deleteEmptyRows", help: "모든 셀이 비어있는 행을 삭제\n예: 27행 중 빈 행 3개 → 24행" },
              { type: "divider" },
              { label: "열 복사", icon: <Copy className="w-4 h-4" />, callback: "copyColumn" },
              { label: "열 추가", icon: <Plus className="w-4 h-4" />, callback: "addColumn" },
              { label: "열 삭제", icon: <X className="w-4 h-4" />, callback: "deleteColumn" },
              { type: "divider" },
              { label: "수량×단가 → 금액", icon: <Calculator className="w-4 h-4" />, callback: "addAmountColumn", help: "수량×단가 계산 열 추가\n예: qty=5, uprice=100 → amount=500" },
              { label: "금액÷수량 → 단가", icon: <Calculator className="w-4 h-4" />, callback: "addUnitPriceColumn", help: "금액÷수량 계산 열 추가\n예: amount=500, qty=5 → uprice=100" }
            ].map((item, idx) =>
              item.type === "divider" ? (
                <div key={idx} className="border-t border-gray-200 my-1" />
              ) : (
                <button
                  key={idx}
                  className="w-full px-4 text-left text-[13px] hover:bg-gray-100 transition-colors flex items-center gap-3 text-gray-700 group"
                  style={{ padding: '7px 16px', fontWeight: 400 }}
                  onClick={() => {
                    setHeaderMenuVisible(false);
                    handleGridMenuAction(item.callback, headerMenuTarget);
                  }}
                >
                  <span className="text-gray-500 flex-shrink-0">{item.icon}</span>
                  <span className="flex-1">{item.label}</span>
                  {item.help && (
                    <span
                      title={item.help}
                      className="w-4 h-4 rounded-full text-gray-400 text-[10px] flex items-center justify-center cursor-help hover:bg-gray-200 hover:text-gray-600 transition-colors flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >?</span>
                  )}
                </button>
              )
            )}
          </div>
        </>
      )}

      {/* 셀 컨텍스트 메뉴 (Google Sheets 스타일) */}
      {gridMenuVisible && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setGridMenuVisible(false)} />
          <div
            className="fixed z-50 bg-white rounded-lg py-1.5 select-none"
            style={{
              left: gridMenuPosition.x,
              top: gridMenuPosition.y,
              minWidth: '220px',
              boxShadow: '0 2px 6px 2px rgba(60,64,67,0.15), 0 1px 2px 0 rgba(60,64,67,0.3)',
              border: '1px solid #dadce0'
            }}
          >
            {/* 헤더 */}
            <div className="px-4 py-1.5 text-[11px] text-gray-500 border-b border-gray-200 flex items-center justify-between" style={{ fontWeight: 500 }}>
              <span>행 {gridMenuTarget.rowRange}</span>
            </div>

            {/* 행 작업 */}
            <div className="px-4 pt-2 pb-1 text-[11px] text-gray-400 select-none" style={{ fontWeight: 500 }}>행 작업</div>
            {[
              { label: `위에 행 ${gridMenuTarget.rowCount}개 삽입`, icon: <Plus className="w-4 h-4" />, callback: "insertRow" },
              { label: "아래에 행 추가", icon: <ArrowUp className="w-4 h-4 rotate-180" />, callback: "addRow" },
              { label: `${gridMenuTarget.rowCount > 1 ? `${gridMenuTarget.rowCount}개 행 삭제` : `${gridMenuTarget.rowRange}행 삭제`}`, icon: <Trash2 className="w-4 h-4" />, callback: "deleteSelectedRows" },
            ].map((item, idx) => (
              <button
                key={idx}
                className="w-full text-left text-[13px] flex items-center gap-3 hover:bg-gray-100 text-gray-700 transition-colors group"
                style={{ padding: '7px 16px', fontWeight: 400 }}
                onClick={() => {
                  setGridMenuVisible(false);
                  handleGridMenuAction(item.callback, gridMenuTarget);
                }}
              >
                <span className="text-gray-500 flex-shrink-0">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
              </button>
            ))}

            <div className="border-t border-gray-200 my-1" />

            {/* 편집 */}
            <div className="px-4 pt-2 pb-1 text-[11px] text-gray-400 select-none" style={{ fontWeight: 500 }}>편집</div>
            {[
              { label: "셀 내용 지우기", icon: <Eraser className="w-4 h-4" />, callback: "clearCell" },
              { label: "위로 병합", icon: <ArrowUp className="w-4 h-4" />, callback: "mergeUp" },
              { label: "왼쪽으로 병합", icon: <ArrowLeft className="w-4 h-4" />, callback: "mergeLeft" },
              { label: "삭제 옵션...", icon: <Trash2 className="w-4 h-4" />, callback: "showDeleteDialog" },
            ].map((item, idx) => (
              <button
                key={idx}
                className="w-full text-left text-[13px] flex items-center gap-3 hover:bg-gray-100 text-gray-700 transition-colors group"
                style={{ padding: '7px 16px', fontWeight: 400 }}
                onClick={() => {
                  setGridMenuVisible(false);
                  handleGridMenuAction(item.callback, gridMenuTarget);
                }}
              >
                <span className="text-gray-500 flex-shrink-0">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
